import "./style.css";
import { ORIGINAL_DATA } from "./data/generated/original-data";
import { browserPreferredGameLocale, gameLocale, gameText, loadGameLocale, normalizeGameLocale, type GameLocale } from "./i18n";
import { ORIGINAL_AUDIO_POLICY } from "./audio/originalAudio";
import {
  originalAudioRuntimeConsumesDecodedAudio,
  originalBgmRuntimeAsset,
  originalSoundEffectRuntimeAsset
} from "./audio/originalAudioRuntime";
import { BATTLE_RUNTIME_POLICY } from "./runtime/battle";
import { EVENT_RUNTIME_POLICY } from "./runtime/events";
import { MAP_MOVEMENT_POLICY } from "./runtime/movement";
import { POINTER_MOVEMENT_POLICY } from "./runtime/pointerMovement";
import { SAVE_RUNTIME_POLICY } from "./runtime/save";
import { ORIGINAL_SURFACE_RENDERING_POLICY } from "./render/originalRenderer";
import { SPRITE_RENDERING_POLICY } from "./render/sprites";
import {
  originalHpGaugeSegments,
  originalMapTileSourceRect,
  originalMpGaugeSegments,
  originalPlayerSpriteLayout,
  originalWayForDirection
} from "./render/assetLayoutRuntime";
import {
  originalHpRenderingSegments,
  originalMapTileRenderingOperation,
  originalMpRenderingSegments,
  originalPlayerRenderingComposition
} from "./render/originalRenderingRuntime";
import {
  ORIGINAL_MESSAGE_PRINT_LAYOUT,
  originalMessagePrintLines,
  originalMessageTextLines,
  originalStatusPanelActorName,
  originalUiRuntimeConsumesDecodedUi
} from "./render/originalUiRuntime";
import {
  originalKeyboardMovementIntent,
  originalTryMovePlan,
  originalWalkingStepFrame
} from "./runtime/originalMovementRuntime";
import { originalCollisionDecision, originalTilePassability } from "./runtime/originalCollisionRuntime";
import {
  originalBattleEnemyRawDamage,
  originalBattleEscapeFails,
  originalBattlePlayerRawDamage,
  originalBattleRuntimeConsumesDecodedBattle
} from "./runtime/originalBattleRuntime";
import {
  originalEventArgs,
  originalEventCommandName,
  originalEventDispatch,
  originalEventLabels,
  originalEventTokens,
  originalIfPredicateType
} from "./runtime/originalEventCommandRuntime";
import {
  originalMessageCommandDispatch,
  originalMessageCommandHasSourceBackedEffect,
  originalMessageCommandName,
  originalMessageCommandRuntimeConsumesDecodedMessageCommands
} from "./runtime/originalMessageCommandRuntime";
import { originalMsgSubIsColonLabelLine, originalMsgSubJumpLine, originalMsgSubJumpTarget } from "./runtime/originalMsgSubParser";
import {
  originalSaveDataSentinelLine,
  originalSaveGlobalKey,
  originalSaveGroupedKey,
  originalSaveParseValues,
  originalSavePartyKey,
  originalSaveRuntimeConsumesDecodedSaveFormat,
  originalSaveSectionHeader
} from "./runtime/originalSaveRuntime";
import {
  originalPointerPathRuntimeCanQueue,
  originalPointerPathRuntimeShouldInterrupt,
  originalPointerPathStepIntervalMs,
  originalPointerPolicyRuntimeConsumesPointerPolicy
} from "./runtime/originalPointerPolicyRuntime";

const BASE_WIDTH = 512;
const BASE_HEIGHT = 384;
const TILE = 32;
const ORIGINAL_MESSAGE_CACHE_MAX_INDEX = 100;
const ORIGINAL_MESSAGE_CACHE_CAPACITY = ORIGINAL_MESSAGE_CACHE_MAX_INDEX + 1;
const POINTER_STEP_INTERVAL_MS = originalPointerPathStepIntervalMs();
const ORIGINAL_POINTER_POLICY_CONSUMED = originalPointerPolicyRuntimeConsumesPointerPolicy();
const MAP_POINTER_MOVEMENT_ENABLED = POINTER_MOVEMENT_POLICY.enabled && ORIGINAL_POINTER_POLICY_CONSUMED;
const ORIGINAL_MESSAGE_COMMAND_EFFECTS_CONSUMED = originalMessageCommandRuntimeConsumesDecodedMessageCommands();
const EVENT_COMMANDS_ENABLED = EVENT_RUNTIME_POLICY.enabled && ORIGINAL_MESSAGE_COMMAND_EFFECTS_CONSUMED;
const ORIGINAL_BATTLE_RUNTIME_CONSUMED = originalBattleRuntimeConsumesDecodedBattle();
const BATTLE_RUNTIME_ENABLED = BATTLE_RUNTIME_POLICY.runtimeEnabled && ORIGINAL_BATTLE_RUNTIME_CONSUMED;
const BATTLE_FORMULAS_ENABLED = BATTLE_RUNTIME_POLICY.formulasEnabled && ORIGINAL_BATTLE_RUNTIME_CONSUMED;
const ORIGINAL_SAVE_FORMAT_CONSUMED = originalSaveRuntimeConsumesDecodedSaveFormat();
const ORIGINAL_INI_EXPORT_ENABLED = SAVE_RUNTIME_POLICY.originalIniExportEnabled && ORIGINAL_SAVE_FORMAT_CONSUMED;
const ORIGINAL_INI_IMPORT_ENABLED = SAVE_RUNTIME_POLICY.originalIniImportEnabled && ORIGINAL_SAVE_FORMAT_CONSUMED;
const PLAYER_SPRITES_ENABLED = SPRITE_RENDERING_POLICY.playerSpritesEnabled;
const ENEMY_SPRITES_ENABLED = SPRITE_RENDERING_POLICY.enemySpritesEnabled;
const ORIGINAL_AUDIO_RUNTIME_CONSUMED = originalAudioRuntimeConsumesDecodedAudio();
const BGM_PLAYBACK_ENABLED = ORIGINAL_AUDIO_POLICY.bgmPlaybackEnabled && ORIGINAL_AUDIO_RUNTIME_CONSUMED;
const MAP_MOVEMENT_ENABLED = MAP_MOVEMENT_POLICY.enabled;
const MAP_TILE_PASSABILITY_ENABLED = MAP_MOVEMENT_POLICY.tilePassabilityEnabled;
const MAP_EVENT_ACTIVATION_ENABLED = MAP_MOVEMENT_POLICY.eventActivationEnabled;
const UNVERIFIED_WEB_SOLID_RANGE_ENABLED = MAP_MOVEMENT_POLICY.unverifiedWebSolidRangeEnabled;
const BASE_WINDOW_RENDERING_ENABLED = ORIGINAL_SURFACE_RENDERING_POLICY.baseWindowEnabled;
const MAP_RENDERING_ENABLED = ORIGINAL_SURFACE_RENDERING_POLICY.mapEnabled;
const ORIGINAL_UI_RUNTIME_CONSUMED = originalUiRuntimeConsumesDecodedUi();
const STATUS_UI_RENDERING_ENABLED = ORIGINAL_SURFACE_RENDERING_POLICY.statusUiEnabled && ORIGINAL_UI_RUNTIME_CONSUMED;
const MESSAGE_UI_RENDERING_ENABLED = ORIGINAL_SURFACE_RENDERING_POLICY.messageUiEnabled && ORIGINAL_UI_RUNTIME_CONSUMED;
const BATTLE_UI_RENDERING_ENABLED = ORIGINAL_SURFACE_RENDERING_POLICY.battleUiEnabled && ORIGINAL_UI_RUNTIME_CONSUMED;
const MAP_X = 8;
const MAP_Y = 8;
const VIEW_COLS = 11;
const VIEW_ROWS = 9;
const MAP_VIEW_WIDTH = VIEW_COLS * TILE;
const MAP_VIEW_HEIGHT = VIEW_ROWS * TILE;
const MAP_CACHE_COLS = VIEW_COLS + 2;
const MAP_CACHE_ROWS = VIEW_ROWS + 2;
const MAP_CACHE_CROP_X = TILE;
const MAP_CACHE_CROP_Y = TILE;
const MESSAGE_X = ORIGINAL_MESSAGE_PRINT_LAYOUT.x;
const MESSAGE_Y = ORIGINAL_MESSAGE_PRINT_LAYOUT.y;
const CLICK_SHAPE_X = 488;
const CLICK_SHAPE_BOTTOM = 376;
const CLICK_SHAPE_WIDTH = 16;
const CLICK_SHAPE_BASE_Y = 360;
const CLICK_SHAPE_BASE_HEIGHT = 16;
const CLICK_SHAPE_BACK_COLOR = "#000080";
const WIPE_DURATION_STEPS = 16;
const WIPE_HALF_STEP_X = 11;
const WIPE_HALF_STEP_Y = 9;
const WIPE_DEST_CENTER_X = 184;
const WIPE_DEST_CENTER_Y = 152;
const WIPE_SOURCE_CENTER_X = 176;
const WIPE_SOURCE_CENTER_Y = 144;
const ORIGINAL_MAIN_BACK_COLOR = "#000080";
const CLICK_SHAPE_INTERVAL_MS = 30;
const ORIGINAL_FORM_FONT = 'bold 12pt "ＭＳ 明朝", "MS Mincho", serif';
const ORIGINAL_FORM_TEXT_LINE_HEIGHT = 22;
const ORIGINAL_MONEY_TEXT_WIDTH = 128;
const TITLE_BGM = "OPENING1.MID";
const DEFAULT_BATTLE_BGM = "BATTLE1.MID";
const ORIGINAL_EFFECTIVE_TIMER_INTERVAL_MS = 50;
const ORIGINAL_WALKING_SUB_STEPS = 4;
// TimerWalking is declared as 30ms in main.frm, but the target Win3.1 runtime effectively ticks at 50ms.
const WALK_LOGIC_STEP_INTERVAL_MS = ORIGINAL_EFFECTIVE_TIMER_INTERVAL_MS;
const WALK_SCROLL_DURATION_MS = ORIGINAL_WALKING_SUB_STEPS * ORIGINAL_EFFECTIVE_TIMER_INTERVAL_MS;
const WIPE_LOGIC_STEP_INTERVAL_MS = ORIGINAL_EFFECTIVE_TIMER_INTERVAL_MS;
const WIPE_DURATION_MS = WIPE_DURATION_STEPS * WIPE_LOGIC_STEP_INTERVAL_MS;
const KEY_CHECK_INTERVAL_MS = 30;
const TIMER_MESSAGE_INTERVAL_MS = 1;
const TIMER_MESSAGE_MAX_STEPS_PER_DRAIN = 32;
const BATTLE_ENEMY_DOUBLE_CLICK_WINDOW_MS = 500;
const SAVE_KEY = "faleal-story-classic-save-v1";
const AUTO_SAVE_KEY = "faleal-story-classic-autosave-v1";
const SETTINGS_KEY = "faleal-story-classic-settings-v1";
const CANONICAL_URL = "https://faleal-story-classic.frievegames.com/";
const PRODUCTION_HOST = "faleal-story-classic.frievegames.com";
const APP_NAME = "faleal_story_classic";
const GA_MEASUREMENT_ID = "G-B64JG15FNS";
const DEFAULT_AUDIO_VOLUME = 70;
const TRACKED_EVENT_NAMES = ["game_start", "game_clear_level", "game_clear", "share"] as const;
const UNVERIFIED_WEB_SOLID_TILE_INDEXES = new Set(Array.from({ length: 22 }, (_, index) => index + 42));
const ORIGINAL_STATE_WALK_USABLE = 2048;
const ORIGINAL_STATE_BATTLE_USABLE = 4096;
const ORIGINAL_STATE_REUSABLE_ITEM = 512;
const ORIGINAL_STATE_CUSTOM_SOUND = 8192;
const ORIGINAL_STATE_ALL_TARGETS = 16384;
const ORIGINAL_MAIN_STATE_WALK = 0;
const ORIGINAL_MAIN_STATE_WALKING = 1;
const ORIGINAL_MAIN_STATE_START = 2;
const ORIGINAL_MAIN_STATE_MENU_USING = 4;
const ORIGINAL_MAIN_STATE_IN_TIMER_MSG = 8;
const ORIGINAL_MAIN_STATE_WAIT_CLICK = 16;
const ORIGINAL_MAIN_STATE_WIPING = 64;
const ORIGINAL_MAIN_STATE_FIGHTING = 128;

type Mode = "title" | "map" | "message" | "battle" | "shop" | "inn";
type TrackedEventName = (typeof TRACKED_EVENT_NAMES)[number];
type PwaInstallPromptOutcome = "accepted" | "dismissed";
type PwaInstallPromptChoice = {
  outcome?: PwaInstallPromptOutcome;
  userChoice?: PwaInstallPromptOutcome;
  platform?: string;
};
type PwaBeforeInstallPromptEvent = Event & {
  platforms?: readonly string[];
  userChoice?: Promise<PwaInstallPromptChoice>;
  prompt: () => Promise<PwaInstallPromptChoice>;
};
type EventScriptContext = { checkMode: boolean; eventX?: number; eventY?: number; defaultMessageProcessing?: boolean };
type OriginalEventRunResult = { fired: boolean; queuedMessage: boolean };
type OriginalMapDrawTraceState = {
  first: number;
  second: number;
  third: number;
  thirdSuppressedByDefaultMessage: number;
  lastReason: string;
  lastStages: string[];
};

type Item = {
  id: number;
  name: string;
  type: number;
  state: number;
  price: number;
  sellPrice: number;
  value: number;
  wave: string;
  script: string;
};

type Magic = {
  id: number;
  name: string;
  type: number;
  state: number;
  mp: number;
  value: number;
  wave: string;
  script: string;
};

type Enemy = {
  id: number;
  name: string;
  hp: number;
  defense: number;
  speed: number;
  exp: number;
  gold: number;
  wave: string;
  image: string;
  actions: EnemyAction[];
};

type EnemyAction = {
  type: number;
  power: number;
  aux: number;
  text: string;
};

type Point = {
  x: number;
  y: number;
};

type PointerWalkStep = Point & {
  enterable: boolean;
};

type TileCollisionState = {
  normalizedX: number;
  normalizedY: number;
  event?: MapEvent;
  eventIndex: number;
  tilePassable: boolean;
  enterable: boolean;
  blockedEvent: boolean;
};

type EnemyParty = {
  id: number;
  enemyIds: number[];
  battleBgm: string;
};

type MapEvent = {
  x: number;
  y: number;
  script: string;
};

type MapData = {
  id: string;
  file: string;
  width: number;
  height: number;
  tileSheet: string;
  rawRows: number[][];
  events: MapEvent[];
};

type Actor = {
  name: string;
  hp: number;
  maxHp: number;
  mp: number;
  maxMp: number;
  offense: number;
  weapon: number;
  defense: number;
  protector: number;
  speed: number;
  level: number;
  experience: number;
  nextExp: number;
  maxHpUp: number;
  maxMpUp: number;
  offenseUp: number;
  defenseUp: number;
  speedUp: number;
  state: number;
};

type FormulaProbeOptions = {
  actor?: Partial<Actor>;
  inventoryIds?: number[];
  inventoryByActor?: number[][];
  magicIds?: number[];
  seed?: number;
};

type OriginalInspectionLine = {
  text: string;
  x: number;
  y: number;
};

type OriginalInspection = {
  kind: "item" | "magic" | "status";
  lines: OriginalInspectionLine[];
};

type OriginalMenuMode = "first" | "walk" | "fight" | "et" | "inn" | "shop";

type OriginalMenuSurface = {
  mode: OriginalMenuMode;
  system: {
    newGame: boolean;
    load: boolean;
    save: boolean;
    newSave: boolean;
    title: boolean;
  };
  commands: {
    inn: boolean;
    shop: boolean;
    check: boolean;
    fight: boolean;
    escape: boolean;
    item: boolean;
    magic: boolean;
    state: boolean;
  };
};

type OriginalMenuButtonDefinition = {
  key: string;
  labelId: string;
  shortcut?: string;
  visible: (surface: OriginalMenuSurface) => boolean;
  enabled: (surface: OriginalMenuSurface) => boolean;
  action: () => void;
};

type OriginalClickShapeState = {
  x: number;
  y: number;
  width: number;
  height: number;
  animation: number;
};

type InventoryEntry = {
  id: number;
  group: number;
  slot: number;
};

type BattleEnemy = Enemy & {
  currentHp: number;
  powerScale: number;
  powerModifier: number;
};

const EMPTY_BATTLE_ENEMY: BattleEnemy = {
  id: 0,
  name: "",
  hp: 0,
  defense: 0,
  speed: 0,
  exp: 0,
  gold: 0,
  wave: "",
  image: "",
  actions: Array.from({ length: 4 }, () => ({ type: -1, power: 0, aux: 0, text: "" })),
  currentHp: 0,
  powerScale: 1,
  powerModifier: 0
};

type BattleStatBuffers = {
  actorOffense: number[];
  actorDefense: number[];
  actorSpeed: number[];
  enemyDefense: number[];
  enemySpeed: number[];
  enemyActionEffects: number[][];
};

type BattleItemMenuMode = "root" | "use" | "transfer" | "discard" | "see" | "important";
type BattleMagicMenuMode = "root" | "chant" | "see";
type BattleItemSubmenuMode = Exclude<BattleItemMenuMode, "root">;
type BattleMagicSubmenuMode = Exclude<BattleMagicMenuMode, "root">;
type OriginalSystemSubmenuMode = "newSave" | "load";
type OriginalFieldRootMenuMode = "item" | "magic" | "shop";
type OriginalFieldSubmenuMode = BattleItemSubmenuMode | BattleMagicSubmenuMode | "shopBuy" | "shopSee";
type OriginalPopupSubmenuMode = BattleItemSubmenuMode | BattleMagicSubmenuMode | OriginalSystemSubmenuMode | OriginalFieldSubmenuMode;
type OriginalConfirmSelection = "yes" | "no";
type OriginalConfirmMenuSnapshot = {
  originalSystemMenuOpen: boolean;
  originalSystemSubmenuOpen: OriginalSystemSubmenuMode | undefined;
  originalSystemMenuSelected: number;
  originalSystemSubmenuSelected: number;
  originalFieldMenuOpen: OriginalFieldRootMenuMode | undefined;
  originalFieldSubmenuOpen: OriginalFieldSubmenuMode | undefined;
  originalFieldRootSelected: number;
  originalFieldLeafSelected: number;
  originalMenuBarTracking: boolean;
  originalMenuBarSelectedKey: string | undefined;
  originalPointerMenuPreviewArmed: boolean;
  pendingOriginalMenuCommandFocus: boolean;
  battle:
    | {
        selected: number;
        battleRootSelected: number;
        battleLeafSelected: number;
        battlePopupFocus: "root" | "leaf";
        phase: BattleState["phase"];
        battleItemMenuMode: BattleItemMenuMode | undefined;
        battleMagicMenuMode: BattleMagicMenuMode | undefined;
      }
    | undefined;
};

type BattleState = {
  partyId: number;
  enemies: BattleEnemy[];
  enemiesVisible: boolean;
  hiddenEnemyIndexes: number[];
  selected: number;
  battleRootSelected: number;
  battleLeafSelected: number;
  battlePopupFocus: "root" | "leaf";
  targetEnemyIndex: number;
  phase: "entry" | "command" | "magic" | "item";
  battleItemMenuMode?: BattleItemMenuMode;
  battleMagicMenuMode?: BattleMagicMenuMode;
  commandActorIndex: number;
  turnSpeeds: number[];
  turnPending: boolean;
  statBuffers: BattleStatBuffers;
  onWinScript?: string;
  onWinMessageQueue?: OriginalMessageQueueState;
  onWinContext?: EventScriptContext;
  battleLog: string[];
  rewardExp: number;
  rewardGold: number;
  endingQueued?: "victory" | "escape" | "end";
};

type BattleEnemyCommandClickCandidate = {
  battle: BattleState;
  commandActorIndex: number;
  enemyIndex: number;
  clickedAt: number;
};

type OriginalPopupRow = {
  label: string;
  action: () => void;
  disabled?: boolean;
  checked?: boolean;
  labelWarning?: boolean;
  detail?: string;
  detailWarning?: boolean;
  submenuMode?: OriginalPopupSubmenuMode;
};

type WalkingState = {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  moveX: number;
  moveY: number;
  direction: GameState["direction"];
  moveCount: number;
  startedAt: number;
  durationMs: number;
  nextFrameAt: number;
  suppressRandomEncounter?: boolean;
};

type OriginalForcedWalkState = {
  targetX: number;
  targetY: number;
};

type OriginalMapCacheState = {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  mapId: string;
  centerX: number;
  centerY: number;
  tileSheet: string;
};

type WipeState = {
  type: "out" | "in";
  count: number;
  duration: number;
  startedAt: number;
  durationMs: number;
  nextFrameAt: number;
  onDone?: () => void;
};

type TimerMessageWaitState = {
  until: number;
  action: () => void;
};

type OriginalMessageQueueEntry = {
  token: string;
  context: EventScriptContext;
};

type OriginalMessageQueueState = {
  slots: Array<OriginalMessageQueueEntry | undefined>;
  count: number;
  shiftedCount: number;
  lastShifted?: string;
  overflowed: boolean;
  nextTimerAt: number;
  timerSteps: number;
  lastTimerStepAt?: number;
};

type Settings = {
  bgmOn: boolean;
  bgmVolume: number;
  seOn: boolean;
  seVolume: number;
  battleTextSpeed: BattleTextSpeed;
  language: GameLocale;
};

type BattleTextSpeed = "fast" | "normal" | "slow";

type SerializedSaveState = ReturnType<typeof serializeState>;
type VersionedStoredSaveSlot = {
  version: 2;
  state: SerializedSaveState;
  ini?: string;
  savedAt?: number;
};
type StoredSaveSlot = SerializedSaveState | VersionedStoredSaveSlot;
type AutoSaveReason = "map_transition" | "battle_end" | "page_deactivate";
type StoredAutoSave = VersionedStoredSaveSlot & {
  reason: AutoSaveReason;
};

type BgmPlayback = {
  id: number;
  name: string;
  source?: AudioBufferSourceNode;
  gain?: GainNode;
  timer?: number;
};

type PlayerSpriteFramePixels = {
  source: ImageData;
  mask: ImageData;
};

type GameState = {
  mode: Mode;
  previousMode: Mode;
  mapId: string;
  x: number;
  y: number;
  direction: "up" | "down" | "left" | "right";
  bgm: string;
  enemyLevel: number;
  charType: number;
  sourceActorIndex: number;
  targetActorIndex: number;
  saveDataSlot: number;
  party: Actor[];
  money: number;
  inventory: number[];
  inventoryByActor: number[][];
  learnedMagic: number[];
  actorMagic: number[][];
  flags: Set<number>;
  tileOverrides: Record<string, number>;
  messages: string[];
  messageHistory: string[];
  originalMessageQueue: OriginalMessageQueueState;
  deferred: (() => void)[];
  waitClick: boolean;
  timerMessageWait?: TimerMessageWaitState;
  walking?: WalkingState;
  forcedWalk?: OriginalForcedWalkState;
  wipe?: WipeState;
  activeInspection?: OriginalInspection;
  originalMenuMode: OriginalMenuMode;
  battle?: BattleState;
  shopItems?: number[];
  innPrice?: number;
  settings: Settings;
  rngSeed: number;
  encounterCount: number;
  pointerPath: Point[];
  pointerTarget?: Point;
  started: boolean;
  endingReached: boolean;
  gameOverQueued: boolean;
};

type DataShape = {
  items: Item[];
  magic: Magic[];
  enemies: Enemy[];
  enemyParties: EnemyParty[];
  maps: MapData[];
  defaultSave: Omit<GameState, "mode" | "previousMode" | "direction" | "flags" | "tileOverrides" | "messages" | "messageHistory" | "originalMessageQueue" | "deferred" | "waitClick" | "timerMessageWait" | "walking" | "forcedWalk" | "wipe" | "activeInspection" | "settings" | "rngSeed" | "started" | "endingReached" | "gameOverQueued" | "inventoryByActor" | "sourceActorIndex" | "targetActorIndex" | "originalMenuMode" | "pointerTarget"> & {
    flags: number[];
    magic: number[];
    magicByActor?: number[][];
    inventoryByActor?: number[][];
    openingScript: string;
  };
  sampleSaves: Array<{
    name: string;
    party: Actor[];
    money: number;
    inventory: number[];
    inventoryByActor?: number[][];
    magic: number[];
    magicByActor?: number[][];
    flags: number[];
    x: number;
    y: number;
    mapId: string;
    bgm: string;
    enemyLevel: number;
    charType: number;
  }>;
};

const ORIGINAL_ACTOR_GROWTH_KEYS = ["maxHpUp", "maxMpUp", "offenseUp", "defenseUp", "speedUp"] as const;
const TARGET_ACTOR_EXP_CHEAT_CODE = "seishintotokinoheya";
const TARGET_ACTOR_EXP_CHEAT_LEVELS = 10;
const MONEY_CHEAT_CODE = "elonmuskspocketmoney";
const MONEY_CHEAT_AMOUNT = 10000;
const FALLBACK_RNG_SEED = 0x19960331;

declare global {
  interface ImportMeta {
    hot?: {
      dispose(callback: () => void): void;
    };
  }

  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __FALEAL_ANALYTICS_STATUS__?: Record<string, unknown>;
    __falealGoogleTagConfigured?: boolean;
    __falealGoogleTagConfiguredId?: string;
    __FALEAL_BGM_RUNTIME_OWNER__?: number;
    __FALEAL_DEBUG__?: {
      state: () => Record<string, unknown>;
      press: (key: string) => void;
      runScript: (script: string, checkMode?: boolean) => void;
      forceBattle: (partyId: number) => void;
      forceEnemyAction: (enemyIndex: number, actionSlot: number) => void;
      boostParty: () => void;
      setPosition: (mapId: string, x: number, y: number) => void;
      grantItem: (itemId: number, ownerIndex?: number) => void;
      setSeed: (seed: number) => void;
      setActorStats: (actorIndex: number, actor: Partial<Actor>) => void;
      configureFormulaProbe: (options?: FormulaProbeOptions) => void;
      clearLastSe: () => void;
      exportIni: (sectionName?: string) => string;
      importIni: (text: string) => void;
    };
  }
}

await loadGameLocale(readStoredLanguageSetting());

const ORIGINAL_SOURCE_DATA = ORIGINAL_DATA as unknown as DataShape;
let DATA = localizeOriginalData(ORIGINAL_SOURCE_DATA);
const maps = new Map<string, MapData>();
const items = new Map<number, Item>();
const magic = new Map<number, Magic>();
const enemies = new Map<number, Enemy>();
const enemyParties = new Map<number, EnemyParty>();
applyLocalizedData(DATA);

function replaceIndexedMap<K, V>(target: Map<K, V>, values: V[], keyFor: (value: V) => K): void {
  target.clear();
  values.forEach((value) => target.set(keyFor(value), value));
}

function applyLocalizedData(nextData: DataShape): void {
  DATA = nextData;
  replaceIndexedMap(maps, DATA.maps, (map) => map.id);
  replaceIndexedMap(items, DATA.items, (item) => item.id);
  replaceIndexedMap(magic, DATA.magic, (spell) => spell.id);
  replaceIndexedMap(enemies, DATA.enemies, (enemy) => enemy.id);
  replaceIndexedMap(enemyParties, DATA.enemyParties, (party) => party.id);
}

function textIdNumber(value: number, width = 3): string {
  return String(value).padStart(width, "0");
}

function printToken(id: string, values?: Parameters<typeof gameText>[1]): string {
  return `Print ${gameText(id, values)}`;
}

function localizeOriginalData(source: DataShape): DataShape {
  return {
    ...source,
    items: source.items.map((item) => ({
      ...item,
      name: item.name ? gameText(`data.items.${textIdNumber(item.id)}.name`) : item.name,
      script: localizeOriginalScriptPrints(`data.items.${textIdNumber(item.id)}.script`, item.script)
    })),
    magic: source.magic.map((spell) => ({
      ...spell,
      name: spell.name ? gameText(`data.magic.${textIdNumber(spell.id)}.name`) : spell.name,
      script: localizeOriginalScriptPrints(`data.magic.${textIdNumber(spell.id)}.script`, spell.script)
    })),
    enemies: source.enemies.map((enemy) => ({
      ...enemy,
      name: enemy.name ? gameText(`data.enemies.${textIdNumber(enemy.id)}.name`) : enemy.name,
      actions: enemy.actions.map((action, index) => ({
        ...action,
        text: action.text ? gameText(`data.enemies.${textIdNumber(enemy.id)}.actions.${textIdNumber(index + 1, 2)}.text`) : action.text
      }))
    })),
    maps: source.maps.map((map) => ({
      ...map,
      events: map.events.map((event, index) => ({
        ...event,
        script: localizeOriginalScriptPrints(`data.maps.${map.id}.events.${textIdNumber(index + 1)}.script`, event.script)
      }))
    })),
    defaultSave: {
      ...source.defaultSave,
      party: source.defaultSave.party.map((actor, index) => ({
        ...actor,
        name: actor.name ? gameText(`data.defaultSave.party.${textIdNumber(index + 1, 2)}.name`) : actor.name
      })),
      openingScript: localizeOriginalScriptPrints("data.defaultSave.openingScript", source.defaultSave.openingScript)
    },
    sampleSaves: source.sampleSaves.map((sample, sampleIndex) => ({
      ...sample,
      name: sample.name ? gameText(`data.sampleSaves.${textIdNumber(sampleIndex + 1, 2)}.name`) : sample.name,
      party: sample.party.map((actor, actorIndex) => ({
        ...actor,
        name: actor.name ? gameText(`data.sampleSaves.${textIdNumber(sampleIndex + 1, 2)}.party.${textIdNumber(actorIndex + 1, 2)}.name`) : actor.name
      }))
    }))
  };
}

function localizeOriginalScriptPrints(scopeId: string, script: string): string {
  if (!script) return script;
  let printIndex = 0;
  return script
    .split("\\")
    .map((token) => {
      const match = /^(\s*Print(?:\s+|$))(.*)$/i.exec(token);
      if (!match || match[2].length === 0) return token;
      printIndex += 1;
      return `${match[1]}${gameText(`${scopeId}.print.${textIdNumber(printIndex)}`)}`;
    })
    .join("\\");
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`${selector} not found`);
  }
  return element;
}

function requiredCanvasContext(element: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = element.getContext("2d");
  if (!context) {
    throw new Error("2D context not available");
  }
  return context;
}

const originalMenuBar = document.querySelector<HTMLDivElement>("#original-menu-bar");
const canvas = requiredElement<HTMLCanvasElement>("#game");
const settingsButton = document.querySelector<HTMLButtonElement>("#settings-button");
const settingsDialog = document.querySelector<HTMLDialogElement>("#settings-dialog");
const helpButton = document.querySelector<HTMLButtonElement>("#help-button");
const helpDialog = document.querySelector<HTMLDialogElement>("#help-dialog");
const bgmEnabledInput = document.querySelector<HTMLInputElement>("#bgm-enabled");
const bgmVolumeRange = document.querySelector<HTMLInputElement>("#bgm-volume-range");
const bgmVolumeOutput = document.querySelector<HTMLOutputElement>("#bgm-volume-output");
const seEnabledInput = document.querySelector<HTMLInputElement>("#se-enabled");
const seVolumeRange = document.querySelector<HTMLInputElement>("#se-volume-range");
const seVolumeOutput = document.querySelector<HTMLOutputElement>("#se-volume-output");
const battleTextSpeedSelect = document.querySelector<HTMLSelectElement>("#battle-text-speed-select");
const settingsLanguageSelect = document.querySelector<HTMLSelectElement>("#settings-language-select");
const pwaInstallButton = document.querySelector<HTMLButtonElement>("#install-pwa-button");
const helpLanguageSelect = document.querySelector<HTMLSelectElement>("#help-language-select");
const shareXButton = document.querySelector<HTMLButtonElement>("#share-x");
const shareFacebookButton = document.querySelector<HTMLButtonElement>("#share-facebook");
const copyUrlButton = document.querySelector<HTMLButtonElement>("#copy-url");
const copyToast = document.querySelector<HTMLDivElement>("#copy-toast");
const ctx = requiredCanvasContext(canvas);
ctx.imageSmoothingEnabled = false;

const imageCache = new Map<string, HTMLImageElement>();
const playerSpriteFrameCache = new Map<string, PlayerSpriteFramePixels>();
const seCache = new Map<string, HTMLAudioElement>();
const activeSeAudio = new Set<HTMLAudioElement>();
const bgmRuntimeOwner = (window.__FALEAL_BGM_RUNTIME_OWNER__ ?? 0) + 1;
window.__FALEAL_BGM_RUNTIME_OWNER__ = bgmRuntimeOwner;
const bgmBufferCache = new Map<string, AudioBuffer>();
const bgmBufferPromises = new Map<string, Promise<AudioBuffer | undefined>>();
let originalMapCache: OriginalMapCacheState | undefined;
let originalMapDrawTrace: OriginalMapDrawTraceState = makeOriginalMapDrawTraceState();
let deferredPwaInstallPrompt: PwaBeforeInstallPromptEvent | undefined;
const BGM_OPUS_FILES: Record<string, string> = {
  "OPENING1.MID": "OPENING1.opus",
  "CAVE1.MID": "CAVE1.opus",
  "BATTLE1.MID": "BATTLE1.opus",
  "FIELD1.MID": "FIELD1.opus",
  "TOWN1.MID": "TOWN1.opus",
  "TOWER1.MID": "TOWER1.opus",
  "SHIP1.MID": "SHIP1.opus",
  "BATTLE2.MID": "BATTLE2.opus"
};
let originalMenuPopup: HTMLDivElement | undefined;
let originalMenuSubPopup: HTMLDivElement | undefined;
let originalSystemMenuOpen = false;
let originalSystemSubmenuOpen: OriginalSystemSubmenuMode | undefined;
let originalSystemMenuSelected = 0;
let originalSystemSubmenuSelected = 0;
let originalFieldMenuOpen: OriginalFieldRootMenuMode | undefined;
let originalFieldSubmenuOpen: OriginalFieldSubmenuMode | undefined;
let originalFieldRootSelected = 0;
let originalFieldLeafSelected = 0;
let originalMenuBarTracking = false;
let originalMenuBarSelectedKey: string | undefined;
let originalPointerMenuPreviewArmed = false;
let pendingOriginalMenuCommandFocus = false;
let originalConfirmDialog: HTMLDialogElement | undefined;
let originalConfirmMessageElement: HTMLParagraphElement | undefined;
let originalConfirmWarningElement: HTMLParagraphElement | undefined;
let originalConfirmYesButton: HTMLButtonElement | undefined;
let originalConfirmNoButton: HTMLButtonElement | undefined;
let originalConfirmAction: (() => void) | undefined;
let originalConfirmPreviousFocus: HTMLElement | undefined;
let originalConfirmSelection: OriginalConfirmSelection = "yes";
let originalConfirmMenuSnapshot: OriginalConfirmMenuSnapshot | undefined;
let animationTick = 0;
let currentBgm: BgmPlayback | undefined;
let requestedBgm: string | undefined;
let lastRequestedSe: string | undefined;
let clickAnimationStartedAt = 0;
let audioContext: AudioContext | undefined;
let pageAudioActive = document.visibilityState === "visible";
let bgmStoppedBySource = false;
let gamepadCooldownUntil = 0;
let pointerStepCooldownUntil = 0;
let nextKeyCheckAt = 0;
let bgmPlaybackId = 0;
let copyToastTimer: number | undefined;
let animationFrameId: number | undefined;
let inactiveStartedAt: number | undefined;
let originalMessageQueueDraining = false;
const heldMovementKeys = new Set<string>();
const heldKeyboardMovementKeys = new Set<string>();
const heldTouchMovementKeys = new Set<string>();
const heldTouchMovementPointerKeys = new Map<number, string | undefined>();
let originalMouseDown = false;
let battleEnemyCommandClickCandidate: BattleEnemyCommandClickCandidate | undefined;
let targetActorExpCheatProgress = "";
let moneyCheatProgress = "";
applyStaticDomI18n();

function applyStaticDomI18n(): void {
  document.documentElement.lang = gameLocale();
  document.title = gameText("app.title");
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", gameText("app.meta.description"));
  document.querySelectorAll<HTMLElement>("[data-i18n-title]").forEach((element) => {
    const titleId = element.dataset.i18nTitle;
    if (titleId) element.setAttribute("title", gameText(titleId));
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-aria-label]").forEach((element) => {
    const labelId = element.dataset.i18nAriaLabel;
    if (labelId) element.setAttribute("aria-label", gameText(labelId));
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-text]").forEach((element) => {
    const textId = element.dataset.i18nText;
    if (textId) element.textContent = gameText(textId);
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-markdown]").forEach((element) => {
    const markdownId = element.dataset.i18nMarkdown;
    if (markdownId) renderHelpMarkdown(element, gameText(markdownId));
  });
  refreshOriginalConfirmDialogI18n();
}

function refreshOriginalConfirmDialogI18n(): void {
  const title = originalConfirmDialog?.querySelector<HTMLElement>("#original-confirm-title");
  if (title) title.textContent = gameText("ui.confirm.title");
  if (originalConfirmYesButton) originalConfirmYesButton.textContent = gameText("ui.confirm.yes");
  if (originalConfirmNoButton) originalConfirmNoButton.textContent = gameText("ui.confirm.no");
}

function renderHelpMarkdown(container: HTMLElement, markdown: string): void {
  container.replaceChildren();
  let currentList: HTMLUListElement | undefined;
  let currentParagraph: HTMLParagraphElement | undefined;
  const flushParagraph = () => {
    currentParagraph = undefined;
  };
  const flushList = () => {
    currentList = undefined;
  };

  for (const rawLine of markdown.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = Math.min(heading[1].length + 1, 4) as 2 | 3 | 4;
      const element = document.createElement(`h${level}`);
      element.textContent = heading[2];
      container.append(element);
      continue;
    }

    const listItem = /^[-*]\s+(.+)$/.exec(line);
    if (listItem) {
      flushParagraph();
      if (!currentList) {
        currentList = document.createElement("ul");
        container.append(currentList);
      }
      const element = document.createElement("li");
      element.textContent = listItem[1];
      currentList.append(element);
      continue;
    }

    flushList();
    if (!currentParagraph) {
      currentParagraph = document.createElement("p");
      container.append(currentParagraph);
    } else {
      currentParagraph.append(document.createElement("br"));
    }
    currentParagraph.append(line);
  }
}

function makeOriginalMessageQueueState(): OriginalMessageQueueState {
  return {
    slots: Array(ORIGINAL_MESSAGE_CACHE_CAPACITY).fill(undefined),
    count: 0,
    shiftedCount: 0,
    overflowed: false,
    nextTimerAt: 0,
    timerSteps: 0
  };
}

function originalMessageQueueEntries(queue = state.originalMessageQueue): OriginalMessageQueueEntry[] {
  return queue.slots.slice(0, queue.count).filter((entry): entry is OriginalMessageQueueEntry => Boolean(entry));
}

function originalMessageQueueCount(queue = state.originalMessageQueue): number {
  return queue.count;
}

function clearOriginalMessageQueueEntries(queue = state.originalMessageQueue): void {
  queue.slots.fill(undefined);
  queue.count = 0;
  queue.nextTimerAt = 0;
}

function appendOriginalMessageCacheEntry(entry: OriginalMessageQueueEntry): void {
  appendOriginalMessageQueueEntry(state.originalMessageQueue, entry);
}

function appendOriginalMessageQueueEntry(queue: OriginalMessageQueueState, entry: OriginalMessageQueueEntry): void {
  if (queue.count > ORIGINAL_MESSAGE_CACHE_MAX_INDEX) {
    queue.overflowed = true;
    return;
  }
  queue.slots[queue.count] = entry;
  queue.count += 1;
  if (queue.nextTimerAt <= 0) queue.nextTimerAt = performance.now();
}

function makeOriginalMessageQueueStateFromTokens(tokens: string[], context: EventScriptContext): OriginalMessageQueueState {
  const queue = makeOriginalMessageQueueState();
  tokens.forEach((token) => appendOriginalMessageQueueEntry(queue, { token, context: { ...context } }));
  return queue;
}

const state: GameState = makeInitialState();
initAnalytics();
wirePwaInstallPrompt();
registerServiceWorker();
loadImages().then(() => startFrameLoop());
wireInput();
wireSettingsControls();
wireHelpControls();
wireShareButtons();
wireAudioLifecycle();
import.meta.hot?.dispose(() => {
  if (isCurrentBgmRuntimeOwner()) stopBgm(true, true);
});
playBgm(TITLE_BGM);
if (debugAllowed()) {
  window.__FALEAL_DEBUG__ = {
    state: () => ({
      mode: state.mode,
      mapId: state.mapId,
      x: state.x,
      y: state.y,
      originalMapCache: originalMapCache
        ? {
            mapId: originalMapCache.mapId,
            centerX: originalMapCache.centerX,
            centerY: originalMapCache.centerY,
            width: originalMapCache.canvas.width,
            height: originalMapCache.canvas.height
          }
        : null,
      walking: state.walking
        ? {
            fromX: state.walking.fromX,
            fromY: state.walking.fromY,
            toX: state.walking.toX,
            toY: state.walking.toY,
            moveCount: state.walking.moveCount,
            scrollPixels: smoothWalkingScrollFrame(state.walking).scrollPixels,
            durationMs: state.walking.durationMs,
            suppressRandomEncounter: Boolean(state.walking.suppressRandomEncounter)
          }
        : null,
      forcedWalk: state.forcedWalk ? { ...state.forcedWalk } : null,
      wipe: state.wipe ? { type: state.wipe.type, count: state.wipe.count, step: smoothWipeStep(state.wipe) } : null,
      requestedBgm,
      currentBgm: currentBgm?.name,
      lastSe: lastRequestedSe ?? null,
      autoSave: latestAutoSave()
        ? {
            savedAt: latestAutoSave()?.savedAt ?? null,
            reason: latestAutoSave()?.reason ?? null,
            mapId: latestAutoSave()?.state.mapId ?? null,
            x: latestAutoSave()?.state.x ?? null,
            y: latestAutoSave()?.state.y ?? null,
            saveDataSlot: latestAutoSave()?.state.saveDataSlot ?? 0
          }
        : null,
      money: state.money,
      direction: state.direction,
      saveDataSlot: state.saveDataSlot,
      mainState: originalMainStateMask(),
      timerMessageCanRun: originalTimerMessageCanRun(),
      timerMessageWaitMs: state.timerMessageWait ? Math.max(0, Math.round(state.timerMessageWait.until - performance.now())) : null,
      originalMenu: originalMenuSurface(),
      messages: state.messages.length,
      messageLines: [...state.messageHistory],
      deferredCount: state.deferred.length,
      messageCasheLength: originalMessageQueueCount(),
      messageCasheCapacity: ORIGINAL_MESSAGE_CACHE_CAPACITY,
      messageCasheMaxIndex: ORIGINAL_MESSAGE_CACHE_MAX_INDEX,
      messageCasheHead: originalMessageQueueEntries().slice(0, 4).map((entry) => entry.token),
      messageCasheShiftedCount: state.originalMessageQueue.shiftedCount,
      messageCasheLastShifted: state.originalMessageQueue.lastShifted ?? null,
      messageCasheOverflowed: state.originalMessageQueue.overflowed,
      messageCasheTimerSteps: state.originalMessageQueue.timerSteps,
      messageCasheTimerNextMs: state.originalMessageQueue.nextTimerAt
        ? Math.max(0, Math.round(state.originalMessageQueue.nextTimerAt - performance.now()))
        : null,
      messageCasheTimerLastStepMsAgo: state.originalMessageQueue.lastTimerStepAt
        ? Math.max(0, Math.round(performance.now() - state.originalMessageQueue.lastTimerStepAt))
        : null,
      originalMapDrawTrace: {
        ...originalMapDrawTrace,
        lastStages: [...originalMapDrawTrace.lastStages]
      },
      waitClick: state.waitClick,
      clickShape: originalClickShapeState(),
      inventory: flattenInventoryGroups().length,
      inventoryIds: flattenInventoryGroups(),
      inventoryByActor: state.inventoryByActor.map((row) => [...row]),
      flags: state.flags.size,
      flagIds: [...state.flags].sort((a, b) => a - b),
      battle: Boolean(state.battle),
      battlePhase: state.battle?.phase,
      battleEnemiesVisible: state.battle?.enemiesVisible,
      battleHiddenEnemyIndexes: state.battle?.hiddenEnemyIndexes ?? [],
      battleCommandActorIndex: state.battle?.commandActorIndex,
      battleTargetEnemyIndex: state.battle?.targetEnemyIndex,
      battleTurnFlag: state.battle?.turnPending ?? false,
      battleMessageCashe2Length: state.battle?.onWinMessageQueue ? originalMessageQueueCount(state.battle.onWinMessageQueue) : 0,
      battleMessageCashe2Capacity: ORIGINAL_MESSAGE_CACHE_CAPACITY,
      battleMessageCashe2Head: state.battle?.onWinMessageQueue
        ? originalMessageQueueEntries(state.battle.onWinMessageQueue).slice(0, 4).map((entry) => entry.token)
        : [],
      battleMessageCashe2Overflowed: state.battle?.onWinMessageQueue?.overflowed ?? false,
      battleTurnSpeeds: state.battle?.turnSpeeds.map((value) => Math.round(value * 1000) / 1000) ?? [],
      battleEnemyHp: state.battle?.enemies.map((enemy) => enemy.currentHp) ?? [],
      battleEnemies:
        state.battle?.enemies.map((enemy) => ({
          id: enemy.id,
          name: enemy.name,
          hp: enemy.hp,
          currentHp: enemy.currentHp,
          baseDefense: enemy.defense,
          defense: state.battle ? battleEnemyDefense(enemy, state.battle) : enemy.defense,
          baseSpeed: enemy.speed,
          speed: state.battle ? battleEnemySpeed(enemy, state.battle) : enemy.speed,
          exp: enemy.exp,
          gold: enemy.gold,
          wave: enemy.wave,
          powerScale: enemy.powerScale,
          powerModifier: enemy.powerModifier,
          actions: enemy.actions.map((action) => ({ ...action }))
        })) ?? [],
      battleRewardExp: state.battle?.rewardExp ?? 0,
      battleRewardGold: state.battle?.rewardGold ?? 0,
      battleStatBuffers: state.battle
        ? {
            actorOffense: [...state.battle.statBuffers.actorOffense],
            actorDefense: [...state.battle.statBuffers.actorDefense],
            actorSpeed: [...state.battle.statBuffers.actorSpeed],
            enemyDefense: [...state.battle.statBuffers.enemyDefense],
            enemySpeed: [...state.battle.statBuffers.enemySpeed],
            enemyActionEffects: state.battle.statBuffers.enemyActionEffects.map((row) => [...row])
          }
        : null,
      battleLog: state.battle?.battleLog.slice(-8) ?? [],
      partyHp: state.party.map((actor) => actor.hp),
      partyStats: state.party.map((actor, index) => ({
        name: actor.name,
        hp: actor.hp,
        maxHp: actor.maxHp,
        mp: actor.mp,
        maxMp: actor.maxMp,
        baseOffense: actor.offense,
        offense: state.battle ? state.battle.statBuffers.actorOffense[index] ?? actor.offense : actor.offense,
        weapon: actor.weapon,
        baseDefense: actor.defense,
        defense: state.battle ? state.battle.statBuffers.actorDefense[index] ?? actor.defense : actor.defense,
        protector: actor.protector,
        baseSpeed: actor.speed,
        speed: state.battle ? state.battle.statBuffers.actorSpeed[index] ?? actor.speed : actor.speed,
        level: actor.level,
        experience: actor.experience,
        nextExp: actor.nextExp,
        maxHpUp: actor.maxHpUp,
        maxMpUp: actor.maxMpUp,
        offenseUp: actor.offenseUp,
        defenseUp: actor.defenseUp,
        speedUp: actor.speedUp,
        state: actor.state,
        totalOffense: state.battle ? battleTotalOffense(actor, state.battle) : totalOffense(actor),
        totalDefense: state.battle ? battleTotalDefense(actor, state.battle) : totalDefense(actor)
      })),
      partyStates: state.party.map((actor) => actor.state),
      partyLevel: state.party.map((actor) => actor.level),
      actorMagic: state.actorMagic.map((ids) => [...ids]),
      charType: state.charType,
      sourceActorIndex: originalSourceActorIndex(),
      targetActorIndex: targetActorIndex(),
      rngSeed: state.rngSeed,
      encounterCount: state.encounterCount,
      pointerPath: state.pointerPath.map((point) => ({ ...point })),
      pointerTarget: state.pointerTarget ? { ...state.pointerTarget } : null,
      started: state.started,
      endingReached: state.endingReached,
      gameOverQueued: state.gameOverQueued
    }),
    press: (key: string) => handleKey(key),
    runScript: (script: string, checkMode = false) => executeEventScript(script, { checkMode }),
    forceBattle: (partyId: number) => startBattle(partyId),
    forceEnemyAction: (enemyIndex: number, actionSlot: number) => {
      const battle = state.battle;
      const enemy = battle?.enemies[enemyIndex];
      const action = enemy?.actions[actionSlot];
      if (!battle || !enemy || !action) return;
      applyEnemyAction(enemy, { action, slot: actionSlot }, battle);
    },
    boostParty: () => {
      state.party.forEach((actor) => {
        if (actor.state <= 0) return;
        actor.level = Math.max(actor.level, 30);
        actor.maxHp = Math.max(actor.maxHp, 5000);
        actor.hp = actor.maxHp;
        actor.maxMp = Math.max(actor.maxMp, 1200);
        actor.mp = actor.maxMp;
        actor.offense = Math.max(actor.offense, 800);
        actor.defense = Math.max(actor.defense, 600);
        actor.speed = Math.max(actor.speed, 600);
      });
      const allMagic = DATA.magic.filter((spell) => spell.name).map((spell) => spell.id);
      state.learnedMagic = [...allMagic];
      state.actorMagic = state.party.map((actor) => (actor.state > 0 ? [...allMagic] : []));
    },
    setPosition: (mapId: string, x: number, y: number) => {
      state.mapId = normalizeMapArg(mapId, state.mapId);
      state.x = clamp(x, 0, currentMap().width - 1);
      state.y = clamp(y, 0, currentMap().height - 1);
      resetOriginalMapDrawTrace();
      invalidateOriginalMapCache();
      state.mode = "map";
      state.previousMode = "map";
      state.battle = undefined;
      state.messages = [];
      state.messageHistory = [];
      state.originalMessageQueue = makeOriginalMessageQueueState();
      state.deferred = [];
      state.waitClick = false;
      clickAnimationStartedAt = 0;
      state.timerMessageWait = undefined;
      state.activeInspection = undefined;
      state.walking = undefined;
      state.forcedWalk = undefined;
      clearPointerIntent();
    },
    grantItem: (itemId: number, ownerIndex = selectedActorIndex()) => addInventory(itemId, ownerIndex),
    setSeed: (seed: number) => {
      state.rngSeed = seed >>> 0;
    },
    setActorStats: (actorIndex: number, actor: Partial<Actor>) => {
      if (state.party[actorIndex]) Object.assign(state.party[actorIndex], actor);
    },
    configureFormulaProbe: (options: FormulaProbeOptions = {}) => configureFormulaProbe(options),
    clearLastSe: () => {
      lastRequestedSe = undefined;
    },
    exportIni: (sectionName = "Default") =>
      ORIGINAL_INI_EXPORT_ENABLED ? exportOriginalIniSection(sectionName, serializeState()) : "",
    importIni: (text: string) => {
      if (ORIGINAL_INI_IMPORT_ENABLED) {
        importOriginalIniSection(text);
      }
    }
  };
}

function makeInitialState(): GameState {
  const savedSettings = readSettings();
  const save = DATA.defaultSave;
  const inventoryByActor = cloneInventoryGroups(save.inventoryByActor, save.inventory, save.charType);
  return {
    mode: "title",
    previousMode: "title",
    mapId: save.mapId,
    x: save.x,
    y: save.y,
    direction: "down",
    bgm: save.bgm,
    enemyLevel: save.enemyLevel,
    charType: save.charType,
    sourceActorIndex: 0,
    targetActorIndex: 0,
    saveDataSlot: 0,
    party: cloneParty(save.party),
    money: save.money,
    inventory: flattenInventoryGroups(inventoryByActor),
    inventoryByActor,
    learnedMagic: [...save.magic],
    actorMagic: cloneActorMagic(save.magicByActor, save.magic),
    flags: new Set(save.flags),
    tileOverrides: {},
    messages: [],
    messageHistory: [],
    originalMessageQueue: makeOriginalMessageQueueState(),
    deferred: [],
    waitClick: false,
    timerMessageWait: undefined,
    walking: undefined,
    forcedWalk: undefined,
    wipe: undefined,
    activeInspection: undefined,
    originalMenuMode: "first",
    settings: savedSettings,
    rngSeed: makeRuntimeRngSeed(),
    encounterCount: save.encounterCount ?? 200,
    pointerPath: [],
    pointerTarget: undefined,
    started: false,
    endingReached: false,
    gameOverQueued: false
  };
}

function makeRuntimeRngSeed(): number {
  const randomBytes = new Uint32Array(1);
  globalThis.crypto?.getRandomValues?.(randomBytes);
  const now = Date.now() >>> 0;
  const subMs = Math.floor((globalThis.performance?.now?.() ?? 0) * 1000) >>> 0;
  const seed = (randomBytes[0] ^ now ^ subMs ^ FALLBACK_RNG_SEED) >>> 0;
  return seed || FALLBACK_RNG_SEED;
}

function originalMainStateMask(): number {
  if (!state.started) return ORIGINAL_MAIN_STATE_START;
  let mask = ORIGINAL_MAIN_STATE_WALK;
  if (state.walking) mask |= ORIGINAL_MAIN_STATE_WALKING;
  if (state.mode === "shop" || state.mode === "inn") {
    mask |= ORIGINAL_MAIN_STATE_MENU_USING;
  }
  if (state.waitClick) mask |= ORIGINAL_MAIN_STATE_WAIT_CLICK;
  if (state.wipe) mask |= ORIGINAL_MAIN_STATE_WIPING;
  if (state.battle) mask |= ORIGINAL_MAIN_STATE_FIGHTING;
  if (originalMessageQueueCount() > 0 || state.timerMessageWait) mask |= ORIGINAL_MAIN_STATE_IN_TIMER_MSG;
  return mask;
}

function originalMainStateIsWalk(): boolean {
  return originalMainStateMask() === ORIGINAL_MAIN_STATE_WALK;
}

function originalMainStateAllowsStartOrWalkMenu(): boolean {
  const mask = originalMainStateMask();
  return mask === ORIGINAL_MAIN_STATE_START || mask === ORIGINAL_MAIN_STATE_WALK;
}

function originalBattleCommandMenuReady(): boolean {
  return originalMainStateMask() === ORIGINAL_MAIN_STATE_FIGHTING && state.battle?.phase === "command";
}

function originalBattleNativeMenuReady(): boolean {
  const battle = state.battle;
  if (!battle || originalMainStateMask() !== ORIGINAL_MAIN_STATE_FIGHTING) return false;
  return battle.phase === "command" || battle.phase === "item" || battle.phase === "magic";
}

function originalTimerMessageCanRun(): boolean {
  return (originalMainStateMask() & (ORIGINAL_MAIN_STATE_WAIT_CLICK | ORIGINAL_MAIN_STATE_WIPING)) === 0;
}

function setOriginalMenuMode(mode: OriginalMenuMode): void {
  state.originalMenuMode = mode;
}

function originalMenuSurface(mode: OriginalMenuMode = state.originalMenuMode): OriginalMenuSurface {
  const disabledSystem = { newGame: false, load: false, save: false, newSave: false, title: false };
  const hiddenCommands = { inn: false, shop: false, check: false, fight: false, escape: false, item: false, magic: false, state: false };
  if (mode === "first") {
    return {
      mode,
      system: { newGame: true, load: true, save: false, newSave: false, title: false },
      commands: hiddenCommands
    };
  }
  if (mode === "walk") {
    return {
      mode,
      system: { newGame: true, load: true, save: true, newSave: true, title: true },
      commands: { ...hiddenCommands, check: true, item: true, magic: true, state: true }
    };
  }
  if (mode === "fight") {
    return {
      mode,
      system: disabledSystem,
      commands: { ...hiddenCommands, fight: true, escape: true, item: true, magic: true }
    };
  }
  if (mode === "inn") {
    return { mode, system: disabledSystem, commands: { ...hiddenCommands, inn: true } };
  }
  if (mode === "shop") {
    return { mode, system: disabledSystem, commands: { ...hiddenCommands, shop: true } };
  }
  return { mode: "et", system: disabledSystem, commands: hiddenCommands };
}

function originalMenuInteractionBlocked(): boolean {
  return Boolean(state.waitClick || state.wipe || state.timerMessageWait || originalMessageQueueCount() > 0);
}

function originalMenuButtonDisabled(definition: OriginalMenuButtonDefinition, surface: OriginalMenuSurface): boolean {
  return !definition.enabled(surface) || originalMenuInteractionBlocked();
}

function originalSystemShortcutAvailable(kind: "save" | "newSave" | "load"): boolean {
  if (originalConfirmDialogOpen() || originalMenuInteractionBlocked()) return false;
  const surface = originalMenuSurface();
  if (kind === "save") return surface.system.save && originalMainStateIsWalk() && state.saveDataSlot !== 0;
  if (kind === "newSave") return surface.system.newSave && originalMainStateIsWalk();
  return surface.system.load && originalMainStateAllowsStartOrWalkMenu();
}

function originalNativeMenuOpen(): boolean {
  return originalSystemMenuOpen || Boolean(originalFieldMenuOpen) || originalBattlePopupOpen();
}

function originalMenuBarContainsFocus(): boolean {
  const active = document.activeElement;
  return Boolean(active instanceof Node && originalMenuBar?.contains(active));
}

function originalMenuKeyboardCapturesInput(): boolean {
  if (originalNativeMenuOpen()) return true;
  if (originalMenuBarContainsFocus()) return true;
  if ((state.mode === "shop" || state.mode === "inn") && originalMenuBarTracking) return true;
  return Boolean(state.battle && originalBattleNativeMenuReady() && originalMenuSelectionHighlightVisible());
}

function originalMenuSelectionHighlightVisible(): boolean {
  return originalNativeMenuOpen() || originalMenuBarTracking || pendingOriginalMenuCommandFocus;
}

function clearOriginalMenuFocusFromGameInput(redraw = true): void {
  const hadMenuFocus =
    originalMenuBarTracking ||
    pendingOriginalMenuCommandFocus ||
    originalMenuBarSelectedKey !== undefined ||
    originalMenuBarContainsFocus();
  originalMenuBarTracking = false;
  pendingOriginalMenuCommandFocus = false;
  originalPointerMenuPreviewArmed = false;
  if (!originalNativeMenuOpen()) originalMenuBarSelectedKey = undefined;
  if (originalMenuBarContainsFocus()) (document.activeElement as HTMLElement | null)?.blur?.();
  if (redraw && hadMenuFocus) renderOriginalMenuBar();
}

const originalBattleCommandKeys = ["fight", "escape", "item", "magic"] as const;
type OriginalBattleCommandKey = (typeof originalBattleCommandKeys)[number];
const originalBattleItemSubmenuModes: readonly BattleItemSubmenuMode[] = ["use", "transfer", "discard", "see", "important"];
const originalBattleMagicSubmenuModes: readonly BattleMagicSubmenuMode[] = ["chant", "see"];
type OriginalBattlePopupLevel = "root" | "leaf";

const originalMenuButtons = new Map<string, HTMLButtonElement>();
const ORIGINAL_MENU_REFERENCE_DISPLAY_WIDTH = 1024;
const ORIGINAL_MENU_FONT_SIZE = 26;
const ORIGINAL_AUX_DIALOG_MIN_RESPONSIVE_SCALE = 0.72;
const ORIGINAL_AUX_DIALOG_FONT_SIZE = 20;
const ORIGINAL_AUX_DIALOG_HELP_CONTENT_FONT_SIZE = 17;
const ORIGINAL_AUX_DIALOG_HELP_PRE_FONT_SIZE = 13;
const ORIGINAL_MENU_POPUP_MIN_WIDTH = 240;
const ORIGINAL_MENU_ROW_FALLBACK_HEIGHT = 44;
let originalMenuResponsiveScale = 1;
const originalMenuButtonDefinitions: OriginalMenuButtonDefinition[] = [
  { key: "inn", labelId: "source.main.ac237396", visible: (surface) => surface.commands.inn, enabled: (surface) => surface.commands.inn, action: triggerOriginalInnMenu },
  { key: "shop", labelId: "source.main.55ef550d", visible: (surface) => surface.commands.shop, enabled: (surface) => surface.commands.shop, action: triggerOriginalShopMenu },
  { key: "check", labelId: "source.main.c46bbb7a", shortcut: "C", visible: (surface) => surface.commands.check, enabled: (surface) => surface.commands.check, action: triggerOriginalCheckMenu },
  { key: "fight", labelId: "source.main.e7e8f51b", visible: (surface) => surface.commands.fight, enabled: (surface) => surface.commands.fight, action: triggerOriginalFightMenu },
  { key: "escape", labelId: "source.main.fca161b1", visible: (surface) => surface.commands.escape, enabled: (surface) => surface.commands.escape, action: attemptEscape },
  { key: "item", labelId: "source.main.afcb767d", shortcut: "I", visible: (surface) => surface.commands.item, enabled: (surface) => surface.commands.item, action: triggerOriginalItemMenu },
  { key: "magic", labelId: "source.main.35963a22", shortcut: "M", visible: (surface) => surface.commands.magic, enabled: (surface) => surface.commands.magic, action: triggerOriginalMagicMenu },
  { key: "state", labelId: "source.main.73940a1e", shortcut: "T", visible: (surface) => surface.commands.state, enabled: (surface) => surface.commands.state, action: openStatusMenu },
  { key: "titleStart", labelId: "source.main.08ea9b7d", visible: (surface) => surface.mode === "first", enabled: (surface) => surface.system.newGame, action: triggerOriginalTitleStart },
  { key: "titleContinue", labelId: "source.main.54584c08", visible: (surface) => surface.mode === "first", enabled: (surface) => surface.system.load && latestAutoSave() !== undefined, action: continueFromLatestAutoSaveOrNewGame },
  { key: "system", labelId: "source.main.6b1221b3", shortcut: "Y", visible: () => true, enabled: () => true, action: toggleOriginalSystemPopup }
];

function createOriginalMenuPopupElement(id: string): HTMLDivElement {
  const popup = document.createElement("div");
  popup.id = id;
  popup.setAttribute("role", "menu");
  popup.hidden = true;
  popup.addEventListener("pointerdown", stopOriginalMenuPropagation);
  popup.addEventListener("mousedown", stopOriginalMenuPropagation);
  popup.addEventListener("click", stopOriginalMenuEvent);
  return popup;
}

function originalMenuScaledPixels(value: number): number {
  return value * originalMenuResponsiveScale;
}

function originalAuxDialogResponsiveScale(menuScale: number): number {
  const minScaleForMenuTextParity = (ORIGINAL_MENU_FONT_SIZE * menuScale) / ORIGINAL_AUX_DIALOG_HELP_CONTENT_FONT_SIZE;
  return Math.max(menuScale, ORIGINAL_AUX_DIALOG_MIN_RESPONSIVE_SCALE, minScaleForMenuTextParity);
}

function updateOriginalMenuResponsiveScale(redrawOpenPopup = true): void {
  if (!originalMenuBar) return;
  const displayedCanvasWidth = canvas.getBoundingClientRect().width || BASE_WIDTH;
  const nextScale = Math.min(displayedCanvasWidth / ORIGINAL_MENU_REFERENCE_DISPLAY_WIDTH, 1);
  const rootStyle = document.documentElement.style;
  if (
    Math.abs(nextScale - originalMenuResponsiveScale) < 0.001 &&
    originalMenuBar.style.getPropertyValue("--original-menu-scale") &&
    rootStyle.getPropertyValue("--original-confirm-dialog-width") &&
    rootStyle.getPropertyValue("--original-aux-dialog-width")
  ) {
    return;
  }
  originalMenuResponsiveScale = nextScale;
  const px = (value: number) => `${Math.round(originalMenuScaledPixels(value) * 100) / 100}px`;
  const auxDialogScale = originalAuxDialogResponsiveScale(nextScale);
  const auxPx = (value: number) => `${Math.round(value * auxDialogScale * 100) / 100}px`;
  rootStyle.setProperty("--original-game-ui-scale", nextScale.toFixed(3));
  rootStyle.setProperty("--original-aux-dialog-scale", auxDialogScale.toFixed(3));
  rootStyle.setProperty("--original-aux-dialog-width", auxPx(460));
  rootStyle.setProperty("--original-aux-dialog-edge-margin", auxPx(28));
  rootStyle.setProperty("--original-aux-dialog-radius", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-font-size", auxPx(ORIGINAL_AUX_DIALOG_FONT_SIZE));
  rootStyle.setProperty("--original-aux-dialog-panel-gap", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-panel-padding", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-header-gap", auxPx(12));
  rootStyle.setProperty("--original-aux-dialog-header-padding", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-header-radius", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-title-padding-x", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-icon-button-size", auxPx(42));
  rootStyle.setProperty("--original-aux-dialog-icon-size", auxPx(22));
  rootStyle.setProperty("--original-aux-dialog-settings-grid-gap", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-settings-grid-offset", auxPx(68));
  rootStyle.setProperty("--original-aux-dialog-row-gap", auxPx(10));
  rootStyle.setProperty("--original-aux-dialog-row-padding-y", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-row-padding-x", auxPx(10));
  rootStyle.setProperty("--original-aux-dialog-row-radius", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-row-label-width", auxPx(86));
  rootStyle.setProperty("--original-aux-dialog-row-label-wide-width", auxPx(112));
  rootStyle.setProperty("--original-aux-dialog-row-control-width", auxPx(120));
  rootStyle.setProperty("--original-aux-dialog-row-output-width", auxPx(38));
  rootStyle.setProperty("--original-aux-dialog-select-padding-y", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-select-padding-x", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-select-radius", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-checkbox-size", auxPx(22));
  rootStyle.setProperty("--original-aux-dialog-help-panel-max-height", auxPx(620));
  rootStyle.setProperty("--original-aux-dialog-help-content-gap", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-help-content-offset", auxPx(86));
  rootStyle.setProperty("--original-aux-dialog-help-content-padding-right", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-help-content-font-size", auxPx(ORIGINAL_AUX_DIALOG_HELP_CONTENT_FONT_SIZE));
  rootStyle.setProperty("--original-aux-dialog-help-section-gap", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-help-heading-padding-y", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-help-heading-padding-x", auxPx(8));
  rootStyle.setProperty("--original-aux-dialog-help-heading-radius", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-help-list-margin-bottom", auxPx(4));
  rootStyle.setProperty("--original-aux-dialog-help-pre-max-height", auxPx(220));
  rootStyle.setProperty("--original-aux-dialog-help-pre-padding", auxPx(10));
  rootStyle.setProperty("--original-aux-dialog-help-pre-radius", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-help-pre-font-size", auxPx(ORIGINAL_AUX_DIALOG_HELP_PRE_FONT_SIZE));
  rootStyle.setProperty("--original-aux-dialog-scrollbar-width", auxPx(12));
  rootStyle.setProperty("--original-aux-dialog-scrollbar-radius", auxPx(6));
  rootStyle.setProperty("--original-aux-dialog-scrollbar-thumb-border", auxPx(2));
  rootStyle.setProperty("--original-menu-font-size", px(ORIGINAL_MENU_FONT_SIZE));
  rootStyle.setProperty("--original-confirm-dialog-width", px(460));
  rootStyle.setProperty("--original-confirm-dialog-edge-margin", px(28));
  rootStyle.setProperty("--original-confirm-dialog-radius", px(8));
  rootStyle.setProperty("--original-confirm-dialog-font-size", px(20));
  rootStyle.setProperty("--original-confirm-panel-padding", px(6));
  rootStyle.setProperty("--original-confirm-header-gap", px(12));
  rootStyle.setProperty("--original-confirm-header-padding", px(4));
  rootStyle.setProperty("--original-confirm-header-radius", px(6));
  rootStyle.setProperty("--original-confirm-title-padding-x", px(8));
  rootStyle.setProperty("--original-confirm-message-padding-x", px(12));
  rootStyle.setProperty("--original-confirm-message-padding-top", px(10));
  rootStyle.setProperty("--original-confirm-message-padding-bottom", px(2));
  rootStyle.setProperty("--original-confirm-message-radius", px(6));
  rootStyle.setProperty("--original-confirm-warning-padding-bottom", px(10));
  rootStyle.setProperty("--original-confirm-actions-gap", px(8));
  rootStyle.setProperty("--original-confirm-actions-margin-top", px(8));
  rootStyle.setProperty("--original-confirm-button-min-height", px(44));
  rootStyle.setProperty("--original-confirm-button-padding-y", px(6));
  rootStyle.setProperty("--original-confirm-button-padding-x", px(16));
  rootStyle.setProperty("--original-confirm-button-radius", px(6));
  rootStyle.setProperty("--original-confirm-button-outline", px(2));
  rootStyle.setProperty("--original-confirm-button-outline-offset", px(-4));
  originalMenuBar.style.setProperty("--original-menu-scale", nextScale.toFixed(3));
  originalMenuBar.style.setProperty("--original-menu-font-size", px(ORIGINAL_MENU_FONT_SIZE));
  originalMenuBar.style.setProperty("--original-menu-gap", px(2));
  originalMenuBar.style.setProperty("--original-menu-padding", px(3));
  originalMenuBar.style.setProperty("--original-menu-bar-radius", px(8));
  originalMenuBar.style.setProperty("--original-menu-button-min-width", px(88));
  originalMenuBar.style.setProperty("--original-menu-button-min-height", px(44));
  originalMenuBar.style.setProperty("--original-menu-button-padding-y", px(5));
  originalMenuBar.style.setProperty("--original-menu-button-padding-x", px(14));
  originalMenuBar.style.setProperty("--original-menu-button-radius", px(6));
  originalMenuBar.style.setProperty("--original-menu-popup-min-width", px(ORIGINAL_MENU_POPUP_MIN_WIDTH));
  originalMenuBar.style.setProperty("--original-menu-popup-padding", px(4));
  originalMenuBar.style.setProperty("--original-menu-popup-radius", px(8));
  originalMenuBar.style.setProperty("--original-menu-row-min-height", px(ORIGINAL_MENU_ROW_FALLBACK_HEIGHT));
  originalMenuBar.style.setProperty("--original-menu-row-padding-y", px(6));
  originalMenuBar.style.setProperty("--original-menu-row-padding-x", px(16));
  if (redrawOpenPopup && originalNativeMenuOpen()) renderOriginalMenuBar();
}

function wireOriginalMenuResponsiveScale(): void {
  updateOriginalMenuResponsiveScale(false);
  const resizeObserver = (window as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  if (resizeObserver) {
    const observer = new resizeObserver(() => updateOriginalMenuResponsiveScale());
    observer.observe(canvas);
    import.meta.hot?.dispose(() => observer.disconnect());
    return;
  }
  const update = () => updateOriginalMenuResponsiveScale();
  window.addEventListener("resize", update);
  import.meta.hot?.dispose(() => window.removeEventListener("resize", update));
}

function clampOriginalPopupSelection(rows: OriginalPopupRow[], selectedIndex: number): number {
  if (!rows.length) return 0;
  const clamped = clamp(selectedIndex, 0, rows.length - 1);
  if (!rows[clamped]?.disabled) return clamped;
  const nextEnabled = rows.findIndex((row) => !row.disabled);
  return nextEnabled >= 0 ? nextEnabled : clamped;
}

function moveOriginalPopupSelection(rows: OriginalPopupRow[], selectedIndex: number, direction: number): number {
  if (!rows.length) return 0;
  for (let offset = 1; offset <= rows.length; offset += 1) {
    const next = (selectedIndex + rows.length + direction * offset) % rows.length;
    if (!rows[next]?.disabled) return next;
  }
  return clamp(selectedIndex, 0, rows.length - 1);
}

function originalMenuButtonHasSubmenu(key: string): boolean {
  return key === "system" || key === "item" || key === "magic" || key === "shop";
}

function originalConfirmButtons(): HTMLButtonElement[] {
  return [originalConfirmYesButton, originalConfirmNoButton].filter((button): button is HTMLButtonElement => Boolean(button));
}

function syncOriginalConfirmSelectionDom(): void {
  if (originalConfirmYesButton) {
    originalConfirmYesButton.dataset.selected = originalConfirmSelection === "yes" ? "true" : "false";
    originalConfirmYesButton.setAttribute("aria-selected", originalConfirmSelection === "yes" ? "true" : "false");
  }
  if (originalConfirmNoButton) {
    originalConfirmNoButton.dataset.selected = originalConfirmSelection === "no" ? "true" : "false";
    originalConfirmNoButton.setAttribute("aria-selected", originalConfirmSelection === "no" ? "true" : "false");
  }
}

function setOriginalConfirmSelection(selection: OriginalConfirmSelection): void {
  originalConfirmSelection = selection;
  syncOriginalConfirmSelectionDom();
  const button = selection === "yes" ? originalConfirmYesButton : originalConfirmNoButton;
  button?.focus({ preventScroll: true });
}

function moveOriginalConfirmButtonFocus(direction: number): void {
  const buttons = originalConfirmButtons();
  if (!buttons.length) return;
  const selectedButton = originalConfirmSelection === "yes" ? originalConfirmYesButton : originalConfirmNoButton;
  const currentIndex = Math.max(0, buttons.findIndex((button) => button === selectedButton || button === document.activeElement));
  const nextIndex = (currentIndex + buttons.length + direction) % buttons.length;
  setOriginalConfirmSelection(buttons[nextIndex] === originalConfirmNoButton ? "no" : "yes");
}

function handleOriginalConfirmKey(rawKey: string): boolean {
  if (!originalConfirmDialogOpen()) return false;
  const key = normalizeInputKey(rawKey);
  if (key === "Escape") {
    closeOriginalConfirmDialog(false);
    return true;
  }
  if (key === "Enter") {
    closeOriginalConfirmDialog(originalConfirmSelection === "yes");
    return true;
  }
  if (key === "ArrowLeft" || key === "ArrowUp") {
    moveOriginalConfirmButtonFocus(-1);
    return true;
  }
  if (key === "ArrowRight" || key === "ArrowDown") {
    moveOriginalConfirmButtonFocus(1);
    return true;
  }
  return false;
}

function handleOriginalConfirmTouchButton(rawButtonKey: string): boolean {
  if (!originalConfirmDialogOpen()) return false;
  return handleOriginalConfirmKey(rawButtonKey);
}

function originalConfirmTouchButtonKeyAt(clientX: number, clientY: number): string | undefined {
  const touchControls = document.querySelector<HTMLDivElement>("#touch-controls");
  if (!touchControls || getComputedStyle(touchControls).display === "none") return undefined;
  for (const button of touchControls.querySelectorAll<HTMLButtonElement>("button[data-key]")) {
    const rect = button.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
      return button.dataset.key ?? "Enter";
    }
  }
  return undefined;
}

function handleOriginalConfirmTouchPointer(event: PointerEvent): boolean {
  if (!originalConfirmDialogOpen()) return false;
  const rawButtonKey = originalConfirmTouchButtonKeyAt(event.clientX, event.clientY);
  if (!rawButtonKey) return false;
  event.preventDefault();
  event.stopPropagation();
  handleOriginalConfirmTouchButton(rawButtonKey);
  return true;
}

function captureOriginalConfirmMenuSnapshot(): OriginalConfirmMenuSnapshot {
  const battle = state.battle;
  return {
    originalSystemMenuOpen,
    originalSystemSubmenuOpen,
    originalSystemMenuSelected,
    originalSystemSubmenuSelected,
    originalFieldMenuOpen,
    originalFieldSubmenuOpen,
    originalFieldRootSelected,
    originalFieldLeafSelected,
    originalMenuBarTracking,
    originalMenuBarSelectedKey,
    originalPointerMenuPreviewArmed,
    pendingOriginalMenuCommandFocus,
    battle: battle
      ? {
          selected: battle.selected,
          battleRootSelected: battle.battleRootSelected,
          battleLeafSelected: battle.battleLeafSelected,
          battlePopupFocus: battle.battlePopupFocus,
          phase: battle.phase,
          battleItemMenuMode: battle.battleItemMenuMode,
          battleMagicMenuMode: battle.battleMagicMenuMode
        }
      : undefined
  };
}

function restoreOriginalConfirmMenuSnapshot(snapshot: OriginalConfirmMenuSnapshot, focusTarget: HTMLElement | undefined): void {
  originalSystemMenuOpen = snapshot.originalSystemMenuOpen;
  originalSystemSubmenuOpen = snapshot.originalSystemSubmenuOpen;
  originalSystemMenuSelected = snapshot.originalSystemMenuSelected;
  originalSystemSubmenuSelected = snapshot.originalSystemSubmenuSelected;
  originalFieldMenuOpen = snapshot.originalFieldMenuOpen;
  originalFieldSubmenuOpen = snapshot.originalFieldSubmenuOpen;
  originalFieldRootSelected = snapshot.originalFieldRootSelected;
  originalFieldLeafSelected = snapshot.originalFieldLeafSelected;
  originalMenuBarTracking = snapshot.originalMenuBarTracking;
  originalMenuBarSelectedKey = snapshot.originalMenuBarSelectedKey;
  originalPointerMenuPreviewArmed = snapshot.originalPointerMenuPreviewArmed;
  pendingOriginalMenuCommandFocus = snapshot.pendingOriginalMenuCommandFocus;
  if (snapshot.battle && state.battle) {
    state.battle.selected = snapshot.battle.selected;
    state.battle.battleRootSelected = snapshot.battle.battleRootSelected;
    state.battle.battleLeafSelected = snapshot.battle.battleLeafSelected;
    state.battle.battlePopupFocus = snapshot.battle.battlePopupFocus;
    state.battle.phase = snapshot.battle.phase;
    state.battle.battleItemMenuMode = snapshot.battle.battleItemMenuMode;
    state.battle.battleMagicMenuMode = snapshot.battle.battleMagicMenuMode;
  }
  renderOriginalMenuBar();
  draw();
  queueMicrotask(() => focusOriginalConfirmRestoredMenu(focusTarget));
}

function focusOriginalConfirmRestoredMenu(focusTarget: HTMLElement | undefined): void {
  if (originalSystemMenuOpen) {
    if (originalSystemSubmenuOpen) focusOriginalSystemSubmenuSelection();
    else focusOriginalSystemRootSelection();
    return;
  }
  if (originalFieldMenuOpen) {
    if (originalFieldSubmenuOpen) focusOriginalFieldLeafSelection();
    else focusOriginalFieldRootSelection();
    return;
  }
  if (originalBattlePopupOpen()) {
    focusOriginalMenuPopupSelection();
    return;
  }
  if (originalMenuBarTracking && originalMenuBarSelectedKey) {
    originalMenuButtons.get(originalMenuBarSelectedKey)?.focus({ preventScroll: true });
    return;
  }
  if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
}

function ensureOriginalConfirmDialog(): HTMLDialogElement {
  if (originalConfirmDialog) return originalConfirmDialog;
  const dialog = document.createElement("dialog");
  dialog.className = "original-confirm-dialog";
  dialog.setAttribute("aria-labelledby", "original-confirm-title");
  dialog.setAttribute("aria-describedby", "original-confirm-message original-confirm-warning");

  const touchProxy = document.createElement("div");
  touchProxy.className = "original-confirm-touch-proxy";
  touchProxy.setAttribute("aria-hidden", "true");
  touchProxy.addEventListener("pointerdown", (event) => {
    if (handleOriginalConfirmTouchPointer(event)) return;
    stopOriginalMenuPropagation(event);
  });

  const panel = document.createElement("div");
  panel.className = "original-confirm-panel";

  const header = document.createElement("header");
  header.className = "dialog-header original-confirm-header";
  const title = document.createElement("h1");
  title.id = "original-confirm-title";
  title.textContent = gameText("ui.confirm.title");
  header.append(title);

  const message = document.createElement("p");
  message.id = "original-confirm-message";
  message.className = "original-confirm-message";
  originalConfirmMessageElement = message;

  const warning = document.createElement("p");
  warning.id = "original-confirm-warning";
  warning.className = "original-confirm-warning";
  warning.hidden = true;
  originalConfirmWarningElement = warning;

  const actions = document.createElement("div");
  actions.className = "original-confirm-actions";

  const yesButton = document.createElement("button");
  yesButton.type = "button";
  yesButton.className = "original-confirm-button";
  yesButton.dataset.action = "yes";
  yesButton.textContent = gameText("ui.confirm.yes");
  yesButton.addEventListener("focus", () => {
    originalConfirmSelection = "yes";
    syncOriginalConfirmSelectionDom();
  });
  yesButton.addEventListener("pointerdown", (event) => activateOriginalConfirmButton(event, true));
  yesButton.addEventListener("click", (event) => activateOriginalConfirmButton(event, true));
  originalConfirmYesButton = yesButton;

  const noButton = document.createElement("button");
  noButton.type = "button";
  noButton.className = "original-confirm-button";
  noButton.dataset.action = "no";
  noButton.textContent = gameText("ui.confirm.no");
  noButton.addEventListener("focus", () => {
    originalConfirmSelection = "no";
    syncOriginalConfirmSelectionDom();
  });
  noButton.addEventListener("pointerdown", (event) => activateOriginalConfirmButton(event, false));
  noButton.addEventListener("click", (event) => activateOriginalConfirmButton(event, false));
  originalConfirmNoButton = noButton;

  actions.append(yesButton, noButton);
  panel.append(header, message, warning, actions);
  dialog.append(touchProxy, panel);
  dialog.addEventListener("pointerdown", (event) => {
    if (handleOriginalConfirmTouchPointer(event)) return;
    stopOriginalMenuPropagation(event);
  });
  dialog.addEventListener("mousedown", stopOriginalMenuPropagation);
  dialog.addEventListener("click", stopOriginalMenuEvent);
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeOriginalConfirmDialog(false);
  });
  dialog.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if (handleOriginalConfirmKey(event.key)) {
      event.preventDefault();
    }
  });
  document.body.append(dialog);
  originalConfirmDialog = dialog;
  return dialog;
}

function activateOriginalConfirmButton(event: Event, confirmed: boolean): void {
  event.preventDefault();
  event.stopPropagation();
  closeOriginalConfirmDialog(confirmed);
}

function originalConfirmDialogOpen(): boolean {
  return Boolean(originalConfirmDialog?.open);
}

function showOriginalConfirmDialog(message: string, onYes: () => void, warning?: string): void {
  if (originalConfirmDialogOpen()) return;
  clearHeldMovementKeys();
  const active = document.activeElement;
  originalConfirmPreviousFocus = active instanceof HTMLElement ? active : undefined;
  originalConfirmMenuSnapshot = captureOriginalConfirmMenuSnapshot();
  originalConfirmAction = onYes;
  originalConfirmSelection = "yes";
  const dialog = ensureOriginalConfirmDialog();
  const hasWarning = Boolean(warning);
  dialog.dataset.warning = hasWarning ? "true" : "false";
  if (originalConfirmMessageElement) originalConfirmMessageElement.textContent = message;
  if (originalConfirmWarningElement) {
    originalConfirmWarningElement.textContent = warning ?? "";
    originalConfirmWarningElement.hidden = !hasWarning;
  }
  if (typeof dialog.showModal === "function") {
    dialog.showModal();
  } else {
    dialog.setAttribute("open", "");
  }
  setOriginalConfirmSelection("yes");
  queueMicrotask(() => setOriginalConfirmSelection("yes"));
  window.setTimeout(() => setOriginalConfirmSelection("yes"), 0);
}

function closeOriginalConfirmDialog(confirmed: boolean): void {
  const dialog = originalConfirmDialog;
  const action = originalConfirmAction;
  const menuSnapshot = originalConfirmMenuSnapshot;
  originalConfirmAction = undefined;
  originalConfirmMenuSnapshot = undefined;
  if (dialog?.open) dialog.close();
  const focusTarget = originalConfirmPreviousFocus;
  originalConfirmPreviousFocus = undefined;
  if (confirmed) {
    action?.();
    return;
  }
  if (menuSnapshot) {
    restoreOriginalConfirmMenuSnapshot(menuSnapshot, focusTarget);
    return;
  }
  if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
}

function originalSystemMenuRows(surface: OriginalMenuSurface = originalMenuSurface()): OriginalPopupRow[] {
  return [
    { label: gameText("source.main.08ea9b7d"), disabled: !surface.system.newGame, action: triggerOriginalNewGameMenu },
    { label: gameText("source.main.744a4383"), detail: gameText("source.main.93fd5ced"), disabled: !surface.system.newSave, action: openOriginalSystemSubmenuNewSave, submenuMode: "newSave" },
    { label: originalCurrentSaveCaption(), detail: gameText("source.main.80c6dc6a"), disabled: !surface.system.save, action: saveCurrentDataSlot },
    { label: gameText("source.main.48ff805f"), detail: gameText("source.main.84c6e2b6"), disabled: !surface.system.load, action: openOriginalSystemSubmenuLoad, submenuMode: "load" },
    { label: gameText("source.main.65d9051e"), disabled: !surface.system.title, action: confirmBackToTitle }
  ];
}

function originalCurrentSaveCaption(): string {
  return state.saveDataSlot > 0 ? gameText("source.main.59dcb0e4", { originalDataSlotLabel_state_saveData: originalDataSlotLabel(state.saveDataSlot) }) : gameText("source.main.e66653cc");
}

function toggleOriginalSystemPopup(): void {
  originalSystemMenuOpen = !originalSystemMenuOpen;
  originalMenuBarTracking = originalSystemMenuOpen;
  originalMenuBarSelectedKey = "system";
  originalPointerMenuPreviewArmed = originalSystemMenuOpen;
  originalSystemSubmenuOpen = undefined;
  originalSystemMenuSelected = 0;
  originalSystemSubmenuSelected = 0;
  if (originalSystemMenuOpen) clearHeldMovementKeys();
  if (originalSystemMenuOpen) cancelOriginalFieldPopup(false);
  if (originalSystemMenuOpen) cancelOriginalBattlePopup(false);
}

function openOriginalSystemPopup(): void {
  clearHeldMovementKeys();
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = "system";
  originalPointerMenuPreviewArmed = true;
  originalSystemMenuOpen = true;
  originalSystemSubmenuOpen = undefined;
  originalSystemSubmenuSelected = 0;
  cancelOriginalFieldPopup(false);
  cancelOriginalBattlePopup(false);
}

function cancelOriginalSystemPopup(redraw = true): boolean {
  if (!originalSystemMenuOpen) return false;
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  originalSystemSubmenuSelected = 0;
  if (redraw) originalMenuBarTracking = false;
  if (redraw) {
    renderOriginalMenuBar();
    draw();
  }
  return true;
}

function openOriginalSystemSubmenuNewSave(): void {
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = "system";
  originalSystemMenuOpen = true;
  originalSystemSubmenuOpen = "newSave";
  originalSystemMenuSelected = originalSystemRootIndexForSubmenu("newSave");
  originalSystemSubmenuSelected = 0;
  renderOriginalMenuBar();
}

function openOriginalSystemSubmenuLoad(): void {
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = "system";
  originalSystemMenuOpen = true;
  originalSystemSubmenuOpen = "load";
  originalSystemMenuSelected = originalSystemRootIndexForSubmenu("load");
  originalSystemSubmenuSelected = 0;
  renderOriginalMenuBar();
}

function originalSystemRootIndexForSubmenu(mode: OriginalSystemSubmenuMode): number {
  const index = originalSystemMenuRows().findIndex((row) => row.submenuMode === mode);
  return index >= 0 ? index : 0;
}

function selectOriginalBattleCommandKey(key: string): void {
  const battle = state.battle;
  if (!battle || !originalBattleNativeMenuReady() || !isOriginalBattleCommandKey(key)) return;
  if (battle.phase !== "command") return;
  const next = originalBattleCommandIndex(key);
  if (battle.selected === next) return;
  battle.selected = next;
}

function closeOriginalTopMenuPopups(): void {
  cancelOriginalSystemPopup(false);
  cancelOriginalFieldPopup(false);
  cancelOriginalBattlePopup(false);
  pendingOriginalMenuCommandFocus = false;
}

function closeOriginalNativePopupToTopMenu(redraw = true): boolean {
  const anchorKey = originalOpenTopMenuAnchorKey() ?? originalMenuBarSelectedKey;
  const closedSystem = cancelOriginalSystemPopup(false);
  const closedField = cancelOriginalFieldPopup(false);
  const closedBattle = cancelOriginalBattlePopup(false);
  if (!closedSystem && !closedField && !closedBattle) return false;
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = anchorKey;
  originalPointerMenuPreviewArmed = false;
  pendingOriginalMenuCommandFocus = false;
  if (anchorKey && isOriginalBattleCommandKey(anchorKey)) selectOriginalBattleCommandKey(anchorKey);
  if (redraw) {
    renderOriginalMenuBar();
    draw();
  }
  if (anchorKey) queueMicrotask(() => originalMenuButtons.get(anchorKey)?.focus({ preventScroll: true }));
  return true;
}

function preferredOriginalTitleMenuKey(): string {
  return latestAutoSave() !== undefined ? "titleContinue" : "titleStart";
}

function focusOriginalTitleMenuSelection(redraw = true): void {
  if (state.mode !== "title" || state.originalMenuMode !== "first") return;
  const key = preferredOriginalTitleMenuKey();
  closeOriginalTopMenuPopups();
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = key;
  originalPointerMenuPreviewArmed = false;
  pendingOriginalMenuCommandFocus = false;
  if (redraw) renderOriginalMenuBar();
  queueMicrotask(() => originalMenuButtons.get(key)?.focus({ preventScroll: true }));
}

function selectOriginalTopMenuButton(key: string, keepPointerPreview = false): void {
  clearHeldMovementKeys();
  closeOriginalTopMenuPopups();
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = key;
  originalPointerMenuPreviewArmed = keepPointerPreview;
  selectOriginalBattleCommandKey(key);
  renderOriginalMenuBar();
}

function previewOriginalMenuButton(key: string): void {
  if (originalPointerMenuPreviewArmed && originalMenuButtonHasSubmenu(key)) {
    if (originalOpenTopMenuAnchorKey() === key) return;
    activateOriginalTopMenuButton(key);
    renderOriginalMenuBar();
    draw();
    return;
  }
  selectOriginalTopMenuButton(key, originalPointerMenuPreviewArmed);
}

function activateOriginalTopMenuButton(key: string): void {
  clearHeldMovementKeys();
  closeOriginalTopMenuPopups();
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = key;
  selectOriginalBattleCommandKey(key);
  const definition = originalMenuButtonDefinitions.find((candidate) => candidate.key === key);
  const surface = originalMenuSurface();
  if (!definition || !definition.visible(surface) || originalMenuButtonDisabled(definition, surface)) {
    originalPointerMenuPreviewArmed = originalNativeMenuOpen();
    renderOriginalMenuBar();
    return;
  }
  definition.action();
  originalPointerMenuPreviewArmed = originalNativeMenuOpen();
  if (!originalNativeMenuOpen()) clearOriginalMenuFocusFromGameInput(false);
}

function ensureOriginalMenuBar(): void {
  if (!originalMenuBar || originalMenuButtons.size > 0) return;
  originalMenuButtonDefinitions.forEach((definition) => {
    const button = document.createElement("button");
    button.type = "button";
    renderOriginalMenuButtonLabel(button, definition);
    button.dataset.menuKey = definition.key;
    if (originalMenuButtonHasSubmenu(definition.key)) {
      button.setAttribute("aria-haspopup", "menu");
      button.setAttribute("aria-expanded", "false");
    }
    button.addEventListener("pointerdown", stopOriginalMenuPropagation);
    button.addEventListener("mousedown", stopOriginalMenuPropagation);
    button.addEventListener("pointerenter", () => previewOriginalMenuButton(definition.key));
    button.addEventListener("focus", () => {
      clearHeldMovementKeys();
      originalMenuBarSelectedKey = definition.key;
      selectOriginalBattleCommandKey(definition.key);
      renderOriginalMenuBar();
    });
    button.addEventListener("click", (event) => {
      stopOriginalMenuEvent(event);
      if (button.disabled) return;
      resumeAudio();
      activateOriginalTopMenuButton(definition.key);
      renderOriginalMenuBar();
      draw();
    });
    originalMenuButtons.set(definition.key, button);
    originalMenuBar.append(button);
  });
  originalMenuPopup = createOriginalMenuPopupElement("original-menu-popup");
  originalMenuSubPopup = createOriginalMenuPopupElement("original-menu-subpopup");
  originalMenuBar.addEventListener("pointerleave", () => {
    originalPointerMenuPreviewArmed = false;
  });
  originalMenuBar.append(originalMenuPopup);
  originalMenuBar.append(originalMenuSubPopup);
}

function renderOriginalMenuButtonLabel(button: HTMLButtonElement, definition: OriginalMenuButtonDefinition): void {
  let label = Array.from(button.children).find(
    (child): child is HTMLSpanElement => child instanceof HTMLSpanElement && child.classList.contains("original-menu-button-label")
  );
  if (!label) {
    label = document.createElement("span");
    label.className = "original-menu-button-label";
    button.prepend(label);
  }
  const nextLabel = gameText(definition.labelId);
  if (label.textContent !== nextLabel) label.textContent = nextLabel;

  let shortcut = Array.from(button.children).find(
    (child): child is HTMLSpanElement => child instanceof HTMLSpanElement && child.classList.contains("original-menu-button-shortcut")
  );
  if (!definition.shortcut) {
    shortcut?.remove();
    return;
  }
  if (!shortcut) {
    shortcut = document.createElement("span");
    shortcut.className = "original-menu-button-shortcut";
    button.append(shortcut);
  }
  if (shortcut.textContent !== definition.shortcut) shortcut.textContent = definition.shortcut;
}

function renderOriginalMenuBar(): void {
  if (!originalMenuBar) return;
  ensureOriginalMenuBar();
  const surface = originalMenuSurface();
  if (originalMenuInteractionBlocked()) {
    if (originalSystemMenuOpen) cancelOriginalSystemPopup(false);
    if (originalFieldMenuOpen) cancelOriginalFieldPopup(false);
  }
  originalMenuBar.dataset.mode = surface.mode;
  const topMenuFocusVisible = originalMenuSelectionHighlightVisible();
  const battleCommandFocus = topMenuFocusVisible && Boolean(state.battle && surface.mode === "fight" && state.battle.phase === "command");
  originalMenuButtonDefinitions.forEach((definition) => {
    const button = originalMenuButtons.get(definition.key);
    if (!button) return;
    const active = topMenuFocusVisible && originalMenuButtonActive(definition.key);
    renderOriginalMenuButtonLabel(button, definition);
    button.hidden = !definition.visible(surface);
    button.disabled = originalMenuButtonDisabled(definition, surface);
    button.dataset.active = active ? "true" : "false";
    button.dataset.selected = battleCommandFocus && active ? "true" : "false";
    button.tabIndex = battleCommandFocus && isOriginalBattleCommandKey(definition.key) ? (active ? 0 : -1) : 0;
    if (battleCommandFocus && active) button.setAttribute("aria-current", "true");
    else button.removeAttribute("aria-current");
    button.dataset.expanded = originalMenuButtonExpanded(definition.key) ? "true" : "false";
    if (originalMenuButtonHasSubmenu(definition.key)) {
      button.setAttribute("aria-expanded", originalMenuButtonExpanded(definition.key) ? "true" : "false");
    }
  });
  renderOriginalMenuPopup();
  if (pendingOriginalMenuCommandFocus) {
    pendingOriginalMenuCommandFocus = false;
    queueMicrotask(focusOriginalMenuCommandSelection);
  }
}

function renderOriginalMenuPopup(): void {
  if (!originalMenuPopup || !originalMenuSubPopup) return;
  if (originalSystemMenuOpen) {
    renderOriginalSystemMenuPopup();
    return;
  }
  if (originalFieldMenuOpen) {
    renderOriginalFieldMenuPopup();
    return;
  }
  const battle = state.battle;
  if (!battle || (battle.phase !== "item" && battle.phase !== "magic")) {
    originalMenuPopup.hidden = true;
    originalMenuPopup.replaceChildren();
    originalMenuSubPopup.hidden = true;
    originalMenuSubPopup.replaceChildren();
    return;
  }
  const rootRows = battleRootPopupRows(battle);
  const rootSelectedIndex = battleRootSelectedIndex(battle, rootRows.length);
  battle.battleRootSelected = rootSelectedIndex;
  originalMenuPopup.replaceChildren();
  rootRows.forEach((row, index) => {
    originalMenuPopup?.append(createOriginalPopupButton(battle, row, index, "root", rootSelectedIndex));
  });
  originalMenuPopup.hidden = false;
  positionOriginalMenuPopup(battle);
  renderOriginalMenuLeafPopup(battle, true);
}

function renderOriginalSystemMenuPopup(): void {
  if (!originalMenuPopup || !originalMenuSubPopup) return;
  const rows = originalSystemMenuRows();
  originalSystemMenuSelected = clampOriginalPopupSelection(rows, originalSystemMenuSelected);
  originalMenuPopup.replaceChildren();
  rows.forEach((row, index) => {
    originalMenuPopup?.append(createOriginalSystemPopupButton(row, index));
  });
  originalMenuPopup.hidden = false;
  positionOriginalMenuPopupAtButton("system", ORIGINAL_MENU_POPUP_MIN_WIDTH);
  if (originalSystemSubmenuOpen) {
    renderOriginalSystemSubPopup();
  } else {
    originalMenuSubPopup.hidden = true;
    originalMenuSubPopup.replaceChildren();
    queueMicrotask(focusOriginalSystemRootSelection);
  }
}

function renderOriginalSystemSubPopup(): void {
  if (!originalMenuPopup || !originalMenuSubPopup) return;
  const rows = originalSystemSubPopupRows();
  originalSystemSubmenuSelected = clampOriginalPopupSelection(rows, originalSystemSubmenuSelected);
  originalMenuSubPopup.replaceChildren();
  rows.forEach((row, index) => {
    originalMenuSubPopup?.append(createOriginalSystemSettingButton(row, index));
  });
  originalMenuSubPopup.hidden = false;
  positionOriginalMenuSubPopupAtRootRow(originalSystemMenuSelected);
  queueMicrotask(focusOriginalSystemSubmenuSelection);
}

function focusOriginalSystemRootSelection(): void {
  if (!originalMenuPopup || originalMenuPopup.hidden) return;
  const button =
    originalMenuPopup.querySelector<HTMLButtonElement>('button[data-selected="true"]:not(:disabled)') ??
    originalMenuPopup.querySelector<HTMLButtonElement>("button:not(:disabled)");
  button?.focus({ preventScroll: true });
}

function originalSystemSubPopupRows(): OriginalPopupRow[] {
  if (originalSystemSubmenuOpen === "newSave") {
    return [1, 2, 3, 4, 5].map((slot) => ({
      label: originalDataSlotLabel(slot),
      ...originalDataSlotLevelRowDetail(slot, true),
      action: () => confirmNewSaveSlot(slot)
    }));
  }
  if (originalSystemSubmenuOpen === "load") {
    return [1, 2, 3, 4, 5].map((slot) => ({
      label: originalDataSlotLabel(slot),
      ...originalDataSlotLevelRowDetail(slot, false),
      disabled: !originalDataSlotHasData(slot),
      action: () => confirmLoadSlot(slot)
    }));
  }
  return [];
}

function createOriginalSystemPopupButton(row: OriginalPopupRow, index: number): HTMLButtonElement {
  const selected = !row.disabled && index === originalSystemMenuSelected;
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.popupRow = String(index);
  button.dataset.popupLevel = "system";
  button.dataset.selected = selected ? "true" : "false";
  button.setAttribute("role", "menuitem");
  button.setAttribute("aria-selected", selected ? "true" : "false");
  if (row.submenuMode === "newSave" || row.submenuMode === "load") {
    button.setAttribute("aria-haspopup", "menu");
    button.setAttribute("aria-expanded", originalSystemSubmenuOpen === row.submenuMode ? "true" : "false");
  }
  renderOriginalPopupRowLabel(button, row);
  button.disabled = Boolean(row.disabled);
  button.tabIndex = selected ? 0 : -1;
  let pointerActivated = false;
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) {
      stopOriginalMenuPropagation(event);
      return;
    }
    pointerActivated = true;
    stopOriginalMenuEvent(event);
    activateOriginalSystemPopupRow(row, index);
  });
  button.addEventListener("mousedown", stopOriginalMenuPropagation);
  button.addEventListener("pointerenter", () => focusOriginalSystemPopupRow(row, index, { previewSubmenu: true }));
  button.addEventListener("focus", () => focusOriginalSystemPopupRow(row, index, { previewSubmenu: false }));
  button.addEventListener("click", (event) => {
    stopOriginalMenuEvent(event);
    if (pointerActivated) {
      pointerActivated = false;
      return;
    }
    activateOriginalSystemPopupRow(row, index);
  });
  return button;
}

function createOriginalSystemSettingButton(row: OriginalPopupRow, index: number): HTMLButtonElement {
  const selected = !row.disabled && index === originalSystemSubmenuSelected;
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.popupRow = String(index);
  button.dataset.popupLevel = "system-settings";
  button.dataset.selected = selected ? "true" : "false";
  button.dataset.checked = row.checked ? "true" : "false";
  button.setAttribute("role", row.checked === undefined ? "menuitem" : "menuitemcheckbox");
  button.setAttribute("aria-selected", selected ? "true" : "false");
  if (row.checked !== undefined) button.setAttribute("aria-checked", row.checked ? "true" : "false");
  renderOriginalPopupRowLabel(button, row);
  button.disabled = Boolean(row.disabled);
  button.tabIndex = selected ? 0 : -1;
  let pointerActivated = false;
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) {
      stopOriginalMenuPropagation(event);
      return;
    }
    pointerActivated = true;
    stopOriginalMenuEvent(event);
    focusOriginalSystemSubmenuRow(row, index);
    if (!row.disabled) row.action();
  });
  button.addEventListener("mousedown", stopOriginalMenuPropagation);
  button.addEventListener("pointerenter", () => focusOriginalSystemSubmenuRow(row, index));
  button.addEventListener("focus", () => focusOriginalSystemSubmenuRow(row, index));
  button.addEventListener("click", (event) => {
    stopOriginalMenuEvent(event);
    if (pointerActivated) {
      pointerActivated = false;
      return;
    }
    if (!row.disabled) row.action();
  });
  return button;
}

function renderOriginalPopupRowLabel(button: HTMLButtonElement, row: OriginalPopupRow): void {
  button.replaceChildren();
  const label = document.createElement("span");
  label.className = "original-menu-row-label";
  if (row.checked !== undefined) label.classList.add("original-menu-row-label--checked");
  label.dataset.warning = row.labelWarning ? "true" : "false";
  if (row.checked === undefined) {
    label.textContent = row.label;
  } else {
    label.append(createOriginalMenuCheckSlot(row.checked), document.createTextNode(row.label));
  }
  button.append(label);
  if (row.detail === undefined) return;
  const detail = document.createElement("span");
  detail.className = "original-menu-row-detail";
  detail.dataset.warning = row.detailWarning ? "true" : "false";
  detail.textContent = row.detail;
  button.append(detail);
}

function createOriginalMenuCheckSlot(checked: boolean): HTMLSpanElement {
  const slot = document.createElement("span");
  slot.className = "original-menu-check-slot";
  slot.setAttribute("aria-hidden", "true");
  if (!checked) return slot;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M3.1 8.2 6.4 11.4 12.9 4.4");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.2");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  slot.append(svg);
  return slot;
}

function focusOriginalSystemSubmenuSelection(): void {
  if (!originalMenuSubPopup || originalMenuSubPopup.hidden) return;
  const button =
    originalMenuSubPopup.querySelector<HTMLButtonElement>('button[data-selected="true"]:not(:disabled)') ??
    originalMenuSubPopup.querySelector<HTMLButtonElement>("button:not(:disabled)");
  button?.focus({ preventScroll: true });
}

function focusOriginalSystemSubmenuRow(row: OriginalPopupRow, index: number): void {
  if (row.disabled) return;
  originalSystemSubmenuSelected = index;
  syncOriginalSystemSubmenuDomSelection(index);
}

function syncOriginalSystemSubmenuDomSelection(selectedIndex: number): void {
  originalMenuSubPopup?.querySelectorAll<HTMLButtonElement>('button[data-popup-level="system-settings"]').forEach((button) => {
    const selected = !button.disabled && Number(button.dataset.popupRow) === selectedIndex;
    button.dataset.selected = selected ? "true" : "false";
    button.setAttribute("aria-selected", selected ? "true" : "false");
    button.tabIndex = selected ? 0 : -1;
  });
}

function syncOriginalSystemRootDomSelection(selectedIndex: number): void {
  originalMenuPopup?.querySelectorAll<HTMLButtonElement>('button[data-popup-level="system"]').forEach((button) => {
    const selected = !button.disabled && Number(button.dataset.popupRow) === selectedIndex;
    button.dataset.selected = selected ? "true" : "false";
    button.setAttribute("aria-selected", selected ? "true" : "false");
    button.tabIndex = selected ? 0 : -1;
  });
}

function focusOriginalSystemPopupRow(row: OriginalPopupRow, index: number, options: { previewSubmenu?: boolean } = {}): void {
  if (row.disabled) return;
  originalSystemMenuSelected = index;
  originalSystemSubmenuSelected = 0;
  syncOriginalSystemRootDomSelection(index);
  if (!options.previewSubmenu) return;
  const nextSubmenu =
    row.submenuMode === "newSave" || row.submenuMode === "load"
      ? row.submenuMode
      : undefined;
  if (originalSystemSubmenuOpen === nextSubmenu) return;
  originalSystemSubmenuOpen = nextSubmenu;
  renderOriginalSystemMenuPopup();
}

function activateOriginalSystemPopupRow(row: OriginalPopupRow, index: number): void {
  if (row.disabled) return;
  originalSystemMenuSelected = index;
  if (row.submenuMode === "newSave" || row.submenuMode === "load") {
    originalSystemSubmenuOpen = row.submenuMode;
    originalSystemSubmenuSelected = 0;
    renderOriginalMenuBar();
    return;
  }
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  originalMenuBarTracking = false;
  row.action();
  renderOriginalMenuBar();
  draw();
}

function renderOriginalFieldMenuPopup(): void {
  if (!originalMenuPopup || !originalMenuSubPopup || !originalFieldMenuOpen) return;
  const rootRows = originalFieldRootPopupRows();
  originalFieldRootSelected = clampOriginalPopupSelection(rootRows, originalFieldRootSelected);
  originalMenuPopup.replaceChildren();
  rootRows.forEach((row, index) => {
    originalMenuPopup?.append(createOriginalFieldPopupButton(row, index, "root", originalFieldRootSelected));
  });
  originalMenuPopup.hidden = false;
  positionOriginalMenuPopupAtButton(originalFieldMenuOpen, ORIGINAL_MENU_POPUP_MIN_WIDTH);
  renderOriginalFieldLeafPopup();
}

function renderOriginalFieldLeafPopup(): void {
  if (!originalMenuSubPopup) return;
  const rows = originalFieldLeafPopupRows();
  if (rows === undefined) {
    originalMenuSubPopup.hidden = true;
    originalMenuSubPopup.replaceChildren();
    queueMicrotask(focusOriginalFieldRootSelection);
    return;
  }
  originalFieldLeafSelected = clampOriginalPopupSelection(rows, originalFieldLeafSelected);
  originalMenuSubPopup.replaceChildren();
  rows.forEach((row, index) => {
    originalMenuSubPopup?.append(createOriginalFieldPopupButton(row, index, "leaf", originalFieldLeafSelected));
  });
  originalMenuSubPopup.hidden = false;
  positionOriginalMenuSubPopupAtRootRow(originalFieldRootSelected);
  queueMicrotask(focusOriginalFieldLeafSelection);
}

function focusOriginalFieldRootSelection(): void {
  if (!originalMenuPopup || originalMenuPopup.hidden) return;
  const button =
    originalMenuPopup.querySelector<HTMLButtonElement>('button[data-popup-level="root"][data-selected="true"]:not(:disabled)') ??
    originalMenuPopup.querySelector<HTMLButtonElement>('button[data-popup-level="root"]:not(:disabled)');
  button?.focus({ preventScroll: true });
}

function focusOriginalFieldLeafSelection(): void {
  if (!originalMenuSubPopup || originalMenuSubPopup.hidden) return;
  const button =
    originalMenuSubPopup.querySelector<HTMLButtonElement>('button[data-popup-level="leaf"][data-selected="true"]:not(:disabled)') ??
    originalMenuSubPopup.querySelector<HTMLButtonElement>('button[data-popup-level="leaf"]:not(:disabled)');
  button?.focus({ preventScroll: true });
}

function createOriginalFieldPopupButton(
  row: OriginalPopupRow,
  index: number,
  level: OriginalBattlePopupLevel,
  selectedIndex: number
): HTMLButtonElement {
  const selected = !row.disabled && index === selectedIndex;
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.popupRow = String(index);
  button.dataset.popupLevel = level;
  button.dataset.selected = selected ? "true" : "false";
  button.setAttribute("role", "menuitem");
  button.setAttribute("aria-selected", selected ? "true" : "false");
  if (level === "root" && row.submenuMode) {
    button.setAttribute("aria-haspopup", "menu");
    button.setAttribute("aria-expanded", originalFieldSubmenuOpen === row.submenuMode ? "true" : "false");
  }
  button.disabled = Boolean(row.disabled);
  button.tabIndex = selected ? 0 : -1;
  renderOriginalPopupRowLabel(button, row);
  let pointerActivated = false;
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) {
      stopOriginalMenuPropagation(event);
      return;
    }
    pointerActivated = true;
    stopOriginalMenuEvent(event);
    focusOriginalFieldPopupRow(row, index, level, { previewSubmenu: false });
    activateOriginalFieldPopupRow(row, index, level);
  });
  button.addEventListener("mousedown", stopOriginalMenuPropagation);
  button.addEventListener("pointerenter", () => focusOriginalFieldPopupRow(row, index, level, { previewSubmenu: true }));
  button.addEventListener("focus", () => focusOriginalFieldPopupRow(row, index, level, { previewSubmenu: false }));
  button.addEventListener("click", (event) => {
    stopOriginalMenuEvent(event);
    if (pointerActivated) {
      pointerActivated = false;
      return;
    }
    activateOriginalFieldPopupRow(row, index, level);
  });
  return button;
}

function originalFieldRootPopupRows(): OriginalPopupRow[] {
  if (originalFieldMenuOpen === "item") {
    return [
      { label: gameText("source.main.ac671418"), action: () => setOriginalFieldSubmenu("use"), submenuMode: "use" },
      { label: gameText("source.main.49c929ef"), action: () => setOriginalFieldSubmenu("transfer"), submenuMode: "transfer" },
      { label: gameText("source.main.87b01978"), action: () => setOriginalFieldSubmenu("discard"), submenuMode: "discard" },
      { label: gameText("source.main.89811853"), action: () => setOriginalFieldSubmenu("see"), submenuMode: "see" },
      { label: gameText("source.main.6d334963"), action: () => setOriginalFieldSubmenu("important"), submenuMode: "important" }
    ];
  }
  if (originalFieldMenuOpen === "magic") {
    return [
      { label: gameText("source.main.8a100415"), action: () => setOriginalFieldSubmenu("chant"), submenuMode: "chant" },
      { label: gameText("source.main.89811853"), action: () => setOriginalFieldSubmenu("see"), submenuMode: "see" }
    ];
  }
  if (originalFieldMenuOpen === "shop") {
    return [
      { label: gameText("source.main.1d6b7d90"), action: () => setOriginalFieldSubmenu("shopBuy"), submenuMode: "shopBuy" },
      { label: gameText("source.main.89811853"), action: () => setOriginalFieldSubmenu("shopSee"), submenuMode: "shopSee" }
    ];
  }
  return [];
}

function originalFieldLeafPopupRows(): OriginalPopupRow[] | undefined {
  if (!originalFieldMenuOpen || !originalFieldSubmenuOpen) return undefined;
  if (originalFieldMenuOpen === "item") return originalFieldItemLeafRows(originalFieldSubmenuOpen);
  if (originalFieldMenuOpen === "magic") return originalFieldMagicLeafRows(originalFieldSubmenuOpen);
  if (originalFieldMenuOpen === "shop") return originalFieldShopLeafRows(originalFieldSubmenuOpen);
  return undefined;
}

function originalFieldItemLeafRows(mode: OriginalFieldSubmenuMode): OriginalPopupRow[] | undefined {
  if (mode !== "use" && mode !== "transfer" && mode !== "discard" && mode !== "see" && mode !== "important") return undefined;
  const entries = mode === "important" ? sharedInventoryEntries() : personalInventoryEntriesForActor(originalSourceActorIndex());
  if (!entries.length) return [{ label: gameText("source.main.9033cf96"), action: () => undefined }];
  return entries.map((entry) => {
    const label = items.get(entry.id)?.name ?? gameText("source.main.970dbaba", { entry_id: entry.id });
    if (mode === "transfer") return { label, action: () => transferMapItemFromNativeMenu(entry, label) };
    if (mode === "discard") return { label, labelWarning: true, action: () => confirmDiscardMapItem(entry, label) };
    if (mode === "see") return { label, action: () => seeMapItemFromNativeMenu(entry) };
    return { label, action: () => useMapItemFromNativeMenu(entry) };
  });
}

function originalFieldMagicLeafRows(mode: OriginalFieldSubmenuMode): OriginalPopupRow[] | undefined {
  if (mode !== "chant" && mode !== "see") return undefined;
  const ids = availableMagic(originalSourceActorIndex());
  if (!ids.length) return [{ label: gameText("source.main.67c0fa80"), action: () => undefined }];
  return ids.map((id) => ({
    label: magic.get(id)?.name ?? gameText("source.main.8620acbf", { id: id }),
    action: () => (mode === "see" ? seeMapMagicFromNativeMenu(id) : useMapMagicFromNativeMenu(id))
  }));
}

function originalFieldShopLeafRows(mode: OriginalFieldSubmenuMode): OriginalPopupRow[] | undefined {
  if (mode !== "shopBuy" && mode !== "shopSee") return undefined;
  return shopEntries().map(({ id, item }) => ({
    label: shopItemLabel(id, item),
    action: () => (mode === "shopSee" ? seeShopItemFromNativeMenu(id) : buyShopItemFromNativeMenu(id))
  }));
}

function setOriginalFieldSubmenu(mode: OriginalFieldSubmenuMode): void {
  originalFieldSubmenuOpen = mode;
  originalFieldLeafSelected = 0;
  renderOriginalMenuBar();
  draw();
}

function focusOriginalFieldPopupRow(
  row: OriginalPopupRow,
  index: number,
  level: OriginalBattlePopupLevel,
  options: { previewSubmenu?: boolean } = {}
): void {
  if (row.disabled) return;
  if (level === "root") {
    originalFieldRootSelected = index;
    if (options.previewSubmenu && row.submenuMode) {
      if (originalFieldSubmenuOpen === row.submenuMode) {
        syncOriginalFieldPopupDomSelection("root", index);
        return;
      }
      originalFieldSubmenuOpen = row.submenuMode as OriginalFieldSubmenuMode;
      originalFieldLeafSelected = 0;
      renderOriginalFieldMenuPopup();
      return;
    }
    syncOriginalFieldPopupDomSelection("root", index);
    return;
  }
  originalFieldLeafSelected = index;
  syncOriginalFieldPopupDomSelection("leaf", index);
}

function syncOriginalFieldPopupDomSelection(level: OriginalBattlePopupLevel, selectedIndex: number): void {
  const popup = level === "root" ? originalMenuPopup : originalMenuSubPopup;
  popup?.querySelectorAll<HTMLButtonElement>(`button[data-popup-level="${level}"]`).forEach((button) => {
    const selected = !button.disabled && Number(button.dataset.popupRow) === selectedIndex;
    button.dataset.selected = selected ? "true" : "false";
    button.setAttribute("aria-selected", selected ? "true" : "false");
    button.tabIndex = selected ? 0 : -1;
  });
}

function activateOriginalFieldPopupRow(row: OriginalPopupRow, index: number, level: OriginalBattlePopupLevel): void {
  if (row.disabled) return;
  if (level === "root" && row.submenuMode) {
    originalFieldRootSelected = index;
    originalFieldSubmenuOpen = row.submenuMode as OriginalFieldSubmenuMode;
    originalFieldLeafSelected = 0;
    row.action();
    return;
  }
  originalFieldLeafSelected = index;
  row.action();
}

function openOriginalFieldPopup(mode: OriginalFieldRootMenuMode): void {
  clearHeldMovementKeys();
  originalFieldMenuOpen = mode;
  originalMenuBarSelectedKey = mode;
  originalFieldSubmenuOpen = undefined;
  originalFieldRootSelected = 0;
  originalFieldLeafSelected = 0;
  originalMenuBarTracking = true;
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  cancelOriginalBattlePopup(false);
  renderOriginalMenuBar();
  draw();
}

function focusOriginalMenuUsingCommand(key: "inn" | "shop"): void {
  clearHeldMovementKeys();
  cancelOriginalSystemPopup(false);
  cancelOriginalFieldPopup(false);
  cancelOriginalBattlePopup(false);
  originalMenuBarTracking = true;
  originalMenuBarSelectedKey = key;
  originalPointerMenuPreviewArmed = false;
  renderOriginalMenuBar();
  draw();
  queueMicrotask(() => originalMenuButtons.get(key)?.focus({ preventScroll: true }));
}

function cancelOriginalFieldPopup(redraw = true): boolean {
  if (!originalFieldMenuOpen) return false;
  originalFieldMenuOpen = undefined;
  originalFieldSubmenuOpen = undefined;
  originalFieldRootSelected = 0;
  originalFieldLeafSelected = 0;
  if (redraw) originalMenuBarTracking = false;
  if (redraw) {
    renderOriginalMenuBar();
    draw();
  }
  return true;
}

function leaveOriginalMenuUsingToWalk(redraw = true): boolean {
  if (state.mode !== "shop" && state.mode !== "inn") return false;
  state.mode = "map";
  state.previousMode = "map";
  state.innPrice = undefined;
  state.shopItems = undefined;
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  originalFieldMenuOpen = undefined;
  originalFieldSubmenuOpen = undefined;
  originalFieldRootSelected = 0;
  originalFieldLeafSelected = 0;
  originalMenuBarTracking = false;
  originalMenuBarSelectedKey = undefined;
  originalPointerMenuPreviewArmed = false;
  setOriginalMenuMode("walk");
  (document.activeElement as HTMLElement | null)?.blur?.();
  if (redraw) {
    renderOriginalMenuBar();
    draw();
  }
  return true;
}

function renderOriginalMenuLeafPopup(battle: BattleState, focusSelection: boolean): void {
  if (!originalMenuSubPopup) return;
  const leafRows = battleLeafPopupRows(battle);
  if (leafRows === undefined) {
    originalMenuSubPopup.hidden = true;
    originalMenuSubPopup.replaceChildren();
    battle.battlePopupFocus = "root";
    if (focusSelection) queueMicrotask(focusOriginalMenuPopupSelection);
    return;
  }
  const selectedIndex = leafRows.length ? clamp(battle.battleLeafSelected, 0, leafRows.length - 1) : 0;
  battle.battleLeafSelected = selectedIndex;
  originalMenuSubPopup.replaceChildren();
  const visibleRows = leafRows.length
    ? leafRows
    : [{ label: battle.phase === "item" ? gameText("source.main.9033cf96") : gameText("source.main.67c0fa80"), action: () => undefined }];
  visibleRows.forEach((row, index) => {
    originalMenuSubPopup?.append(createOriginalPopupButton(battle, row, index, "leaf", selectedIndex));
  });
  originalMenuSubPopup.hidden = false;
  positionOriginalMenuSubPopup(battle);
  if (focusSelection) queueMicrotask(focusOriginalMenuPopupSelection);
}

function createOriginalPopupButton(
  battle: BattleState,
  row: OriginalPopupRow,
  index: number,
  level: OriginalBattlePopupLevel,
  selectedIndex: number
): HTMLButtonElement {
  const selected = !row.disabled && index === selectedIndex;
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.popupRow = String(index);
  button.dataset.popupLevel = level;
  button.dataset.selected = selected ? "true" : "false";
  button.setAttribute("role", "menuitem");
  button.setAttribute("aria-selected", selected ? "true" : "false");
  if (level === "root" && row.submenuMode) {
    button.setAttribute("aria-haspopup", "menu");
    const mode = row.submenuMode;
    const submenuOpen =
      (isOriginalBattleItemSubmenuMode(mode) || isOriginalBattleMagicSubmenuMode(mode)) &&
      battleMenuModeIsOpen(battle, mode);
    button.setAttribute("aria-expanded", submenuOpen ? "true" : "false");
  }
  button.disabled = Boolean(row.disabled);
  button.tabIndex = selected ? 0 : -1;
  renderOriginalPopupRowLabel(button, row);
  let pointerActivated = false;
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) {
      stopOriginalMenuPropagation(event);
      return;
    }
    pointerActivated = true;
    stopOriginalMenuEvent(event);
    focusOriginalPopupRow(battle, row, index, level, { previewSubmenu: false });
    activateOriginalPopupRow(battle, row, index, level);
  });
  button.addEventListener("mousedown", stopOriginalMenuPropagation);
  button.addEventListener("pointerenter", () => focusOriginalPopupRow(battle, row, index, level, { previewSubmenu: true }));
  button.addEventListener("focus", () => focusOriginalPopupRow(battle, row, index, level, { previewSubmenu: false }));
  button.addEventListener("click", (event) => {
    stopOriginalMenuEvent(event);
    if (pointerActivated) {
      pointerActivated = false;
      return;
    }
    activateOriginalPopupRow(battle, row, index, level);
  });
  return button;
}

function focusOriginalMenuPopupSelection(): void {
  const battle = state.battle;
  const preferredPopup =
    battle?.battlePopupFocus === "root"
      ? originalMenuPopup
      : battle?.battlePopupFocus === "leaf"
        ? originalMenuSubPopup
        : undefined;
  const popup =
    preferredPopup && !preferredPopup.hidden
      ? preferredPopup
      : originalMenuSubPopup && !originalMenuSubPopup.hidden
        ? originalMenuSubPopup
        : originalMenuPopup && !originalMenuPopup.hidden
          ? originalMenuPopup
          : undefined;
  const button =
    popup?.querySelector<HTMLButtonElement>('button[data-selected="true"]:not(:disabled)') ??
    popup?.querySelector<HTMLButtonElement>("button:not(:disabled)");
  button?.focus({ preventScroll: true });
}

function queueOriginalMenuCommandFocus(): void {
  pendingOriginalMenuCommandFocus = true;
}

function focusOriginalMenuCommandSelection(): void {
  const battle = state.battle;
  if (!battle || state.originalMenuMode !== "fight" || battle.phase !== "command") return;
  originalMenuBarTracking = true;
  originalMenuButtons.get(originalBattleSelectedCommandKey(battle))?.focus({ preventScroll: true });
}

function isOriginalBattleCommandKey(key: string): key is OriginalBattleCommandKey {
  return (originalBattleCommandKeys as readonly string[]).includes(key);
}

function originalBattleCommandIndex(key: OriginalBattleCommandKey): number {
  return originalBattleCommandKeys.indexOf(key);
}

function originalBattleCommandKeyAt(index: number): OriginalBattleCommandKey {
  return originalBattleCommandKeys[((index % originalBattleCommandKeys.length) + originalBattleCommandKeys.length) % originalBattleCommandKeys.length];
}

function originalBattleSelectedCommandKey(battle: BattleState): OriginalBattleCommandKey {
  if (battle.phase === "item") return "item";
  if (battle.phase === "magic") return "magic";
  return originalBattleCommandKeyAt(battle.selected);
}

function originalBattlePopupAnchorKey(battle: BattleState): OriginalBattleCommandKey | undefined {
  if (battle.phase === "item") return "item";
  if (battle.phase === "magic") return "magic";
  return undefined;
}

function originalBattleCommandActiveKey(): OriginalBattleCommandKey | undefined {
  const battle = state.battle;
  if (!battle || state.originalMenuMode !== "fight") return undefined;
  return originalBattleSelectedCommandKey(battle);
}

function originalOpenTopMenuAnchorKey(): string | undefined {
  if (originalSystemMenuOpen) return "system";
  if (originalFieldMenuOpen) return originalFieldMenuOpen;
  const battle = state.battle;
  return battle ? originalBattlePopupAnchorKey(battle) : undefined;
}

function originalSelectedTopMenuKey(): string | undefined {
  const surface = originalMenuSurface();
  if (originalMenuBarSelectedKey) {
    const definition = originalMenuButtonDefinitions.find((candidate) => candidate.key === originalMenuBarSelectedKey);
    if (definition?.visible(surface) && !originalMenuButtonDisabled(definition, surface)) return originalMenuBarSelectedKey;
  }
  return originalOpenTopMenuAnchorKey() ?? originalBattleCommandActiveKey();
}

function originalMenuButtonActive(key: string): boolean {
  return key === originalSelectedTopMenuKey();
}

function originalMenuButtonExpanded(key: string): boolean {
  return key === originalOpenTopMenuAnchorKey();
}

function originalVisibleEnabledMenuKeys(): string[] {
  const surface = originalMenuSurface();
  return originalMenuButtonDefinitions
    .filter((definition) => definition.visible(surface) && !originalMenuButtonDisabled(definition, surface))
    .map((definition) => definition.key);
}

function currentOriginalMenuBarKey(): string | undefined {
  const selectedKey = originalSelectedTopMenuKey();
  if (selectedKey) return selectedKey;
  const openAnchor = originalOpenTopMenuAnchorKey();
  if (openAnchor) return openAnchor;
  const active = document.activeElement;
  if (active instanceof HTMLElement && active.dataset.menuKey) return active.dataset.menuKey;
  const battle = state.battle;
  if (battle && state.originalMenuMode === "fight") return originalBattleSelectedCommandKey(battle);
  return undefined;
}

function moveOriginalTopMenuSelection(direction: number): boolean {
  const keys = originalVisibleEnabledMenuKeys();
  if (!keys.length) return true;
  const current = currentOriginalMenuBarKey();
  const currentIndex = current ? keys.indexOf(current) : -1;
  const nextKey = keys[(currentIndex + keys.length + direction) % keys.length] ?? keys[0];
  selectOriginalTopMenuButton(nextKey);
  renderOriginalMenuBar();
  draw();
  queueMicrotask(() => originalMenuButtons.get(nextKey)?.focus({ preventScroll: true }));
  return true;
}

function focusFirstOriginalTopMenuSelection(): void {
  const firstKey = originalVisibleEnabledMenuKeys()[0];
  if (!firstKey) return;
  originalMenuBarSelectedKey = firstKey;
  queueMicrotask(() => originalMenuButtons.get(firstKey)?.focus({ preventScroll: true }));
}

function handleOriginalMenuBarKey(key: string): boolean {
  if (key === "Escape") {
    if (leaveOriginalMenuUsingToWalk()) return true;
    cancelOriginalSystemPopup(false);
    cancelOriginalFieldPopup(false);
    originalMenuBarTracking = false;
    (document.activeElement as HTMLElement | null)?.blur?.();
    renderOriginalMenuBar();
    draw();
    return true;
  }
  if (key === "ArrowLeft") return moveOriginalTopMenuSelection(-1);
  if (key === "ArrowRight") return moveOriginalTopMenuSelection(1);
  if (key === "ArrowDown" || key === "ArrowUp") return true;
  if (key !== "Enter") return false;
  const activeKey = currentOriginalMenuBarKey() ?? originalVisibleEnabledMenuKeys()[0];
  const definition = originalMenuButtonDefinitions.find((candidate) => candidate.key === activeKey);
  const surface = originalMenuSurface();
  if (!definition || !definition.visible(surface) || originalMenuButtonDisabled(definition, surface)) return true;
  originalMenuBarTracking = true;
  resumeAudio();
  activateOriginalTopMenuButton(activeKey);
  renderOriginalMenuBar();
  draw();
  return true;
}

function positionOriginalMenuPopupAtButton(anchorKey: string | undefined, minWidth: number): void {
  if (!originalMenuPopup) return;
  const anchor = anchorKey ? originalMenuButtons.get(anchorKey) : undefined;
  originalMenuPopup.dataset.anchor = anchorKey ?? "";
  if (!anchor || anchor.hidden) {
    originalMenuPopup.style.minWidth = "";
    const popupHeight = originalMenuPopup.offsetHeight || originalMenuPopup.scrollHeight;
    originalMenuPopup.style.left = "0px";
    originalMenuPopup.style.top = `${-popupHeight}px`;
    return;
  }
  originalMenuPopup.style.minWidth = `${Math.max(originalMenuScaledPixels(minWidth), anchor.offsetWidth)}px`;
  const popupHeight = originalMenuPopup.offsetHeight || originalMenuPopup.scrollHeight;
  const firstPopupButton = originalMenuPopup.querySelector<HTMLButtonElement>("button");
  const firstPopupButtonLeft = firstPopupButton?.offsetLeft ?? 0;
  originalMenuPopup.style.left = `${anchor.offsetLeft - firstPopupButtonLeft}px`;
  originalMenuPopup.style.top = `${anchor.offsetTop - popupHeight}px`;
  alignOriginalMenuPopupRightEdgeToGameDisplay(originalMenuPopup);
}

function positionOriginalMenuPopup(battle: BattleState): void {
  positionOriginalMenuPopupAtButton(originalBattlePopupAnchorKey(battle), ORIGINAL_MENU_POPUP_MIN_WIDTH);
}

function positionOriginalMenuSubPopup(battle: BattleState): void {
  positionOriginalMenuSubPopupAtRootRow(battleRootSelectedIndex(battle, battleRootPopupRows(battle).length));
}

function positionOriginalMenuSubPopupAtRootRow(rootSelectedIndex: number): void {
  if (!originalMenuPopup || !originalMenuSubPopup) return;
  const rootTop = Number.parseFloat(originalMenuPopup.style.top) || 0;
  const rootLeft = Number.parseFloat(originalMenuPopup.style.left) || 0;
  const rootButton = originalMenuPopup.querySelector<HTMLButtonElement>(
    `button[data-popup-row="${rootSelectedIndex}"]`
  );
  const fallbackRowHeight = originalMenuScaledPixels(ORIGINAL_MENU_ROW_FALLBACK_HEIGHT);
  const rootButtonTop = rootButton?.offsetTop ?? rootSelectedIndex * fallbackRowHeight;
  const rootButtonHeight = rootButton?.offsetHeight ?? fallbackRowHeight;
  originalMenuSubPopup.dataset.anchor = String(rootSelectedIndex);
  originalMenuSubPopup.style.minWidth = `${originalMenuScaledPixels(ORIGINAL_MENU_POPUP_MIN_WIDTH)}px`;
  originalMenuSubPopup.style.left = `${rootLeft + originalMenuPopup.offsetWidth - 1}px`;
  originalMenuSubPopup.dataset.placement = "right";
  if (originalMenuPopupRightEdgeOverflowsGameDisplay(originalMenuSubPopup)) {
    const subPopupWidth = originalMenuSubPopup.offsetWidth || originalMenuSubPopup.scrollWidth;
    originalMenuSubPopup.style.left = `${rootLeft - subPopupWidth + 1}px`;
    originalMenuSubPopup.dataset.placement = "left";
  }
  const subPopupHeight = originalMenuSubPopup.offsetHeight || originalMenuSubPopup.scrollHeight;
  const lastSubPopupButton = originalMenuSubPopup.querySelector<HTMLButtonElement>("button:last-of-type");
  const lastSubPopupButtonBottom =
    lastSubPopupButton === null ? subPopupHeight : lastSubPopupButton.offsetTop + lastSubPopupButton.offsetHeight;
  originalMenuSubPopup.style.top = `${rootTop + rootButtonTop + rootButtonHeight - lastSubPopupButtonBottom}px`;
}

function originalMenuGameDisplayRightEdge(): number {
  const canvasRect = canvas.getBoundingClientRect();
  return canvasRect.width > 0 ? canvasRect.right : document.documentElement.clientWidth || window.innerWidth;
}

function originalMenuPopupRightEdgeOverflowsGameDisplay(popup: HTMLElement): boolean {
  return popup.getBoundingClientRect().right > originalMenuGameDisplayRightEdge();
}

function alignOriginalMenuPopupRightEdgeToGameDisplay(popup: HTMLElement): void {
  const overflow = popup.getBoundingClientRect().right - originalMenuGameDisplayRightEdge();
  if (overflow <= 0) return;
  const left = Number.parseFloat(popup.style.left) || 0;
  popup.style.left = `${left - overflow}px`;
}

function syncOriginalPopupDomSelection(level: OriginalBattlePopupLevel, selectedIndex: number): void {
  const popup = level === "root" ? originalMenuPopup : originalMenuSubPopup;
  popup?.querySelectorAll<HTMLButtonElement>(`button[data-popup-level="${level}"]`).forEach((button) => {
    const selected = !button.disabled && Number(button.dataset.popupRow) === selectedIndex;
    button.dataset.selected = selected ? "true" : "false";
    button.setAttribute("aria-selected", selected ? "true" : "false");
    button.tabIndex = selected ? 0 : -1;
  });
}

function isOriginalBattleItemSubmenuMode(mode: OriginalPopupSubmenuMode): mode is BattleItemSubmenuMode {
  return (originalBattleItemSubmenuModes as readonly string[]).includes(mode);
}

function isOriginalBattleMagicSubmenuMode(mode: OriginalPopupSubmenuMode): mode is BattleMagicSubmenuMode {
  return (originalBattleMagicSubmenuModes as readonly string[]).includes(mode);
}

function previewOriginalRootSubmenu(battle: BattleState, row: OriginalPopupRow, index: number): void {
  const mode = row.submenuMode;
  if (!mode) return;
  if (battle.phase === "item" && isOriginalBattleItemSubmenuMode(mode)) {
    battle.battleItemMenuMode = mode;
  } else if (battle.phase === "magic" && isOriginalBattleMagicSubmenuMode(mode)) {
    battle.battleMagicMenuMode = mode;
  } else {
    return;
  }
  battle.battleRootSelected = index;
  battle.battleLeafSelected = 0;
  syncOriginalPopupDomSelection("root", index);
  renderOriginalMenuLeafPopup(battle, false);
}

function focusOriginalPopupRow(
  battle: BattleState,
  row: OriginalPopupRow,
  index: number,
  level: OriginalBattlePopupLevel,
  options: { previewSubmenu?: boolean } = {}
): void {
  if (row.disabled) return;
  if (level === "root") {
    battle.battleRootSelected = index;
    battle.battlePopupFocus = "root";
    syncOriginalPopupDomSelection("root", index);
    if (options.previewSubmenu) previewOriginalRootSubmenu(battle, row, index);
    return;
  }
  if (battle.battleLeafSelected === index && battle.battlePopupFocus === "leaf") return;
  battle.battleLeafSelected = index;
  battle.battlePopupFocus = "leaf";
  syncOriginalPopupDomSelection("leaf", index);
}

function stopOriginalMenuEvent(event: Event): void {
  event.preventDefault();
  event.stopPropagation();
}

function stopOriginalMenuPropagation(event: Event): void {
  event.stopPropagation();
}

function activateOriginalPopupRow(battle: BattleState, row: OriginalPopupRow, index: number, level: OriginalBattlePopupLevel): void {
  if (row.disabled) return;
  if (level === "root" && row.submenuMode) {
    battle.battleRootSelected = index;
    battle.battlePopupFocus = "leaf";
    row.action();
    return;
  }
  battle.battleLeafSelected = index;
  battle.battlePopupFocus = "leaf";
  row.action();
  renderOriginalMenuBar();
  draw();
}

function originalBattlePopupOpen(battle: BattleState | undefined = state.battle): boolean {
  return Boolean(battle && (battle.phase === "item" || battle.phase === "magic"));
}

function cancelOriginalBattlePopup(redraw = true): boolean {
  const battle = state.battle;
  if (!battle || !originalBattlePopupOpen(battle)) return false;
  const commandKey = originalBattleSelectedCommandKey(battle);
  battle.phase = "command";
  battle.battleItemMenuMode = undefined;
  battle.battleMagicMenuMode = undefined;
  battle.battleRootSelected = 0;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = "root";
  battle.selected = originalBattleCommandIndex(commandKey);
  if (redraw) {
    originalMenuBarTracking = false;
    queueOriginalMenuCommandFocus();
  }
  if (redraw) {
    renderOriginalMenuBar();
    draw();
  }
  return true;
}

function battleRootPopupRows(battle: BattleState): OriginalPopupRow[] {
  return battle.phase === "item" ? battleItemRootPopupRows() : battleMagicRootPopupRows();
}

function battleLeafPopupRows(battle: BattleState): OriginalPopupRow[] | undefined {
  if (battle.phase === "item") {
    return battle.battleItemMenuMode && battle.battleItemMenuMode !== "root" ? battleItemLeafPopupRows(battle) : undefined;
  }
  if (battle.phase === "magic") {
    return battle.battleMagicMenuMode && battle.battleMagicMenuMode !== "root" ? battleMagicLeafPopupRows(battle) : undefined;
  }
  return undefined;
}

function battleRootSelectedIndex(battle: BattleState, rowCount: number): number {
  if (rowCount <= 0) return 0;
  if (battle.phase === "item") {
    const mode = battle.battleItemMenuMode ?? "root";
    if (mode === "root") return clamp(battle.battleRootSelected, 0, rowCount - 1);
    const index = originalBattleItemSubmenuModes.indexOf(mode);
    return index >= 0 ? index : 0;
  }
  if (battle.phase === "magic") {
    const mode = battle.battleMagicMenuMode ?? "root";
    if (mode === "root") return clamp(battle.battleRootSelected, 0, rowCount - 1);
    const index = originalBattleMagicSubmenuModes.indexOf(mode);
    return index >= 0 ? index : 0;
  }
  return 0;
}

function battleMenuModeIsOpen(battle: BattleState, mode: BattleItemSubmenuMode | BattleMagicSubmenuMode): boolean {
  return (
    (battle.phase === "item" && battle.battleItemMenuMode === mode) ||
    (battle.phase === "magic" && battle.battleMagicMenuMode === mode)
  );
}

function returnOriginalBattlePopupToRoot(battle: BattleState): boolean {
  const rootIndex = battleRootSelectedIndex(battle, battleRootPopupRows(battle).length);
  if (battle.phase === "item" && battle.battleItemMenuMode && battle.battleItemMenuMode !== "root") {
    battle.battleItemMenuMode = "root";
    battle.battleRootSelected = rootIndex;
    battle.battleLeafSelected = 0;
    battle.battlePopupFocus = "root";
    renderOriginalMenuBar();
    draw();
    return true;
  }
  if (battle.phase === "magic" && battle.battleMagicMenuMode && battle.battleMagicMenuMode !== "root") {
    battle.battleMagicMenuMode = "root";
    battle.battleRootSelected = rootIndex;
    battle.battleLeafSelected = 0;
    battle.battlePopupFocus = "root";
    renderOriginalMenuBar();
    draw();
    return true;
  }
  return false;
}

function openSelectedOriginalRootSubmenu(battle: BattleState): boolean {
  const rows = battleRootPopupRows(battle);
  if (!rows.length) return false;
  const index = battleRootSelectedIndex(battle, rows.length);
  const row = rows[index];
  if (!row || row.disabled) return false;
  activateOriginalPopupRow(battle, row, index, "root");
  return true;
}

function battleItemRootPopupRows(): OriginalPopupRow[] {
  return [
    { label: gameText("source.main.ac671418"), action: () => setBattleItemMenuMode("use"), submenuMode: "use" },
    { label: gameText("source.main.49c929ef"), action: () => setBattleItemMenuMode("transfer"), submenuMode: "transfer" },
    { label: gameText("source.main.87b01978"), action: () => setBattleItemMenuMode("discard"), submenuMode: "discard" },
    { label: gameText("source.main.89811853"), action: () => setBattleItemMenuMode("see"), submenuMode: "see" },
    { label: gameText("source.main.6d334963"), action: () => setBattleItemMenuMode("important"), submenuMode: "important" }
  ];
}

function battleMagicRootPopupRows(): OriginalPopupRow[] {
  return [
    { label: gameText("source.main.8a100415"), action: () => setBattleMagicMenuMode("chant"), submenuMode: "chant" },
    { label: gameText("source.main.89811853"), action: () => setBattleMagicMenuMode("see"), submenuMode: "see" }
  ];
}

function battleItemLeafPopupRows(battle: BattleState): OriginalPopupRow[] {
  const mode = battle.battleItemMenuMode ?? "root";
  if (mode === "root") return [];
  const entries = mode === "important" ? sharedInventoryEntries() : personalInventoryEntriesForActor(battle.commandActorIndex);
  return entries.map((entry, index) => {
    const label = items.get(entry.id)?.name ?? gameText("source.main.970dbaba", { entry_id: entry.id });
    if (mode === "transfer") return { label, action: () => transferBattleInventoryEntry(index) };
    if (mode === "discard") return { label, labelWarning: true, action: () => confirmDiscardBattleItem(index, label) };
    if (mode === "see") return { label, action: () => seeBattleItemEntry(index) };
    return { label, action: () => useBattleItem(index) };
  });
}

function battleMagicLeafPopupRows(battle: BattleState): OriginalPopupRow[] {
  const mode = battle.battleMagicMenuMode ?? "root";
  if (mode === "root") return [];
  return availableMagic(battle.commandActorIndex).map((id, index) => ({
    label: magic.get(id)?.name ?? gameText("source.main.8620acbf", { id: id }),
    action: () => (mode === "see" ? seeBattleMagic(index) : useBattleMagic(index))
  }));
}

function openOriginalBattleItemRootMenu(battle: BattleState): void {
  state.mode = "battle";
  state.previousMode = "battle";
  originalMenuBarSelectedKey = "item";
  battle.phase = "item";
  battle.battleItemMenuMode = "root";
  battle.battleMagicMenuMode = undefined;
  battle.selected = originalBattleCommandIndex("item");
  battle.battleRootSelected = 0;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = "root";
  renderOriginalMenuBar();
  draw();
}

function openOriginalBattleMagicRootMenu(battle: BattleState): void {
  state.mode = "battle";
  state.previousMode = "battle";
  originalMenuBarSelectedKey = "magic";
  battle.phase = "magic";
  battle.battleMagicMenuMode = "root";
  battle.battleItemMenuMode = undefined;
  battle.selected = originalBattleCommandIndex("magic");
  battle.battleRootSelected = 0;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = "root";
  renderOriginalMenuBar();
  draw();
}

function setBattleItemMenuMode(mode: BattleItemMenuMode): void {
  const battle = state.battle;
  if (!battle || battle.phase !== "item") return;
  battle.battleItemMenuMode = mode;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = mode === "root" ? "root" : "leaf";
  renderOriginalMenuBar();
  draw();
}

function setBattleMagicMenuMode(mode: BattleMagicMenuMode): void {
  const battle = state.battle;
  if (!battle || battle.phase !== "magic") return;
  battle.battleMagicMenuMode = mode;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = mode === "root" ? "root" : "leaf";
  renderOriginalMenuBar();
  draw();
}

function cloneParty(party: Actor[], options: { localizeNames?: boolean } = {}): Actor[] {
  return party.map((actor, index) => {
    const cloned = { ...actor };
    const sourceGrowth = DATA.defaultSave.party[index];
    if (options.localizeNames && sourceGrowth?.name) cloned.name = sourceGrowth.name;
    ORIGINAL_ACTOR_GROWTH_KEYS.forEach((key) => {
      if ((cloned[key] ?? 0) <= 0 && (sourceGrowth?.[key] ?? 0) > 0) cloned[key] = sourceGrowth[key];
    });
    return cloned;
  });
}

function cloneMagicRow(row: number[] | undefined): number[] {
  return (row ?? []).slice(0, 16).map((id) => clamp(id, 0, 255));
}

function cloneActorMagic(magicByActor: number[][] | undefined, fallback: number[] = []): number[][] {
  const rows = Array.from({ length: 4 }, (_, index) => cloneMagicRow(magicByActor?.[index]));
  if (rows.some((row) => row.some(Boolean)) || fallback.length === 0) return rows;
  return rows.map(() => cloneMagicRow(fallback));
}

function cloneInventoryRow(row: number[] | undefined): number[] {
  return (row ?? []).slice(0, 10).map((id) => clamp(id, 0, 255));
}

function cloneInventoryGroups(groups: number[][] | undefined, fallback: number[] = [], ownerIndex = 0): number[][] {
  const rows = Array.from({ length: 5 }, (_, index) => cloneInventoryRow(groups?.[index]));
  if (rows.some((row) => row.some(Boolean)) || fallback.length === 0) return rows;
  fallback.filter(Boolean).forEach((id) => {
    const group = inventoryGroupForItem(id, ownerIndex);
    for (let slot = 0; slot < 10; slot += 1) {
      if ((rows[group][slot] ?? 0) <= 0) {
        rows[group][slot] = id;
        break;
      }
    }
  });
  return rows;
}

function flattenInventoryGroups(groups = state.inventoryByActor): number[] {
  return groups.flat().filter(Boolean);
}

function syncFlatInventory(): void {
  state.inventory = flattenInventoryGroups();
}

function aggregateActorMagic(actorMagic: number[][]): number[] {
  const seen = new Set<number>();
  const result: number[] = [];
  actorMagic.flat().forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    result.push(id);
  });
  return result;
}

function configureFormulaProbe(options: FormulaProbeOptions = {}): void {
  const actor = state.party[0];
  Object.assign(actor, {
    hp: 5000,
    maxHp: 5000,
    mp: 1200,
    maxMp: 1200,
    offense: 100,
    weapon: 0,
    defense: 900,
    protector: 0,
    speed: 2000,
    level: 12,
    experience: 0,
    nextExp: 1000,
    maxHpUp: 10,
    maxMpUp: 5,
    offenseUp: 3,
    defenseUp: 3,
    speedUp: 2,
    state: 1,
    ...options.actor
  });
  state.party.slice(1).forEach((member) => {
    member.hp = 0;
    member.mp = 0;
    member.state = 0;
  });
  state.inventoryByActor = cloneInventoryGroups(options.inventoryByActor, options.inventoryIds ?? [], 0);
  syncFlatInventory();
  const magicIds = [...(options.magicIds ?? [])];
  state.learnedMagic = [...magicIds];
  state.actorMagic = [magicIds, [], [], []];
  state.charType = 0;
  state.sourceActorIndex = 0;
  state.targetActorIndex = 0;
  state.mode = "map";
  state.previousMode = "map";
  state.battle = undefined;
  state.walking = undefined;
  state.forcedWalk = undefined;
  state.messages = [];
  state.messageHistory = [];
  state.originalMessageQueue = makeOriginalMessageQueueState();
  state.deferred = [];
  state.waitClick = false;
  clickAnimationStartedAt = 0;
  state.timerMessageWait = undefined;
  state.encounterCount = 200;
  state.rngSeed = options.seed ?? 0x13572468;
}

function normalizeVolume(value: unknown): number {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_AUDIO_VOLUME;
  return clamp(Math.round(numeric / 10) * 10, 0, 100);
}

function normalizeBattleTextSpeed(value: unknown): BattleTextSpeed {
  if (value === "normal" || value === "slow") return value;
  return "fast";
}

function battleTextWaitMultiplier(speed: BattleTextSpeed = state.settings.battleTextSpeed): number {
  if (speed === "normal") return 1.5;
  if (speed === "slow") return 2;
  return 1;
}

function normalizeSettings(settings: Partial<Settings> | undefined): Settings {
  return {
    bgmOn: Boolean(settings?.bgmOn ?? true),
    bgmVolume: normalizeVolume(settings?.bgmVolume ?? DEFAULT_AUDIO_VOLUME),
    seOn: Boolean(settings?.seOn ?? true),
    seVolume: normalizeVolume(settings?.seVolume ?? DEFAULT_AUDIO_VOLUME),
    battleTextSpeed: normalizeBattleTextSpeed(settings?.battleTextSpeed),
    language: normalizeGameLocale(settings?.language ?? browserPreferredGameLocale())
  };
}

function readStoredLanguageSetting(): string | undefined {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return undefined;
    const settings = JSON.parse(raw) as Partial<Settings>;
    return typeof settings.language === "string" ? settings.language : undefined;
  } catch {
    return undefined;
  }
}

function readSettings(): Settings {
  const fallback = normalizeSettings(undefined);
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    return normalizeSettings({ ...fallback, ...JSON.parse(raw) });
  } catch {
    return fallback;
  }
}

function persistSettings(): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
}

function wirePwaInstallPrompt(): void {
  pwaInstallButton?.addEventListener("click", () => {
    void handlePwaInstallButtonClick();
  });
  window.addEventListener("beforeinstallprompt", (event) => {
    const installPrompt = event as PwaBeforeInstallPromptEvent;
    if (typeof installPrompt.prompt !== "function") return;
    event.preventDefault();
    deferredPwaInstallPrompt = installPrompt;
    syncPwaInstallButton();
  });
  window.addEventListener("appinstalled", () => {
    deferredPwaInstallPrompt = undefined;
    syncPwaInstallButton();
  });
  const standaloneDisplayMode = window.matchMedia?.("(display-mode: standalone)");
  standaloneDisplayMode?.addEventListener?.("change", syncPwaInstallButton);
  import.meta.hot?.dispose(() => standaloneDisplayMode?.removeEventListener?.("change", syncPwaInstallButton));
  syncPwaInstallButton();
}

async function handlePwaInstallButtonClick(): Promise<void> {
  resumeAudio();
  if (isPwaStandalone()) {
    deferredPwaInstallPrompt = undefined;
    syncPwaInstallButton();
    return;
  }
  const installPrompt = deferredPwaInstallPrompt;
  if (!installPrompt) {
    showManualPwaInstallInstructions();
    return;
  }
  try {
    const choice = await installPrompt.prompt();
    deferredPwaInstallPrompt = undefined;
    syncPwaInstallButton();
    if (pwaInstallOutcome(choice) === "accepted") return;
  } catch {
    deferredPwaInstallPrompt = undefined;
    syncPwaInstallButton();
    showManualPwaInstallInstructions();
  }
}

function syncPwaInstallButton(): void {
  if (!pwaInstallButton) return;
  pwaInstallButton.hidden = !canOfferPwaInstall();
}

function canOfferPwaInstall(): boolean {
  if (isPwaStandalone() || location.protocol === "file:") return false;
  if (!deferredPwaInstallPrompt) return false;
  if (location.protocol === "https:") return true;
  return ["localhost", "127.0.0.1", "::1"].includes(location.hostname);
}

function isPwaStandalone(): boolean {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return navigatorWithStandalone.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

function pwaInstallOutcome(choice: PwaInstallPromptChoice | undefined): PwaInstallPromptOutcome | undefined {
  if (choice?.outcome === "accepted" || choice?.outcome === "dismissed") return choice.outcome;
  if (choice?.userChoice === "accepted" || choice?.userChoice === "dismissed") return choice.userChoice;
  return undefined;
}

function showManualPwaInstallInstructions(): void {
  window.alert(gameText("message.install.manual"));
}

function wireSettingsControls(): void {
  settingsButton?.addEventListener("click", () => {
    resumeAudio();
    openSettingsDialog();
  });
  settingsDialog?.addEventListener("keydown", (event) => {
    event.stopPropagation();
  });
  settingsDialog?.addEventListener("click", (event) => {
    if (event.target === settingsDialog) settingsDialog.close();
  });
  bgmEnabledInput?.addEventListener("change", () => {
    void updateSettings({ bgmOn: Boolean(bgmEnabledInput.checked) });
  });
  seEnabledInput?.addEventListener("change", () => {
    void updateSettings({ seOn: Boolean(seEnabledInput.checked) });
  });
  bgmVolumeRange?.addEventListener("input", () => {
    void updateSettings({ bgmVolume: normalizeVolume(bgmVolumeRange.value) });
  });
  seVolumeRange?.addEventListener("input", () => {
    void updateSettings({ seVolume: normalizeVolume(seVolumeRange.value) });
  });
  battleTextSpeedSelect?.addEventListener("change", () => {
    void updateSettings({ battleTextSpeed: normalizeBattleTextSpeed(battleTextSpeedSelect.value) });
  });
  settingsLanguageSelect?.addEventListener("change", () => {
    void updateSettings({ language: normalizeGameLocale(settingsLanguageSelect.value) });
  });
  syncSettingsDialogControls();
}

function wireShareButtons(): void {
  shareXButton?.addEventListener("click", shareToX);
  shareFacebookButton?.addEventListener("click", shareToFacebook);
  copyUrlButton?.addEventListener("click", () => {
    void copyShareUrl();
  });
}

function wireHelpControls(): void {
  helpButton?.addEventListener("click", () => {
    openHelpDialog();
  });
  helpDialog?.addEventListener("keydown", (event) => {
    event.stopPropagation();
  });
  helpDialog?.addEventListener("click", (event) => {
    if (event.target === helpDialog) helpDialog.close();
  });
  helpLanguageSelect?.addEventListener("change", () => {
    void updateSettings({ language: normalizeGameLocale(helpLanguageSelect.value) });
  });
  syncLanguageControls();
}

function openSettingsDialog(): void {
  if (!settingsDialog) return;
  syncSettingsDialogControls();
  if (settingsDialog.open) return;
  if (typeof settingsDialog.showModal === "function") {
    settingsDialog.showModal();
  } else {
    settingsDialog.setAttribute("open", "");
  }
  bgmEnabledInput?.focus({ preventScroll: true });
}

function openHelpDialog(): void {
  if (!helpDialog) return;
  syncLanguageControls();
  if (helpDialog.open) return;
  if (typeof helpDialog.showModal === "function") {
    helpDialog.showModal();
  } else {
    helpDialog.setAttribute("open", "");
  }
  helpDialog.querySelector<HTMLButtonElement>("#close-help-button")?.focus({ preventScroll: true });
}

function syncSettingsDialogControls(): void {
  if (bgmEnabledInput) bgmEnabledInput.checked = state.settings.bgmOn;
  syncVolumeControl(bgmVolumeRange, bgmVolumeOutput, state.settings.bgmVolume);
  if (seEnabledInput) seEnabledInput.checked = state.settings.seOn;
  syncVolumeControl(seVolumeRange, seVolumeOutput, state.settings.seVolume);
  if (battleTextSpeedSelect) battleTextSpeedSelect.value = state.settings.battleTextSpeed;
  syncLanguageControls();
}

function syncLanguageControls(): void {
  [settingsLanguageSelect, helpLanguageSelect].forEach((select) => {
    if (select) select.value = state.settings.language;
  });
}

function syncVolumeControl(range: HTMLInputElement | null, output: HTMLOutputElement | null, value: number): void {
  const normalized = String(normalizeVolume(value));
  if (range) range.value = normalized;
  if (output) {
    output.value = normalized;
    output.textContent = normalized;
  }
}

async function updateSettings(next: Partial<Settings>): Promise<void> {
  const previousBgmOn = state.settings.bgmOn;
  const previousLanguage = state.settings.language;
  const previousData = DATA;
  const previousParty = state.party.map((actor) => ({ ...actor }));
  const previousSourceActorIndex = state.sourceActorIndex;
  const previousTargetActorIndex = state.targetActorIndex;
  state.settings = normalizeSettings({ ...state.settings, ...next });
  if (state.settings.language !== previousLanguage) {
    const requestedLanguage = state.settings.language;
    await loadGameLocale(requestedLanguage);
    if (state.settings.language !== requestedLanguage) return;
    applyLocalizedData(localizeOriginalData(ORIGINAL_SOURCE_DATA));
    relocalizeRuntimeStateAfterLanguageChange(previousData, previousParty, previousSourceActorIndex, previousTargetActorIndex);
  }
  persistSettings();
  if (state.settings.language !== previousLanguage) applyStaticDomI18n();
  syncSettingsDialogControls();
  if (!state.settings.bgmOn) {
    stopBgm(false, true);
  } else if (!previousBgmOn && requestedBgm) {
    bgmStoppedBySource = false;
    playBgm(requestedBgm);
  } else if (currentBgm?.gain) {
    currentBgm.gain.gain.value = state.settings.bgmVolume / 100;
  }
  if (!state.settings.seOn) stopSe();
  activeSeAudio.forEach((audio) => {
    audio.volume = state.settings.seVolume / 100;
  });
}

type LocalizedRuntimeTextMaps = {
  tokens: Map<string, string>;
  lines: Map<string, string>;
};

function relocalizeRuntimeStateAfterLanguageChange(
  previousData: DataShape,
  previousParty: Actor[],
  previousSourceActorIndex: number,
  previousTargetActorIndex: number
): void {
  relocalizePartyNames();
  const textMaps = buildLocalizedRuntimeTextMaps(previousData, DATA, previousParty, previousSourceActorIndex, previousTargetActorIndex);
  relocalizeOriginalMessageQueue(state.originalMessageQueue, textMaps.tokens);
  state.messages = relocalizeTextLines(state.messages, textMaps.lines);
  state.messageHistory = relocalizeTextLines(state.messageHistory, textMaps.lines);
  state.activeInspection = undefined;
  if (state.battle) relocalizeBattleState(state.battle, textMaps);
  if (originalConfirmDialogOpen()) closeOriginalConfirmDialog(false);
  renderOriginalMenuBar();
  draw();
}

function relocalizePartyNames(): void {
  state.party = state.party.map((actor, index) => ({
    ...actor,
    name: DATA.defaultSave.party[index]?.name || actor.name
  }));
}

function buildLocalizedRuntimeTextMaps(
  previousData: DataShape,
  nextData: DataShape,
  previousParty: Actor[],
  previousSourceActorIndex: number,
  previousTargetActorIndex: number
): LocalizedRuntimeTextMaps {
  const tokens = new Map<string, string>();
  const lines = new Map<string, string>();
  const ambiguousTokens = new Set<string>();
  const ambiguousLines = new Set<string>();
  const addPair = (map: Map<string, string>, ambiguous: Set<string>, previousText: string, nextText: string) => {
    if (!previousText || previousText === nextText || ambiguous.has(previousText)) return;
    const existing = map.get(previousText);
    if (existing === undefined) {
      map.set(previousText, nextText);
    } else if (existing !== nextText) {
      map.delete(previousText);
      ambiguous.add(previousText);
    }
  };
  const addPrintLinePair = (previousToken: string, nextToken: string) => {
    const previousText = originalPrintTokenText(previousToken);
    const nextText = originalPrintTokenText(nextToken);
    if (previousText === undefined || nextText === undefined) return;
    addPair(
      lines,
      ambiguousLines,
      replaceNamesWithParty(previousText, previousParty, previousSourceActorIndex, previousTargetActorIndex),
      replaceNames(nextText)
    );
  };
  const addScriptPairs = (previousScript: string, nextScript: string) => {
    if (!previousScript || !nextScript) return;
    const previousTokens = originalEventTokens(previousScript);
    const nextTokens = originalEventTokens(nextScript);
    const count = Math.min(previousTokens.length, nextTokens.length);
    for (let index = 0; index < count; index += 1) {
      addPair(tokens, ambiguousTokens, previousTokens[index], nextTokens[index]);
      addPrintLinePair(previousTokens[index], nextTokens[index]);
    }
  };

  addScriptPairs(previousData.defaultSave.openingScript, nextData.defaultSave.openingScript);
  previousData.items.forEach((item, index) => addScriptPairs(item.script, nextData.items[index]?.script ?? ""));
  previousData.magic.forEach((spell, index) => addScriptPairs(spell.script, nextData.magic[index]?.script ?? ""));
  previousData.maps.forEach((map, mapIndex) => {
    map.events.forEach((event, eventIndex) => {
      addScriptPairs(event.script, nextData.maps[mapIndex]?.events[eventIndex]?.script ?? "");
    });
  });
  return { tokens, lines };
}

function originalPrintTokenText(token: string): string | undefined {
  const match = /^Print\s+(.*)$/i.exec(token);
  return match ? match[1] : undefined;
}

function relocalizeOriginalMessageQueue(queue: OriginalMessageQueueState, tokenMap: Map<string, string>): void {
  for (const entry of queue.slots) {
    if (!entry) continue;
    entry.token = tokenMap.get(entry.token) ?? entry.token;
  }
  if (queue.lastShifted) queue.lastShifted = tokenMap.get(queue.lastShifted) ?? queue.lastShifted;
}

function relocalizeTextLines(values: string[], lineMap: Map<string, string>): string[] {
  return values.map((value) => lineMap.get(value) ?? value);
}

function relocalizeBattleState(battle: BattleState, textMaps: LocalizedRuntimeTextMaps): void {
  battle.enemies = battle.enemies.map((enemy) => relocalizeBattleEnemy(enemy));
  if (battle.onWinMessageQueue) relocalizeOriginalMessageQueue(battle.onWinMessageQueue, textMaps.tokens);
  battle.battleLog = relocalizeTextLines(battle.battleLog, textMaps.lines);
}

function relocalizeBattleEnemy(enemy: BattleEnemy): BattleEnemy {
  if (!isOriginalBattleEnemySlot(enemy)) return enemy;
  const localized = enemies.get(enemy.id);
  if (!localized) return enemy;
  return {
    ...localized,
    currentHp: enemy.currentHp,
    powerScale: enemy.powerScale,
    powerModifier: enemy.powerModifier
  };
}

function shareToX(): void {
  const url = new URL("https://twitter.com/intent/tweet");
  url.searchParams.set("text", gameText("source.main.2bab8b83"));
  url.searchParams.set("url", CANONICAL_URL);
  trackEvent("share", { method: "x" });
  window.open(url, "_blank", "noopener,noreferrer");
}

function shareToFacebook(): void {
  const url = new URL("https://www.facebook.com/sharer/sharer.php");
  url.searchParams.set("u", CANONICAL_URL);
  trackEvent("share", { method: "facebook" });
  window.open(url, "_blank", "noopener,noreferrer");
}

async function copyShareUrl(): Promise<void> {
  await copyText(CANONICAL_URL);
  trackEvent("share", { method: "copy_url" });
  showCopyToast(gameText("ui.toast.urlCopied"));
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back for browsers that expose Clipboard API but deny writes.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function showCopyToast(message: string): void {
  if (!copyToast) return;
  if (copyToastTimer !== undefined) window.clearTimeout(copyToastTimer);
  copyToast.textContent = message;
  copyToast.hidden = false;
  requestAnimationFrame(() => copyToast.classList.add("is-visible"));
  copyToastTimer = window.setTimeout(() => {
    copyToast.classList.remove("is-visible");
    copyToastTimer = window.setTimeout(() => {
      copyToast.hidden = true;
      copyToast.textContent = "";
      copyToastTimer = undefined;
    }, 180);
  }, 1600);
}

async function loadImages(): Promise<void> {
  const imageNames = new Set<string>(["mainwnd.png", "mainchar.png", "hpmpbar.png", "tbox.png"]);
  DATA.maps.forEach((map) => imageNames.add(map.tileSheet));
  DATA.enemies.forEach((enemy) => {
    if (enemy.image) imageNames.add(enemy.image);
  });
  await Promise.all(
    [...imageNames].map((name) => {
      const image = new Image();
      image.src = `/assets/images/${name}`;
      imageCache.set(name, image);
      return new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });
    })
  );
}

function startFrameLoop(): void {
  if (animationFrameId !== undefined || !pageAudioActive) return;
  animationFrameId = requestAnimationFrame(frame);
}

function stopFrameLoop(): void {
  if (animationFrameId === undefined) return;
  cancelAnimationFrame(animationFrameId);
  animationFrameId = undefined;
}

function frame(): void {
  animationFrameId = undefined;
  if (!pageAudioActive) return;
  animationTick += 1;
  advanceWalking();
  advanceKeyboardMovement();
  advanceWipe();
  advanceTimerMessageWait();
  drainOriginalMessageCache();
  advanceOriginalForcedWalk();
  resumeDeferredIfIdle();
  advanceOriginalBattleTurnFlag();
  advancePointerPath();
  pollGamepad();
  renderOriginalMenuBar();
  draw();
  startFrameLoop();
}

function draw(): void {
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, BASE_WIDTH, BASE_HEIGHT);
  drawBaseWindow();
  if (state.mode === "title") {
    drawTitle();
    drawWipeOverlay();
    return;
  }
  if (state.battle && (state.mode === "battle" || state.previousMode === "battle")) {
    if (shouldPreserveMapSurfaceForBattleEntry()) drawMap();
    else drawBattle();
    drawStatus();
    drawModeOverlay();
    drawWipeOverlay();
    return;
  }
  drawMap();
  drawStatus();
  drawModeOverlay();
  drawWipeOverlay();
}

function drawBaseWindow(): void {
  if (!BASE_WINDOW_RENDERING_ENABLED) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);
    return;
  }
  const main = imageCache.get("mainwnd.png");
  if (main?.complete && main.naturalWidth > 0) {
    ctx.drawImage(main, 0, 0);
  } else {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);
    ctx.fillStyle = "#000078";
    ctx.fillRect(0, 304, BASE_WIDTH, 80);
    ctx.fillRect(370, 2, 140, 300);
  }
}

function drawTitle(): void {
  drawOriginalTitleSurface();
}

function drawOriginalTitleSurface(): void {
  // BackToTitle loads MainWnd.Bmp in the original; drawBaseWindow has already painted mainwnd.png.
}

function invalidateOriginalMapCache(): void {
  originalMapCache = undefined;
}

function makeOriginalMapDrawTraceState(): OriginalMapDrawTraceState {
  return { first: 0, second: 0, third: 0, thirdSuppressedByDefaultMessage: 0, lastReason: "", lastStages: [] };
}

function resetOriginalMapDrawTrace(): void {
  originalMapDrawTrace = makeOriginalMapDrawTraceState();
}

function recordOriginalMapDrawStage(stage: "first" | "second" | "third", reason: string): void {
  originalMapDrawTrace[stage] += 1;
  originalMapDrawTrace.lastReason = reason;
  originalMapDrawTrace.lastStages.push(stage);
  if (originalMapDrawTrace.lastStages.length > 12) originalMapDrawTrace.lastStages.shift();
}

function performOriginalMapDrawFirst(reason: string): void {
  recordOriginalMapDrawStage("first", reason);
  invalidateOriginalMapCache();
}

function performOriginalMapDrawSecond(reason: string): void {
  recordOriginalMapDrawStage("second", reason);
}

function performOriginalMapDrawThird(reason: string): void {
  recordOriginalMapDrawStage("third", reason);
  state.activeInspection = undefined;
}

function suppressOriginalMapDrawThirdForDefaultMessage(reason: string): void {
  originalMapDrawTrace.thirdSuppressedByDefaultMessage += 1;
  originalMapDrawTrace.lastReason = reason;
  originalMapDrawTrace.lastStages.push("third:suppressed-default-message");
  if (originalMapDrawTrace.lastStages.length > 12) originalMapDrawTrace.lastStages.shift();
}

function drawMapTile(target: CanvasRenderingContext2D, map: MapData, tile: number, destX: number, destY: number): void {
  const sheet = imageCache.get(map.tileSheet);
  if (sheet?.complete && sheet.naturalWidth > 0) {
    const source = originalMapTileSourceRect(tile);
    const operation = originalMapTileRenderingOperation(tile, destX, destY);
    target.drawImage(
      sheet,
      source.x,
      source.y,
      source.width,
      source.height,
      operation.destination.x,
      operation.destination.y,
      operation.destination.width,
      operation.destination.height
    );
  } else {
    target.fillStyle = tile % 2 === 0 ? "#183018" : "#284028";
    target.fillRect(destX, destY, TILE, TILE);
  }
}

function createOriginalMapCacheCanvas(): OriginalMapCacheState {
  const canvas = document.createElement("canvas");
  canvas.width = MAP_CACHE_COLS * TILE;
  canvas.height = MAP_CACHE_ROWS * TILE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Map cache 2D context not available");
  context.imageSmoothingEnabled = false;
  return {
    canvas,
    context,
    mapId: "",
    centerX: 0,
    centerY: 0,
    tileSheet: ""
  };
}

function rebuildOriginalMapCache(centerX: number, centerY: number): OriginalMapCacheState {
  const map = currentMap();
  const cache = originalMapCache ?? createOriginalMapCacheCanvas();
  cache.mapId = map.id;
  cache.centerX = wrapMapCoordinate(centerX, map.width);
  cache.centerY = wrapMapCoordinate(centerY, map.height);
  cache.tileSheet = map.tileSheet;
  cache.context.clearRect(0, 0, cache.canvas.width, cache.canvas.height);
  for (let y = 0; y < MAP_CACHE_ROWS; y += 1) {
    const mapY = wrapMapCoordinate(cache.centerY + y - Math.floor(MAP_CACHE_ROWS / 2), map.height);
    for (let x = 0; x < MAP_CACHE_COLS; x += 1) {
      const mapX = wrapMapCoordinate(cache.centerX + x - Math.floor(MAP_CACHE_COLS / 2), map.width);
      drawMapTile(cache.context, map, tileIndexFor(map.id, mapX, mapY), x * TILE, y * TILE);
    }
  }
  originalMapCache = cache;
  return cache;
}

function ensureOriginalMapCache(centerX = state.x, centerY = state.y): OriginalMapCacheState {
  const map = currentMap();
  const normalizedCenterX = wrapMapCoordinate(centerX, map.width);
  const normalizedCenterY = wrapMapCoordinate(centerY, map.height);
  if (
    originalMapCache &&
    originalMapCache.mapId === map.id &&
    originalMapCache.centerX === normalizedCenterX &&
    originalMapCache.centerY === normalizedCenterY &&
    originalMapCache.tileSheet === map.tileSheet
  ) {
    return originalMapCache;
  }
  return rebuildOriginalMapCache(normalizedCenterX, normalizedCenterY);
}

function updateOriginalMapCacheAfterMove(moveX: number, moveY: number): void {
  if (!moveX && !moveY) return;
  const map = currentMap();
  const previousX = wrapMapCoordinate(state.x - moveX, map.width);
  const previousY = wrapMapCoordinate(state.y - moveY, map.height);
  if (
    !originalMapCache ||
    originalMapCache.mapId !== map.id ||
    originalMapCache.centerX !== previousX ||
    originalMapCache.centerY !== previousY ||
    originalMapCache.tileSheet !== map.tileSheet
  ) {
    rebuildOriginalMapCache(state.x, state.y);
    return;
  }
  const cache = originalMapCache;
  const target = cache.context;
  if (moveX) {
    if (moveX === 1) {
      target.drawImage(cache.canvas, TILE, 0, (MAP_CACHE_COLS - 1) * TILE, MAP_CACHE_ROWS * TILE, 0, 0, (MAP_CACHE_COLS - 1) * TILE, MAP_CACHE_ROWS * TILE);
      const destX = (MAP_CACHE_COLS - 1) * TILE;
      const mapX = wrapMapCoordinate(state.x + Math.floor(MAP_CACHE_COLS / 2), map.width);
      for (let y = 0; y < MAP_CACHE_ROWS; y += 1) {
        const mapY = wrapMapCoordinate(state.y + y - Math.floor(MAP_CACHE_ROWS / 2), map.height);
        drawMapTile(target, map, tileIndexFor(map.id, mapX, mapY), destX, y * TILE);
      }
    } else {
      target.drawImage(cache.canvas, 0, 0, (MAP_CACHE_COLS - 1) * TILE, MAP_CACHE_ROWS * TILE, TILE, 0, (MAP_CACHE_COLS - 1) * TILE, MAP_CACHE_ROWS * TILE);
      const mapX = wrapMapCoordinate(state.x - Math.floor(MAP_CACHE_COLS / 2), map.width);
      for (let y = 0; y < MAP_CACHE_ROWS; y += 1) {
        const mapY = wrapMapCoordinate(state.y + y - Math.floor(MAP_CACHE_ROWS / 2), map.height);
        drawMapTile(target, map, tileIndexFor(map.id, mapX, mapY), 0, y * TILE);
      }
    }
  } else if (moveY) {
    if (moveY === 1) {
      target.drawImage(cache.canvas, 0, TILE, MAP_CACHE_COLS * TILE, (MAP_CACHE_ROWS - 1) * TILE, 0, 0, MAP_CACHE_COLS * TILE, (MAP_CACHE_ROWS - 1) * TILE);
      const destY = (MAP_CACHE_ROWS - 1) * TILE;
      const mapY = wrapMapCoordinate(state.y + Math.floor(MAP_CACHE_ROWS / 2), map.height);
      for (let x = 0; x < MAP_CACHE_COLS; x += 1) {
        const mapX = wrapMapCoordinate(state.x + x - Math.floor(MAP_CACHE_COLS / 2), map.width);
        drawMapTile(target, map, tileIndexFor(map.id, mapX, mapY), x * TILE, destY);
      }
    } else {
      target.drawImage(cache.canvas, 0, 0, MAP_CACHE_COLS * TILE, (MAP_CACHE_ROWS - 1) * TILE, 0, TILE, MAP_CACHE_COLS * TILE, (MAP_CACHE_ROWS - 1) * TILE);
      const mapY = wrapMapCoordinate(state.y - Math.floor(MAP_CACHE_ROWS / 2), map.height);
      for (let x = 0; x < MAP_CACHE_COLS; x += 1) {
        const mapX = wrapMapCoordinate(state.x + x - Math.floor(MAP_CACHE_COLS / 2), map.width);
        drawMapTile(target, map, tileIndexFor(map.id, mapX, mapY), x * TILE, 0);
      }
    }
  }
  cache.centerX = wrapMapCoordinate(state.x, map.width);
  cache.centerY = wrapMapCoordinate(state.y, map.height);
}

function drawMap(): void {
  if (!MAP_RENDERING_ENABLED) return;
  const walking = state.walking;
  const centerX = walking?.fromX ?? state.x;
  const centerY = walking?.fromY ?? state.y;
  const cache = ensureOriginalMapCache(centerX, centerY);
  const scrollFrame = walking ? smoothWalkingScrollFrame(walking) : undefined;
  const sourceX = scrollFrame?.mapCacheSourceX ?? MAP_CACHE_CROP_X;
  const sourceY = scrollFrame?.mapCacheSourceY ?? MAP_CACHE_CROP_Y;
  const originX = centerX - Math.floor(VIEW_COLS / 2);
  const originY = centerY - Math.floor(VIEW_ROWS / 2);
  ctx.save();
  ctx.beginPath();
  ctx.rect(MAP_X, MAP_Y, VIEW_COLS * TILE, VIEW_ROWS * TILE);
  ctx.clip();
  ctx.fillStyle = "#000";
  ctx.fillRect(MAP_X, MAP_Y, VIEW_COLS * TILE, VIEW_ROWS * TILE);
  ctx.drawImage(cache.canvas, sourceX, sourceY, VIEW_COLS * TILE, VIEW_ROWS * TILE, MAP_X, MAP_Y, VIEW_COLS * TILE, VIEW_ROWS * TILE);
  drawPlayer(originX, originY);
  ctx.restore();
}

function smoothWalkingScrollFrame(walking: WalkingState): { mapCacheSourceX: number; mapCacheSourceY: number; scrollPixels: number } {
  const progress = walkingProgress(walking);
  const scrollPixels = clamp(Math.round(progress * TILE), 0, TILE);
  return {
    mapCacheSourceX: MAP_CACHE_CROP_X + scrollPixels * Math.sign(walking.moveX),
    mapCacheSourceY: MAP_CACHE_CROP_Y + scrollPixels * Math.sign(walking.moveY),
    scrollPixels
  };
}

function walkingProgress(walking: WalkingState, now = performance.now()): number {
  return walking.durationMs > 0 ? clamp((now - walking.startedAt) / walking.durationMs, 0, 1) : 1;
}

function walkingSpriteFrameIndex(walking: WalkingState): number {
  return walkingProgress(walking) < 0.5 ? 0 : 1;
}

function drawPlayer(originX: number, originY: number): void {
  if (!PLAYER_SPRITES_ENABLED) return;
  const sprite = imageCache.get("mainchar.png");
  const screenX = MAP_X + (state.x - originX) * TILE;
  const screenY = MAP_Y + (state.y - originY) * TILE;
  if (sprite?.complete && sprite.naturalWidth > 0) {
    const frameIndex = state.walking ? walkingSpriteFrameIndex(state.walking) : 0;
    const layout = originalPlayerSpriteLayout(state.direction, frameIndex, state.charType);
    const composition = originalPlayerRenderingComposition(
      state.direction,
      frameIndex,
      state.charType,
      screenX,
      screenY
    );
    drawMaskedPlayerSprite(
      sprite,
      layout,
      composition.source.destination.x,
      composition.source.destination.y,
      composition.source.destination.width,
      composition.source.destination.height
    );
  } else {
    ctx.fillStyle = "#fff";
    ctx.fillRect(screenX + 8, screenY + 6, 16, 22);
  }
}

function drawMaskedPlayerSprite(
  sprite: HTMLImageElement,
  layout: ReturnType<typeof originalPlayerSpriteLayout>,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const key = `${layout.source.x}:${layout.source.y}:${layout.mask.x}:${layout.mask.y}:${layout.source.width}:${layout.source.height}`;
  let masked = playerSpriteFrameCache.get(key);
  if (!masked) {
    const scratch = document.createElement("canvas");
    scratch.width = layout.source.width;
    scratch.height = layout.source.height;
    const scratchCtx = scratch.getContext("2d", { willReadFrequently: true });
    if (!scratchCtx) {
      ctx.drawImage(sprite, layout.source.x, layout.source.y, layout.source.width, layout.source.height, x, y, width, height);
      return;
    }
    scratchCtx.drawImage(sprite, layout.source.x, layout.source.y, layout.source.width, layout.source.height, 0, 0, layout.source.width, layout.source.height);
    const source = scratchCtx.getImageData(0, 0, layout.source.width, layout.source.height);
    scratchCtx.clearRect(0, 0, layout.source.width, layout.source.height);
    scratchCtx.drawImage(sprite, layout.mask.x, layout.mask.y, layout.mask.width, layout.mask.height, 0, 0, layout.mask.width, layout.mask.height);
    const mask = scratchCtx.getImageData(0, 0, layout.mask.width, layout.mask.height);
    masked = { source, mask };
    playerSpriteFrameCache.set(key, masked);
  }
  if (width !== layout.source.width || height !== layout.source.height) {
    ctx.drawImage(sprite, layout.source.x, layout.source.y, layout.source.width, layout.source.height, x, y, width, height);
    return;
  }
  try {
    const output = ctx.getImageData(x, y, layout.source.width, layout.source.height);
    for (let index = 0; index < output.data.length; index += 4) {
      output.data[index] = (output.data[index] & masked.mask.data[index]) ^ masked.source.data[index];
      output.data[index + 1] = (output.data[index + 1] & masked.mask.data[index + 1]) ^ masked.source.data[index + 1];
      output.data[index + 2] = (output.data[index + 2] & masked.mask.data[index + 2]) ^ masked.source.data[index + 2];
      output.data[index + 3] = 255;
    }
    ctx.putImageData(output, x, y);
  } catch {
    ctx.drawImage(sprite, layout.source.x, layout.source.y, layout.source.width, layout.source.height, x, y, width, height);
  }
}

function drawStatus(): void {
  if (!STATUS_UI_RENDERING_ENABLED) return;
  drawOriginalAllState();
  drawOriginalStatusSelectionFrames();
}

function drawOriginalAllState(): void {
  for (let i = 0; i < 4; i += 1) {
    drawOriginalStateSlot(i);
  }
  drawOriginalMoney();
}

function drawOriginalStateSlot(index: number): void {
  const actor = state.party[index];
  if (!actor || actor.state <= 0) return;
  drawText(originalStatusPanelActorName(actor), 376, 8 + index * 64, "#ffffff", 14);
  drawActorGaugeBars(index, actor);
}

function drawOriginalMoney(): void {
  clearOriginalFormTextArea(376, 280, ORIGINAL_MONEY_TEXT_WIDTH, ORIGINAL_FORM_TEXT_LINE_HEIGHT);
  drawOriginalFormText(String(state.money).padStart(14, " "), 376, 280, "#ffffff");
}

function drawOriginalStatusSelectionFrames(): void {
  drawOriginalManNoFrame(originalSourceActorIndex(), "source");
  drawOriginalManNoFrame(targetActorIndex(), "target");
}

function drawOriginalManNoFrame(actorIndex: number, role: "source" | "target"): void {
  const actor = state.party[actorIndex];
  if (actorIndex < 0 || actorIndex >= 4 || !actor || actor.state <= 0) return;
  const top = 4 + actorIndex * 64;
  const bottom = 58 + actorIndex * 64;
  ctx.strokeStyle = "#ff0000";
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (role === "source") {
    ctx.moveTo(374, 5 + actorIndex * 64);
    ctx.lineTo(374, 57 + actorIndex * 64);
    ctx.moveTo(373, top);
    ctx.lineTo(373, bottom);
    ctx.moveTo(374, 5 + actorIndex * 64);
    ctx.lineTo(506, 5 + actorIndex * 64);
    ctx.moveTo(373, top);
    ctx.lineTo(507, top);
  } else {
    ctx.moveTo(506, 57 + actorIndex * 64);
    ctx.lineTo(506, 5 + actorIndex * 64);
    ctx.moveTo(507, bottom);
    ctx.lineTo(507, top);
    ctx.moveTo(506, 57 + actorIndex * 64);
    ctx.lineTo(374, 57 + actorIndex * 64);
    ctx.moveTo(507, bottom);
    ctx.lineTo(373, bottom);
  }
  ctx.stroke();
}

function drawModeOverlay(): void {
  const persistentMessageSurface = state.messageHistory.length > 0;
  if (state.mode === "message" || state.messages.length > 0) {
    if (state.activeInspection) drawOriginalInspectionWindow(state.activeInspection);
    if (MESSAGE_UI_RENDERING_ENABLED) drawMessageBox();
    return;
  }
  if (state.mode === "battle" && state.battle) {
    if (BATTLE_UI_RENDERING_ENABLED && !shouldPreserveMapSurfaceForBattleEntry()) drawBattle();
    if (persistentMessageSurface && MESSAGE_UI_RENDERING_ENABLED) drawMessageBox();
    return;
  }
  if (debugOverlayEnabled()) {
    drawText(`${currentMap().id}  X:${state.x} Y:${state.y}`, 14, 326, "#ffffff", 13);
    drawText(gameText("source.main.290a84f6"), 14, 350, "#ffffff", 13);
  }
  if (persistentMessageSurface && MESSAGE_UI_RENDERING_ENABLED) drawMessageBox();
}

function shouldPreserveMapSurfaceForBattleEntry(): boolean {
  const battle = state.battle;
  return Boolean(battle && battle.phase === "entry" && !battle.enemiesVisible);
}

function drawWipeOverlay(): void {
  const wipe = state.wipe;
  if (!wipe) return;
  const step = smoothWipeStep(wipe);
  const halfWidth = Math.round(step * WIPE_HALF_STEP_X);
  const halfHeight = Math.round(step * WIPE_HALF_STEP_Y);
  const rectLeft = WIPE_DEST_CENTER_X - halfWidth;
  const rectTop = WIPE_DEST_CENTER_Y - halfHeight;
  const lineWidth = halfWidth * 2;
  const lineHeight = halfHeight * 2;
  const bitBltWidth = Math.max(0, lineWidth - 1);
  const bitBltHeight = Math.max(0, lineHeight - 1);
  const rectWidth = wipe.type === "out" ? lineWidth : bitBltWidth;
  const rectHeight = wipe.type === "out" ? lineHeight : bitBltHeight;
  const areaLeft = MAP_X;
  const areaTop = MAP_Y;
  const areaRight = MAP_X + MAP_VIEW_WIDTH;
  const areaBottom = MAP_Y + MAP_VIEW_HEIGHT;
  const left = clamp(rectLeft, areaLeft, areaRight);
  const top = clamp(rectTop, areaTop, areaBottom);
  const right = clamp(rectLeft + rectWidth, areaLeft, areaRight);
  const bottom = clamp(rectTop + rectHeight, areaTop, areaBottom);
  ctx.fillStyle = "#000000";
  if (wipe.type === "out") {
    // TimerWipe Case 0 uses Main.Line centered at (184,152) with Step(I*22-1, I*18-1).
    ctx.fillRect(left, top, right - left, bottom - top);
    return;
  }
  // TimerWipe Case 1 BitBlts width/height I*22-1/I*18-1 from MapCashe2. Web draws
  // the target surface first, then keeps only that source-shaped rectangle visible.
  void WIPE_SOURCE_CENTER_X;
  void WIPE_SOURCE_CENTER_Y;
  ctx.fillRect(areaLeft, areaTop, MAP_VIEW_WIDTH, top - areaTop);
  ctx.fillRect(areaLeft, bottom, MAP_VIEW_WIDTH, areaBottom - bottom);
  ctx.fillRect(areaLeft, top, left - areaLeft, bottom - top);
  ctx.fillRect(right, top, areaRight - right, bottom - top);
}

function smoothWipeStep(wipe: WipeState, now = performance.now()): number {
  const progress = wipe.durationMs > 0 ? clamp((now - wipe.startedAt) / wipe.durationMs, 0, 1) : 1;
  return progress * wipe.duration;
}

function drawActorGaugeBars(index: number, actor: Actor): void {
  const bar = imageCache.get("hpmpbar.png");
  if (!bar?.complete || bar.naturalWidth <= 0) return;
  originalHpGaugeSegments(index, Math.max(0, actor.hp), actor.maxHp);
  originalMpGaugeSegments(index, Math.max(0, actor.mp), actor.maxMp);
  for (const segment of originalHpRenderingSegments(index, Math.max(0, actor.hp), actor.maxHp)) {
    ctx.drawImage(
      bar,
      segment.source.x,
      segment.source.y,
      segment.source.width,
      segment.source.height,
      segment.destination.x,
      segment.destination.y,
      segment.destination.width,
      segment.destination.height
    );
  }
  for (const segment of originalMpRenderingSegments(index, Math.max(0, actor.mp), actor.maxMp)) {
    ctx.drawImage(
      bar,
      segment.source.x,
      segment.source.y,
      segment.source.width,
      segment.source.height,
      segment.destination.x,
      segment.destination.y,
      segment.destination.width,
      segment.destination.height
    );
  }
}

function originalClickShapeState(): OriginalClickShapeState | null {
  if (!state.waitClick) return null;
  const elapsed = Math.max(0, performance.now() - clickAnimationStartedAt);
  const ticks = Math.floor(elapsed / CLICK_SHAPE_INTERVAL_MS);
  const animation = (ticks * 3) % 12;
  const height = CLICK_SHAPE_BASE_HEIGHT - animation;
  return {
    x: CLICK_SHAPE_X,
    y: CLICK_SHAPE_BASE_Y + animation,
    width: CLICK_SHAPE_WIDTH,
    height,
    animation
  };
}

function drawOriginalClickShape(): void {
  const shape = originalClickShapeState();
  if (!shape) return;
  ctx.save();
  ctx.fillStyle = CLICK_SHAPE_BACK_COLOR;
  ctx.fillRect(shape.x, shape.y, shape.width, shape.height);
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(
    shape.x + shape.width / 2,
    CLICK_SHAPE_BOTTOM - shape.height / 2,
    shape.width / 2,
    Math.max(1, shape.height / 2),
    0,
    0,
    Math.PI * 2
  );
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawMessageBox(): void {
  if (!MESSAGE_UI_RENDERING_ENABLED) return;
  const lines = state.messageHistory.length > 0 ? state.messageHistory : originalMessageTextLines(state.messages[0] ?? "");
  lines.slice(0, ORIGINAL_MESSAGE_PRINT_LAYOUT.maxLines).forEach((line, index) =>
    drawText(line, MESSAGE_X, MESSAGE_Y + index * ORIGINAL_MESSAGE_PRINT_LAYOUT.lineHeight, "#ffffff", 14)
  );
  drawOriginalClickShape();
}

function drawOriginalInspectionWindow(inspection: OriginalInspection): void {
  void inspection.kind;
  ctx.save();
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 1;
  ctx.strokeRect(24, 24, 319, 255);
  ctx.strokeStyle = "#ffffff";
  for (let i = 1; i <= 3; i += 1) {
    ctx.strokeRect(24 + i, 24 + i, 319 - i * 2, 255 - i * 2);
  }
  ctx.fillStyle = ORIGINAL_MAIN_BACK_COLOR;
  ctx.fillRect(28, 28, 311, 247);
  ctx.restore();
  inspection.lines.forEach((line) => drawText(line.text, 24 + line.x, 24 + line.y, "#ffffff", 12));
}

function drawBattle(): void {
  if (!BATTLE_UI_RENDERING_ENABLED) return;
  const battle = state.battle;
  if (!battle) return;
  ctx.fillStyle = "#000000";
  ctx.fillRect(MAP_X, MAP_Y, VIEW_COLS * TILE, VIEW_ROWS * TILE);
  if (battle.enemiesVisible) {
    battle.enemies.forEach((enemy, index) => {
      if (!isOriginalBattleEnemySlot(enemy)) return;
      const image = imageCache.get(enemy.image);
      const layout = battleEnemyLayout(index, enemy);
      if (battle.hiddenEnemyIndexes.includes(index)) return;
      if (enemy.currentHp > 0 && image?.complete && image.naturalWidth > 0) {
        if (ENEMY_SPRITES_ENABLED) {
          ctx.drawImage(image, layout.x, layout.y, layout.width, layout.height);
        }
      }
      if (battleEnemySelectionVisible(battle, index, enemy)) {
        drawEnemySelectionMarker(layout);
      }
    });
  }
}

function drawEnemySelectionMarker(layout: { x: number; y: number; width: number; height: number }): void {
  ctx.save();
  ctx.strokeStyle = "#ff0000";
  ctx.lineWidth = 2;
  ctx.strokeRect(layout.x - 4, layout.y - 4, layout.width + 9, layout.height + 9);
  ctx.restore();
}

function battleEnemySelectionVisible(battle: BattleState, index: number, enemy: BattleEnemy): boolean {
  if (!battle.enemiesVisible || enemy.currentHp <= 0) return false;
  if (battle.phase !== "command") return false;
  return index === battle.targetEnemyIndex;
}

function battleEnemyLayout(index: number, enemy: BattleEnemy): { x: number; y: number; width: number; height: number } {
  const battle = state.battle;
  const enemiesForLayout = battle?.enemies ?? [enemy];
  const occupiedSlots = enemiesForLayout
    .map((candidate, slot) => ({ enemy: candidate, slot, size: enemySpriteNaturalSize(candidate) }))
    .filter(({ enemy: candidate }) => isOriginalBattleEnemySlot(candidate));
  const totalWidth =
    occupiedSlots.reduce((sum, { size }) => sum + size.width, 0) + Math.max(0, occupiedSlots.length - 1) * 16;
  let x = 8 + Math.floor((11 * TILE - totalWidth) / 2);
  for (const slot of occupiedSlots) {
    if (slot.slot >= index) break;
    x += slot.size.width + 16;
  }
  const size = occupiedSlots.find((slot) => slot.slot === index)?.size ?? enemySpriteNaturalSize(enemy);
  return { x, y: 232 - size.height, width: size.width, height: size.height };
}

function enemySpriteNaturalSize(enemy: BattleEnemy): { width: number; height: number } {
  const image = imageCache.get(enemy.image);
  if (image?.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
    return { width: image.naturalWidth, height: image.naturalHeight };
  }
  return { width: 64, height: 64 };
}

function drawText(text: string, x: number, y: number, color = "#ffffff", size = 14): void {
  ctx.font = `${size}px "MS Gothic", "Yu Gothic", monospace`;
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.fillText(text, x, y);
}

function drawOriginalFormText(text: string, x: number, y: number, color = "#ffffff"): void {
  ctx.font = ORIGINAL_FORM_FONT;
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.fillText(text, x, y);
}

function clearOriginalFormTextArea(x: number, y: number, width: number, height: number): void {
  const main = imageCache.get("mainwnd.png");
  if (main?.complete && main.naturalWidth > 0) {
    ctx.drawImage(main, x, y, width, height, x, y, width, height);
    return;
  }
  ctx.fillStyle = "#000000";
  ctx.fillRect(x, y, width, height);
}

function handleOriginalSystemShortcut(event: KeyboardEvent): boolean {
  if (!event.ctrlKey || event.altKey || event.metaKey) return false;
  const key = event.key.toLowerCase();
  if (key === "s" && event.shiftKey) {
    if (!originalSystemShortcutAvailable("newSave")) return false;
    event.preventDefault();
    openNewSaveMenu();
    return true;
  }
  if (key === "s" && !event.shiftKey) {
    event.preventDefault();
    if (state.saveDataSlot === 0) {
      if (!originalSystemShortcutAvailable("newSave")) return true;
      openNewSaveMenu();
      return true;
    }
    if (!originalSystemShortcutAvailable("save")) return true;
    saveCurrentDataSlot();
    return true;
  }
  if (key === "o" && !event.shiftKey) {
    if (!originalSystemShortcutAvailable("load")) return false;
    event.preventDefault();
    openLoadMenu();
    return true;
  }
  return false;
}

function handleOriginalTopMenuShortcut(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.altKey || event.metaKey || originalConfirmDialogOpen() || originalMenuInteractionBlocked()) return false;
  if (state.mode === "title") return false;
  const shortcut = event.key.length === 1 ? event.key.toUpperCase() : "";
  if (!shortcut) return false;
  const surface = originalMenuSurface();
  const definition = originalMenuButtonDefinitions.find(
    (candidate) => candidate.shortcut === shortcut && candidate.visible(surface) && !originalMenuButtonDisabled(candidate, surface)
  );
  if (!definition) return false;
  event.preventDefault();
  resumeAudio();
  activateOriginalTopMenuButton(definition.key);
  renderOriginalMenuBar();
  draw();
  return true;
}

function touchButtonRuntimeKey(rawButtonKey: string): string {
  if (state.mode === "title" && rawButtonKey === "Enter" && !originalNativeMenuOpen()) return "PointerStart";
  if (state.waitClick && rawButtonKey === "Escape") return "Enter";
  return rawButtonKey;
}

function touchMovementButtonAt(clientX: number, clientY: number): HTMLButtonElement | undefined {
  for (const button of document.querySelectorAll<HTMLButtonElement>("#touch-controls .touch-dpad button[data-key]")) {
    const key = normalizeInputKey(button.dataset.key ?? "");
    if (!isMovementKey(key)) continue;
    const rect = button.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
      return button;
    }
  }
  return undefined;
}

function wireInput(): void {
  const touchControls = document.querySelector<HTMLDivElement>("#touch-controls");
  touchControls?.addEventListener("contextmenu", (event) => {
    event.preventDefault();
  });
  window.addEventListener(
    "keydown",
    (event) => {
      resumeAudioFromUserGesture();
      if (!event.repeat) handleRuntimeCheatKey(event.key);
    },
    { capture: true }
  );
  window.addEventListener(
    "pointerdown",
    (event) => {
      resumeAudioFromUserGesture();
      handleOriginalConfirmTouchPointer(event);
    },
    { capture: true }
  );
  window.addEventListener(
    "touchstart",
    () => {
      resumeAudioFromUserGesture();
    },
    { capture: true, passive: true }
  );
  window.addEventListener("keydown", (event) => {
    const key = normalizeInputKey(event.key);
    if (originalConfirmDialogOpen()) {
      event.preventDefault();
      handleOriginalConfirmKey(event.key);
      return;
    }
    if (handleOriginalSystemShortcut(event)) return;
    if (handleOriginalTopMenuShortcut(event)) return;
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " ", "Enter", "Escape", "Tab"].includes(event.key) || isMovementKey(key)) {
      event.preventDefault();
    }
    if (originalMenuKeyboardCapturesInput()) {
      if (isMovementKey(key)) {
        heldKeyboardMovementKeys.delete(key);
        syncHeldMovementKey(key);
      }
      handleKey(event.key);
      return;
    }
    if (isMovementKey(key) && (state.mode === "map" || state.mode === "message" || Boolean(state.walking))) {
      heldKeyboardMovementKeys.add(key);
      heldMovementKeys.add(key);
      return;
    }
    handleKey(event.key);
  });
  window.addEventListener("keyup", (event) => {
    const key = normalizeInputKey(event.key);
    if (isMovementKey(key)) {
      heldKeyboardMovementKeys.delete(key);
      syncHeldMovementKey(key);
    }
  });
  canvas.addEventListener("pointerdown", (event) => {
    if (originalConfirmDialogOpen()) return;
    event.preventDefault();
    originalMouseDown = true;
    const point = canvasPoint(event);
    handleCanvasPointer(point);
    handleBattleEnemyCommandPointerClick(point, event.detail);
    clearOriginalMenuFocusFromGameInput();
  });
  canvas.addEventListener("dblclick", (event) => {
    if (originalConfirmDialogOpen()) return;
    event.preventDefault();
    handleBattleEnemyDoubleClick(canvasPoint(event));
  });
  canvas.addEventListener("pointermove", (event) => {
    if (originalConfirmDialogOpen()) return;
    if (!originalMouseDown) return;
    if (state.waitClick || originalMainStateMask() >= ORIGINAL_MAIN_STATE_START) return;
    event.preventDefault();
    handleCanvasPointer(canvasPoint(event));
  });
  window.addEventListener("pointerup", () => {
    originalMouseDown = false;
  });
  canvas.addEventListener("pointerleave", () => {
    originalMouseDown = false;
  });
  document.addEventListener("pointerdown", (event) => {
    if (originalConfirmDialogOpen()) return;
    const target = event.target;
    const insideOriginalMenuControl =
      target instanceof Node &&
      (originalMenuPopup?.contains(target) ||
        originalMenuSubPopup?.contains(target) ||
        (target instanceof HTMLButtonElement && Boolean(originalMenuBar?.contains(target))));
    if (
      target instanceof Node &&
      (insideOriginalMenuControl || canvas.contains(target) || touchControls?.contains(target))
    ) {
      return;
    }
    if (cancelOriginalBattlePopup() || cancelOriginalSystemPopup() || cancelOriginalFieldPopup()) {
      clearOriginalMenuFocusFromGameInput();
      event.preventDefault();
    }
  });
  document.querySelectorAll<HTMLButtonElement>("#touch-controls button").forEach((button) => {
    const updateTouchButtonMovement = (event: PointerEvent) => {
      if (!heldTouchMovementPointerKeys.has(event.pointerId)) return;
      event.preventDefault();
      const nextButton = touchMovementButtonAt(event.clientX, event.clientY);
      const nextKey = nextButton ? normalizeInputKey(nextButton.dataset.key ?? "") : undefined;
      setTouchMovementPointerKey(event.pointerId, isMovementKey(nextKey ?? "") && touchMovementCanUseHeldKey() ? nextKey : undefined);
    };
    const releaseTouchButtonMovement = (event: PointerEvent) => {
      releaseTouchMovementPointer(event.pointerId);
    };
    button.addEventListener("pointerdown", (event) => {
      const rawButtonKey = button.dataset.key ?? "Enter";
      if (originalConfirmDialogOpen()) {
        event.preventDefault();
        event.stopPropagation();
        handleOriginalConfirmTouchButton(rawButtonKey);
        return;
      }
      event.preventDefault();
      if (!originalMenuSelectionHighlightVisible() && originalMenuBarContainsFocus()) {
        clearOriginalMenuFocusFromGameInput(false);
      }
      const menuCapturedBeforeTouch = originalMenuKeyboardCapturesInput();
      const buttonKey = normalizeInputKey(rawButtonKey);
      if (isMovementKey(buttonKey) && touchMovementCanUseHeldKey()) {
        setTouchMovementPointerKey(event.pointerId, buttonKey);
        button.setPointerCapture(event.pointerId);
      }
      handleKey(touchButtonRuntimeKey(rawButtonKey));
      if (!menuCapturedBeforeTouch && !originalMenuSelectionHighlightVisible()) {
        clearOriginalMenuFocusFromGameInput();
      }
    });
    button.addEventListener("pointermove", updateTouchButtonMovement);
    button.addEventListener("pointerup", releaseTouchButtonMovement);
    button.addEventListener("pointercancel", releaseTouchButtonMovement);
    button.addEventListener("lostpointercapture", releaseTouchButtonMovement);
  });
}

function resumeAudioFromUserGesture(): void {
  resumeAudio();
}

function wireAudioLifecycle(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") autoSaveGame("page_deactivate");
    setPageAudioActive(document.visibilityState === "visible");
  });
  window.addEventListener("blur", () => {
    autoSaveGame("page_deactivate");
    clearHeldMovementKeys();
    originalMouseDown = false;
    setPageAudioActive(false);
  });
  window.addEventListener("focus", () => {
    setPageAudioActive(document.visibilityState === "visible");
  });
  window.addEventListener("pagehide", () => {
    autoSaveGame("page_deactivate");
    setPageAudioActive(false);
  });
  window.addEventListener("pageshow", () => {
    setPageAudioActive(document.visibilityState === "visible");
  });
}

function canvasPoint(event: Pick<MouseEvent, "clientX" | "clientY">): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: Math.floor(((event.clientX - rect.left) / rect.width) * BASE_WIDTH),
    y: Math.floor(((event.clientY - rect.top) / rect.height) * BASE_HEIGHT)
  };
}

function handleCanvasPointer(point: { x: number; y: number }): void {
  if (state.mode === "title") {
    handleKey("PointerStart");
    return;
  }
  if (state.waitClick) {
    resumeAudio();
    advanceMessage();
    return;
  }
  resumeAudio();
  if (selectActorFromStatusPointer(point)) return;
  if (cancelOriginalSystemPopup()) return;
  if (originalFieldMenuOpen) {
    cancelOriginalFieldPopup();
    if (state.mode !== "shop" && state.mode !== "inn") return;
  }
  if ((state.mode === "shop" || state.mode === "inn") && point.x > 7 && point.x < 360 && point.y > 7 && point.y < 296) {
    closeMenu();
  }
  if (state.mode === "message" && (originalMessageQueueCount() > 0 || state.timerMessageWait || state.wipe)) {
    if (point.y >= 304) clearMessageWindow();
    return;
  }
  if (state.mode === "battle" && state.battle) {
    handleBattlePointer(point);
    return;
  }
  if (state.mode === "map") {
    handleMapPointer(point);
  }
}

function handleBattlePointer(point: { x: number; y: number }): void {
  if (!BATTLE_RUNTIME_ENABLED) return;
  const battle = state.battle;
  if (!battle) return;
  if (battle.phase === "entry") return;
  if (originalBattlePopupOpen(battle)) {
    cancelOriginalBattlePopup();
    return;
  }
  if (selectActorFromStatusPointer(point)) return;
  const enemyIndex = hitEnemySprite(point);
  if (enemyIndex >= 0) {
    battle.targetEnemyIndex = enemyIndex;
    if (battle.phase === "command") {
      battle.targetEnemyIndex = enemyIndex;
    }
    return;
  }
  if (point.y < 308) return;
  if (battle.phase === "command") {
    clearMessageWindow();
    return;
  }
}

function clearBattleEnemyCommandClickCandidate(): void {
  battleEnemyCommandClickCandidate = undefined;
}

function handleBattleEnemyCommandPointerClick(point: { x: number; y: number }, browserClickCount: number): void {
  const battle = state.battle;
  if (!battle || !originalBattleCommandMenuReady()) {
    clearBattleEnemyCommandClickCandidate();
    return;
  }
  const enemyIndex = hitEnemySprite(point);
  if (enemyIndex < 0) {
    clearBattleEnemyCommandClickCandidate();
    return;
  }
  const now = performance.now();
  const previous = battleEnemyCommandClickCandidate;
  const sameCommand =
    previous?.battle === battle &&
    previous.commandActorIndex === battle.commandActorIndex &&
    now - previous.clickedAt <= BATTLE_ENEMY_DOUBLE_CLICK_WINDOW_MS;
  const commandDoubleClick = Boolean(sameCommand && previous?.enemyIndex === enemyIndex);
  battleEnemyCommandClickCandidate = { battle, commandActorIndex: battle.commandActorIndex, enemyIndex, clickedAt: now };
  if (!commandDoubleClick && !(sameCommand && browserClickCount >= 2)) return;
  clearBattleEnemyCommandClickCandidate();
  handleBattleEnemyDoubleClick(point);
}

function handleBattleEnemyDoubleClick(point: { x: number; y: number }): void {
  if (!BATTLE_RUNTIME_ENABLED) return;
  const battle = state.battle;
  if (!battle || !originalBattleCommandMenuReady()) return;
  const enemyIndex = hitEnemySprite(point);
  if (enemyIndex < 0) return;
  battle.targetEnemyIndex = enemyIndex;
  triggerOriginalFightMenu();
}

function hitEnemySprite(point: { x: number; y: number }): number {
  const battle = state.battle;
  if (!battle) return -1;
  for (let index = 0; index < battle.enemies.length; index += 1) {
    const enemy = battle.enemies[index];
    if (!enemy || enemy.currentHp <= 0) continue;
    const layout = battleEnemyLayout(index, enemy);
    if (point.x >= layout.x && point.x <= layout.x + layout.width && point.y >= layout.y && point.y <= layout.y + layout.height) return index;
  }
  return -1;
}

function handleMapPointer(point: { x: number; y: number }): void {
  if (!MAP_POINTER_MOVEMENT_ENABLED) {
    clearPointerIntent();
    return;
  }
  if (!originalPointerPathRuntimeCanQueue(state.mode, state.messages.length, Boolean(state.battle))) {
    clearPointerIntent();
    return;
  }
  if (selectActorFromStatusPointer(point)) return;
  if (point.y >= 304) {
    clearMessageWindow();
    return;
  }
  if (point.x <= 7 || point.x >= 360 || point.y <= 7 || point.y >= 296) return;
  const originX = state.x - Math.floor(VIEW_COLS / 2);
  const originY = state.y - Math.floor(VIEW_ROWS / 2);
  const tx = originX + Math.floor((point.x - MAP_X) / TILE);
  const ty = originY + Math.floor((point.y - MAP_Y) / TILE);
  if (Number.isFinite(tx) && Number.isFinite(ty)) {
    queuePointerPath(tx, ty);
  }
}

function selectActorFromStatusPointer(point: { x: number; y: number }): boolean {
  if (point.x <= 368 || point.y < 0 || point.y >= 256) return false;
  const index = Math.floor(point.y / 64);
  if ((state.party[index]?.state ?? 0) > 0) {
    if (point.x < 440 && originalMainStateMask() === ORIGINAL_MAIN_STATE_WALK) {
      if (originalSourceActorIndex() !== index) {
        changeOriginalSourceActor(index);
      }
      return true;
    }
    if (state.targetActorIndex !== index) {
      changeOriginalTargetActor(index);
    }
    return true;
  }
  return false;
}

function changeOriginalSourceActor(index: number): void {
  if ((state.party[index]?.state ?? 0) <= 0) return;
  state.sourceActorIndex = index;
  refreshActorDependentMenu();
}

function forceOriginalSourceActor(index: number): void {
  state.sourceActorIndex = clamp(index, 0, 3);
  refreshActorDependentMenu();
}

function changeOriginalTargetActor(index: number): void {
  if ((state.party[index]?.state ?? 0) <= 0) return;
  state.targetActorIndex = index;
}

function fChangeOriginalActors(): void {
  const index = firstOriginalExistingActorIndex();
  if ((state.party[index]?.state ?? 0) > 0) {
    state.sourceActorIndex = index;
    state.targetActorIndex = index;
    refreshActorDependentMenu();
  }
}

function pollGamepad(): void {
  if (!("getGamepads" in navigator)) return;
  const now = performance.now();
  if (now < gamepadCooldownUntil) return;
  const pad = navigator.getGamepads().find((candidate) => candidate?.connected);
  if (!pad) return;
  const axisX = pad.axes[0] ?? 0;
  const axisY = pad.axes[1] ?? 0;
  let key = "";
  if (pad.buttons[0]?.pressed) key = "Enter";
  else if (pad.buttons[1]?.pressed || pad.buttons[9]?.pressed) key = "Escape";
  else if (state.battle && pad.buttons[4]?.pressed) key = "PrevEnemyTarget";
  else if (state.battle && pad.buttons[5]?.pressed) key = "NextEnemyTarget";
  else if (pad.buttons[4]?.pressed) key = "PrevActor";
  else if (pad.buttons[5]?.pressed) key = "NextActor";
  else if (pad.buttons[6]?.pressed) key = "PrevTargetActor";
  else if (pad.buttons[7]?.pressed) key = "NextTargetActor";
  else if (pad.buttons[12]?.pressed || axisY < -0.55) key = "ArrowUp";
  else if (pad.buttons[13]?.pressed || axisY > 0.55) key = "ArrowDown";
  else if (pad.buttons[14]?.pressed || axisX < -0.55) key = "ArrowLeft";
  else if (pad.buttons[15]?.pressed || axisX > 0.55) key = "ArrowRight";
  if (!key) return;
  handleKey(key);
  gamepadCooldownUntil = now + 150;
}

function advanceKeyboardMovement(): void {
  const now = performance.now();
  if (now < nextKeyCheckAt) return;
  nextKeyCheckAt = now + KEY_CHECK_INTERVAL_MS;
  if (originalMenuKeyboardCapturesInput()) return;
  if (
    state.mode !== "map" ||
    state.walking ||
    state.forcedWalk ||
    state.wipe ||
    state.waitClick ||
    originalMessageQueueCount() > 0 ||
    state.timerMessageWait
  ) {
    return;
  }
  const keys = [...heldMovementKeys].filter(isMovementKey);
  if (keys.length !== 1) return;
  const movementIntent = originalKeyboardMovementIntent(keys[0], state.x, state.y, currentMap().width, currentMap().height);
  if (!movementIntent) return;
  clearPointerIntent();
  tryMove(movementIntent.dx, movementIntent.dy, movementIntent.direction);
}

function isMovementKey(key: string): boolean {
  return key === "ArrowUp" || key === "ArrowDown" || key === "ArrowLeft" || key === "ArrowRight";
}

function syncHeldMovementKey(key: string): void {
  if (heldKeyboardMovementKeys.has(key) || heldTouchMovementKeys.has(key)) {
    heldMovementKeys.add(key);
  } else {
    heldMovementKeys.delete(key);
  }
}

function syncTouchMovementKey(key: string): void {
  if (!isMovementKey(key)) return;
  let touchHeld = false;
  for (const pointerKey of heldTouchMovementPointerKeys.values()) {
    if (pointerKey === key) {
      touchHeld = true;
      break;
    }
  }
  if (touchHeld) {
    heldTouchMovementKeys.add(key);
    heldMovementKeys.add(key);
  } else {
    heldTouchMovementKeys.delete(key);
    syncHeldMovementKey(key);
  }
}

function clearHeldMovementKeys(): void {
  heldMovementKeys.clear();
  heldKeyboardMovementKeys.clear();
  heldTouchMovementKeys.clear();
  heldTouchMovementPointerKeys.clear();
}

function touchMovementCanUseHeldKey(): boolean {
  return !originalMenuKeyboardCapturesInput() && (state.mode === "map" || state.mode === "message" || Boolean(state.walking));
}

function pressTouchMovementKey(key: string): void {
  if (!isMovementKey(key)) return;
  heldTouchMovementKeys.add(key);
  heldMovementKeys.add(key);
}

function setTouchMovementPointerKey(pointerId: number, key: string | undefined): void {
  if (key !== undefined && !isMovementKey(key)) return;
  const hadPointer = heldTouchMovementPointerKeys.has(pointerId);
  const previousKey = heldTouchMovementPointerKeys.get(pointerId);
  if (hadPointer && previousKey === key) return;
  heldTouchMovementPointerKeys.set(pointerId, key);
  if (previousKey) syncTouchMovementKey(previousKey);
  if (key) pressTouchMovementKey(key);
}

function releaseTouchMovementPointer(pointerId: number): void {
  const previousKey = heldTouchMovementPointerKeys.get(pointerId);
  const hadPointer = heldTouchMovementPointerKeys.delete(pointerId);
  if (hadPointer && previousKey) syncTouchMovementKey(previousKey);
}

function releaseTouchMovementKey(key: string): void {
  if (!isMovementKey(key)) return;
  for (const [pointerId, pointerKey] of heldTouchMovementPointerKeys) {
    if (pointerKey === key) heldTouchMovementPointerKeys.set(pointerId, undefined);
  }
  heldTouchMovementKeys.delete(key);
  syncHeldMovementKey(key);
}

function originalSourceActorKeyCanChange(): boolean {
  return state.started && !state.walking && !state.battle;
}

function originalTargetActorKeyCanChange(): boolean {
  return state.started;
}

function handleOriginalActorSelectionKey(key: string): boolean {
  if (key === "PrevActor" || key === "NextActor") {
    if (!originalSourceActorKeyCanChange()) return false;
    rotateVisibleActor(key === "PrevActor" ? -1 : 1);
    return true;
  }
  if (key === "PrevTargetActor" || key === "NextTargetActor") {
    if (!originalTargetActorKeyCanChange()) return false;
    rotateTargetActor(key === "PrevTargetActor" ? -1 : 1);
    return true;
  }
  return false;
}

function handleKey(key: string): void {
  const rawKey = key;
  key = normalizeInputKey(key);
  if (originalConfirmDialogOpen()) {
    handleOriginalConfirmKey(rawKey);
    return;
  }
  const holdTitleForAudio = shouldHoldTitleForAudioUnlock(rawKey);
  resumeAudio();

  if (isHelpActivationKey(rawKey)) {
    openHelp();
    return;
  }

  if (state.mode === "title") {
    if (holdTitleForAudio) return;
    if (originalNativeMenuOpen()) {
      if (originalMenuInteractionBlocked()) {
        cancelOriginalSystemPopup(false);
        cancelOriginalFieldPopup(false);
        renderOriginalMenuBar();
        draw();
        return;
      }
      if (handleOriginalNativeMenuKey(key)) return;
    }
    if (handleOriginalTitleMenuKey(key)) return;
    if (isOriginalTitleActivationKey(rawKey)) {
      playSe("Beep");
      continueFromLatestAutoSaveOrNewGame();
    }
    if (key === "l" || key === "L") openLoadMenu();
    return;
  }
  if (originalMenuInteractionBlocked()) {
    if (originalSystemMenuOpen || originalFieldMenuOpen) {
      cancelOriginalSystemPopup(false);
      cancelOriginalFieldPopup(false);
      renderOriginalMenuBar();
      draw();
    }
  } else if (originalMenuKeyboardCapturesInput()) {
    if (handleOriginalNativeMenuKey(key)) return;
  }
  if (handleOriginalActorSelectionKey(key)) return;
  if (state.wipe) return;
  if (key === "Delete") {
    clearMessageWindow();
    return;
  }
  if (state.waitClick) {
    if (key === "Enter") advanceMessage();
    return;
  }
  if (state.mode === "message" && (originalMessageQueueCount() > 0 || state.timerMessageWait || state.wipe)) {
    return;
  }
  if (state.mode === "battle") {
    handleBattleKey(key);
    return;
  }
  if (state.mode === "map") {
    if (state.walking) return;
    if (key.startsWith("Arrow") || key === "Enter" || key === "Escape") clearPointerIntent();
    const movementIntent = originalKeyboardMovementIntent(key, state.x, state.y, currentMap().width, currentMap().height);
    if (movementIntent) tryMove(movementIntent.dx, movementIntent.dy, movementIntent.direction);
    else if (key === "Enter") interact();
    else if (key === "Escape") openMainMenu();
  }
}

function rotateVisibleActor(direction: number): void {
  const active = state.party.map((actor, index) => ({ actor, index })).filter(({ actor }) => actor.state > 0);
  if (!active.length) return;
  const current = active.findIndex(({ index }) => index === originalSourceActorIndex());
  const next = active[(current + direction + active.length) % active.length] ?? active[0];
  changeOriginalSourceActor(next.index);
}

function rotateTargetActor(direction: number): void {
  const active = state.party.map((actor, index) => ({ actor, index })).filter(({ actor }) => actor.state > 0);
  if (!active.length) return;
  const current = active.findIndex(({ index }) => index === targetActorIndex());
  const next = active[(current + direction + active.length) % active.length] ?? active[0];
  changeOriginalTargetActor(next.index);
}

function handleRuntimeCheatKey(rawKey: string): void {
  handleTargetActorExpCheatKey(rawKey);
  handleMoneyCheatKey(rawKey);
}

function handleTargetActorExpCheatKey(rawKey: string): void {
  if (rawKey.length !== 1 || !/[a-z]/i.test(rawKey)) {
    targetActorExpCheatProgress = "";
    return;
  }
  const key = rawKey.toLowerCase();
  const nextProgress = targetActorExpCheatProgress + key;
  targetActorExpCheatProgress = TARGET_ACTOR_EXP_CHEAT_CODE.startsWith(nextProgress)
    ? nextProgress
    : TARGET_ACTOR_EXP_CHEAT_CODE.startsWith(key)
      ? key
      : "";
  if (targetActorExpCheatProgress !== TARGET_ACTOR_EXP_CHEAT_CODE) return;
  targetActorExpCheatProgress = "";
  grantTargetActorLevelExperience(TARGET_ACTOR_EXP_CHEAT_LEVELS);
}

function grantTargetActorLevelExperience(levels: number): void {
  const actor = state.party[targetActorIndex()];
  if (!actor || actor.state <= 0) return;
  for (let count = 0; count < levels && actor.level < 50 && actor.nextExp > 0; count += 1) {
    const amountToNextLevel = Math.max(0, actor.nextExp - actor.experience);
    gainExp(actor, amountToNextLevel);
  }
}

function handleMoneyCheatKey(rawKey: string): void {
  if (rawKey.length !== 1 || !/[a-z]/i.test(rawKey)) {
    moneyCheatProgress = "";
    return;
  }
  const key = rawKey.toLowerCase();
  const nextProgress = moneyCheatProgress + key;
  moneyCheatProgress = MONEY_CHEAT_CODE.startsWith(nextProgress)
    ? nextProgress
    : MONEY_CHEAT_CODE.startsWith(key)
      ? key
      : "";
  if (moneyCheatProgress !== MONEY_CHEAT_CODE) return;
  moneyCheatProgress = "";
  state.money = clamp(state.money + MONEY_CHEAT_AMOUNT, 0, 9999999);
}

function refreshActorDependentMenu(): void {
  if (originalFieldMenuOpen) {
    renderOriginalMenuBar();
  }
}

function handleOriginalNativeMenuKey(key: string): boolean {
  key = normalizeOriginalMenuKey(key);
  if (originalSystemMenuOpen) return handleOriginalSystemMenuKey(key);
  if (originalFieldMenuOpen) return handleOriginalFieldMenuKey(key);
  if (originalBattlePopupOpen()) return handleOriginalBattlePopupMenuKey(key);
  if (state.battle && originalBattleNativeMenuReady()) return handleOriginalMenuBarKey(key);
  if ((state.mode === "shop" || state.mode === "inn") && originalMenuBarTracking) return handleOriginalMenuBarKey(key);
  if (originalMenuBarContainsFocus()) return handleOriginalMenuBarKey(key);
  return false;
}

function handleOriginalTitleMenuKey(key: string): boolean {
  if (state.mode !== "title" || state.originalMenuMode !== "first" || !originalMenuSelectionHighlightVisible()) return false;
  key = normalizeOriginalMenuKey(key);
  if (key === "ArrowLeft") return moveOriginalTopMenuSelection(-1);
  if (key === "ArrowRight") return moveOriginalTopMenuSelection(1);
  if (key === "ArrowUp" || key === "ArrowDown") return true;
  if (key !== "Enter") return false;
  const activeKey = currentOriginalMenuBarKey() ?? preferredOriginalTitleMenuKey();
  const definition = originalMenuButtonDefinitions.find((candidate) => candidate.key === activeKey);
  const surface = originalMenuSurface();
  if (!definition || !definition.visible(surface) || originalMenuButtonDisabled(definition, surface)) return true;
  originalMenuBarTracking = true;
  if (activeKey === "titleStart" || activeKey === "titleContinue") playSe("Beep");
  activateOriginalTopMenuButton(activeKey);
  renderOriginalMenuBar();
  draw();
  return true;
}

function handleOriginalSystemMenuKey(key: string): boolean {
  const rootRows = originalSystemMenuRows();
  const subRows = originalSystemSubmenuOpen ? originalSystemSubPopupRows() : undefined;
  if (key === "ArrowRight") return moveOriginalTopMenuSelection(1);
  if (key === "ArrowLeft") return moveOriginalTopMenuSelection(-1);
  if (key === "Escape") {
    if (originalSystemSubmenuOpen) {
      originalSystemSubmenuOpen = undefined;
      renderOriginalMenuBar();
    } else {
      closeOriginalNativePopupToTopMenu();
    }
    return true;
  }
  if (subRows) {
    if (key === "ArrowUp" || key === "ArrowDown") {
      originalSystemSubmenuSelected = moveOriginalPopupSelection(subRows, originalSystemSubmenuSelected, key === "ArrowDown" ? 1 : -1);
      renderOriginalMenuBar();
      return true;
    }
    if (key === "Enter") {
      const row = subRows[originalSystemSubmenuSelected];
      if (!row?.disabled) row?.action();
      return true;
    }
    return true;
  }
  if (key === "ArrowUp" || key === "ArrowDown") {
    originalSystemMenuSelected = moveOriginalPopupSelection(rootRows, originalSystemMenuSelected, key === "ArrowDown" ? 1 : -1);
    renderOriginalMenuBar();
    return true;
  }
  if (key === "Enter" && rootRows[originalSystemMenuSelected]?.submenuMode) {
    activateOriginalSystemPopupRow(rootRows[originalSystemMenuSelected], originalSystemMenuSelected);
    return true;
  }
  if (key === "Enter") {
    activateOriginalSystemPopupRow(rootRows[originalSystemMenuSelected], originalSystemMenuSelected);
    return true;
  }
  return true;
}

function handleOriginalFieldMenuKey(key: string): boolean {
  const rootRows = originalFieldRootPopupRows();
  const leafRows = originalFieldLeafPopupRows();
  if (key === "ArrowRight") return moveOriginalTopMenuSelection(1);
  if (key === "ArrowLeft") return moveOriginalTopMenuSelection(-1);
  if (key === "Escape") {
    if (state.mode === "shop" && leaveOriginalMenuUsingToWalk()) return true;
    if (leafRows) {
      originalFieldSubmenuOpen = undefined;
      renderOriginalMenuBar();
    } else {
      closeOriginalNativePopupToTopMenu();
    }
    return true;
  }
  if (leafRows) {
    if (key === "ArrowUp" || key === "ArrowDown") {
      originalFieldLeafSelected = moveOriginalPopupSelection(leafRows, originalFieldLeafSelected, key === "ArrowDown" ? 1 : -1);
      renderOriginalMenuBar();
      return true;
    }
    if (key === "Enter") {
      leafRows[originalFieldLeafSelected]?.action();
      return true;
    }
    return true;
  }
  if (!rootRows.length) return true;
  if (key === "ArrowUp" || key === "ArrowDown") {
    originalFieldRootSelected = moveOriginalPopupSelection(rootRows, originalFieldRootSelected, key === "ArrowDown" ? 1 : -1);
    renderOriginalMenuBar();
    return true;
  }
  if (key === "Enter") {
    activateOriginalFieldPopupRow(rootRows[originalFieldRootSelected], originalFieldRootSelected, "root");
    return true;
  }
  return true;
}

function handleOriginalBattlePopupMenuKey(key: string): boolean {
  const battle = state.battle;
  if (!battle || !originalBattlePopupOpen(battle)) return false;
  if (key === "ArrowRight") return moveOriginalTopMenuSelection(1);
  if (key === "ArrowLeft") return moveOriginalTopMenuSelection(-1);
  if (key === "Escape") {
    if (returnOriginalBattlePopupToRoot(battle)) return true;
    return closeOriginalNativePopupToTopMenu();
  }
  const leafRows = battleLeafPopupRows(battle);
  if (leafRows === undefined) {
    const rootRows = battleRootPopupRows(battle);
    if (!rootRows.length) return true;
    const nextIndex = moveOriginalPopupSelection(rootRows, battleRootSelectedIndex(battle, rootRows.length), key === "ArrowDown" ? 1 : -1);
    if (key === "ArrowUp" || key === "ArrowDown") {
      battle.battleRootSelected = nextIndex;
      battle.battlePopupFocus = "root";
      renderOriginalMenuBar();
      draw();
      return true;
    }
    if (key === "Enter") {
      openSelectedOriginalRootSubmenu(battle);
      return true;
    }
    return false;
  }
  const visibleLeafRows = leafRows.length
    ? leafRows
    : [{ label: battle.phase === "item" ? gameText("source.main.9033cf96") : gameText("source.main.67c0fa80"), action: () => undefined }];
  if (key === "ArrowUp" || key === "ArrowDown") {
    battle.battleLeafSelected = moveOriginalPopupSelection(visibleLeafRows, battle.battleLeafSelected, key === "ArrowDown" ? 1 : -1);
    battle.battlePopupFocus = "leaf";
    renderOriginalMenuBar();
    draw();
    return true;
  }
  if (key === "Enter") {
    const index = battle.battleLeafSelected % visibleLeafRows.length;
    activateOriginalPopupRow(battle, visibleLeafRows[index], index, "leaf");
    return true;
  }
  return false;
}

function normalizeInputKey(key: string): string {
  if (key === "PointerStart") return key;
  if (key === " ") return "Enter";
  if (key === "q" || key === "Q") return "PrevEnemyTarget";
  if (key === "e" || key === "E") return "NextEnemyTarget";
  if (key === "w" || key === "W") return "ArrowUp";
  if (key === "a" || key === "A") return "ArrowLeft";
  if (key === "s" || key === "S") return "ArrowDown";
  if (key === "d" || key === "D") return "ArrowRight";
  if (key === "z" || key === "Z") return "NextActor";
  if (key === "x" || key === "X") return "NextTargetActor";
  return key;
}

function normalizeOriginalMenuKey(key: string): string {
  return key === "PointerStart" ? "Enter" : key;
}

function isOriginalTitleActivationKey(rawKey: string): boolean {
  return rawKey === " " || rawKey === "Enter" || rawKey === "PointerStart";
}

function isHelpActivationKey(rawKey: string): boolean {
  return rawKey === "h" || rawKey === "H";
}

function shouldHoldTitleForAudioUnlock(rawKey: string): boolean {
  if (state.mode !== "title" || !isOriginalTitleActivationKey(rawKey)) return false;
  if (originalNativeMenuOpen()) return false;
  if (!BGM_PLAYBACK_ENABLED || !state.settings.bgmOn) return false;
  if (requestedBgm !== TITLE_BGM || currentBgm?.source) return false;
  return !audioContext || audioContext.state !== "running";
}

function handleBattleKey(key: string): void {
  if (!BATTLE_RUNTIME_ENABLED) return;
  const battle = state.battle;
  if (!battle) return;
  if (battle.phase === "entry") return;
  if (key === "PrevEnemyTarget" || key === "NextEnemyTarget") {
    selectNextLivingEnemy(key === "PrevEnemyTarget" ? -1 : 1, battle.targetEnemyIndex);
    draw();
    return;
  }
}

function newGame(): void {
  const shouldWipeOut = state.started;
  const initial = makeInitialState();
  Object.assign(state, initial);
  resetOriginalMapDrawTrace();
  invalidateOriginalMapCache();
  state.mode = "map";
  state.started = true;
  fChangeOriginalActors();
  setOriginalMenuMode("walk");
  trackEvent("game_start", { source: "new_game" });
  playBgm(state.bgm);
  executeOriginalLoadMpdSequence(shouldWipeOut ? ["WipeOut"] : [], ["WipeIn"]);
  executeEventScript(DATA.defaultSave.openingScript, { checkMode: false });
}

function currentMap(): MapData {
  return maps.get(state.mapId) ?? DATA.maps[0];
}

function tryMove(
  dx: number,
  dy: number,
  direction: GameState["direction"],
  options: { suppressRandomEncounter?: boolean } = {}
): void {
  if (state.walking) return;
  const map = currentMap();
  const plan = originalTryMovePlan({ x: state.x, y: state.y, dx, dy, mapWidth: map.width, mapHeight: map.height });
  const stepFrame = originalWalkingStepFrame(1, plan.moveX, plan.moveY);
  void stepFrame;
  state.direction = plan.direction ?? direction;
  if (!MAP_MOVEMENT_ENABLED) return;
  const nextX = plan.nextX;
  const nextY = plan.nextY;
  const targetTile = tileCollisionState(nextX, nextY);
  if (!targetTile.enterable) {
    runBlockedWalkStartEvent(nextX, nextY, targetTile);
    return;
  }
  const now = performance.now();
  state.walking = {
    fromX: state.x,
    fromY: state.y,
    toX: nextX,
    toY: nextY,
    moveX: plan.moveX,
    moveY: plan.moveY,
    direction: plan.direction ?? direction,
    moveCount: 0,
    startedAt: now,
    durationMs: WALK_SCROLL_DURATION_MS,
    nextFrameAt: now + WALK_LOGIC_STEP_INTERVAL_MS,
    suppressRandomEncounter: Boolean(options.suppressRandomEncounter)
  };
}

function canEnterTile(x: number, y: number): boolean {
  return tileCollisionState(x, y).enterable;
}

function tileCollisionState(x: number, y: number): TileCollisionState {
  const map = currentMap();
  const normalizedX = wrapMapCoordinate(x, map.width);
  const normalizedY = wrapMapCoordinate(y, map.height);
  const eventIndex = map.events.findIndex((candidate) => candidate.x === normalizedX && candidate.y === normalizedY);
  const event = eventIndex >= 0 ? map.events[eventIndex] : undefined;
  const collision = originalCollisionDecision({
    normalizedTile: tileIndexFor(state.mapId, normalizedX, normalizedY),
    eventIndex: eventIndex >= 0 ? eventIndex : null
  });
  const tilePassable = originalTilePassability(collision.normalizedTile);
  const blockedEvent = collision.eventActivationDecoded && Boolean(event) && !collision.tilePassable;
  const enterable = MAP_TILE_PASSABILITY_ENABLED
    ? UNVERIFIED_WEB_SOLID_RANGE_ENABLED
      ? !UNVERIFIED_WEB_SOLID_TILE_INDEXES.has(collision.normalizedTile)
      : !blockedEvent && tilePassable
    : false;
  return {
    normalizedX,
    normalizedY,
    event,
    eventIndex,
    tilePassable,
    enterable,
    blockedEvent
  };
}

function runBlockedWalkStartEvent(x: number, y: number, before: TileCollisionState): boolean {
  if (!MAP_EVENT_ACTIVATION_ENABLED || !before.blockedEvent || !before.event) return false;
  const fired = runEventAt(x, y, false);
  if (!fired) return false;
  clearPointerIntent();
  // WalkStart re-reads GetMPD after Event False, but the current movement attempt still ends here.
  void tileCollisionState(x, y);
  return true;
}

function clearPointerIntent(): void {
  state.pointerPath = [];
  state.pointerTarget = undefined;
}

function queuePointerPath(targetX: number, targetY: number): void {
  if (targetX === state.x && targetY === state.y) {
    clearPointerIntent();
    return;
  }
  state.pointerTarget = { x: targetX, y: targetY };
  state.pointerPath = [];
  pointerStepCooldownUntil = 0;
  advancePointerPath();
}

function isBlockedEventTile(x: number, y: number): boolean {
  const map = currentMap();
  const normalizedX = wrapMapCoordinate(x, map.width);
  const normalizedY = wrapMapCoordinate(y, map.height);
  const event = map.events.find((candidate) => candidate.x === normalizedX && candidate.y === normalizedY);
  return Boolean(event) && !canEnterTile(x, y);
}

function originalPointerWalkStartStep(x: number, y: number, targetX: number, targetY: number): PointerWalkStep | undefined {
  const preferX = Math.abs(targetX - x) >= Math.abs(targetY - y);
  const firstAxis = preferX ? "x" : "y";
  const secondAxis = preferX ? "y" : "x";
  const first = pointerWalkStartAxisStep(firstAxis, x, y, targetX, targetY, 0);
  if (first) return first;
  return pointerWalkStartAxisStep(secondAxis, x, y, targetX, targetY, firstAxis === "x" ? 1 : 2);
}

function pointerWalkStartAxisStep(
  axis: "x" | "y",
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  tried: number
): PointerWalkStep | undefined {
  const map = currentMap();
  const deltaX = axis === "x" ? (x < targetX ? 1 : -1) : 0;
  const deltaY = axis === "y" ? (y < targetY ? 1 : -1) : 0;
  if ((axis === "x" && x === targetX) || (axis === "y" && y === targetY)) return undefined;
  const nextX = wrapMapCoordinate(x + deltaX, map.width);
  const nextY = wrapMapCoordinate(y + deltaY, map.height);
  const next = { x: nextX, y: nextY };
  if (canEnterTile(next.x, next.y)) return { x: next.x, y: next.y, enterable: true };
  if (isBlockedEventTile(next.x, next.y)) return { x: next.x, y: next.y, enterable: false };
  const triedMask = tried | (axis === "x" ? 1 : 2);
  const blockedByTargetAxis = axis === "x" ? y === targetY : x === targetX;
  if (blockedByTargetAxis || triedMask === 3) return undefined;
  const fallbackAxis = axis === "x" ? "y" : "x";
  return pointerWalkStartAxisStep(fallbackAxis, x, y, targetX, targetY, triedMask);
}

function advancePointerPath(): void {
  if (state.forcedWalk) {
    clearPointerIntent();
    return;
  }
  if (state.wipe) return;
  if (state.walking) return;
  const target = state.pointerTarget;
  if (!target) return;
  if (target.x === state.x && target.y === state.y) {
    clearPointerIntent();
    return;
  }
  if (originalPointerPathRuntimeShouldInterrupt(state.mode, state.messages.length, Boolean(state.battle))) {
    clearPointerIntent();
    return;
  }
  const now = performance.now();
  if (now < pointerStepCooldownUntil) return;
  const step = originalPointerWalkStartStep(state.x, state.y, target.x, target.y);
  if (!step) {
    clearPointerIntent();
    return;
  }
  state.pointerPath = [{ x: step.x, y: step.y }];
  const map = currentMap();
  const dx = coordinateStepDelta(state.x, step.x, map.width);
  const dy = coordinateStepDelta(state.y, step.y, map.height);
  const direction = directionFromStepDelta(dx, dy, state.direction);
  tryMove(dx, dy, direction);
  if (!step.enterable) clearPointerIntent();
  if (originalPointerPathRuntimeShouldInterrupt(state.mode, state.messages.length, Boolean(state.battle))) {
    clearPointerIntent();
  }
  pointerStepCooldownUntil = now + POINTER_STEP_INTERVAL_MS;
}

function advanceOriginalForcedWalk(): void {
  const forced = state.forcedWalk;
  if (!forced) return;
  if (
    state.mode !== "map" ||
    state.battle ||
    state.walking ||
    state.wipe ||
    state.waitClick ||
    state.timerMessageWait ||
    originalMessageQueueCount() > 0
  ) {
    return;
  }
  if (state.x === forced.targetX && state.y === forced.targetY) {
    state.forcedWalk = undefined;
    return;
  }
  const step = originalPointerWalkStartStep(state.x, state.y, forced.targetX, forced.targetY);
  if (!step) {
    state.forcedWalk = undefined;
    return;
  }
  const map = currentMap();
  const dx = coordinateStepDelta(state.x, step.x, map.width);
  const dy = coordinateStepDelta(state.y, step.y, map.height);
  const direction = directionFromStepDelta(dx, dy, state.direction);
  clearPointerIntent();
  tryMove(dx, dy, direction, { suppressRandomEncounter: true });
  if (!step.enterable) state.forcedWalk = undefined;
}

function coordinateStepDelta(from: number, to: number, size: number): number {
  if (to === from) return 0;
  if (to === wrapMapCoordinate(from + 1, size)) return 1;
  if (to === wrapMapCoordinate(from - 1, size)) return -1;
  return to > from ? 1 : -1;
}

function directionFromStepDelta(dx: number, dy: number, fallback: GameState["direction"]): GameState["direction"] {
  if (dx < 0) return "left";
  if (dx > 0) return "right";
  if (dy < 0) return "up";
  if (dy > 0) return "down";
  return fallback;
}

function advanceWalking(): void {
  if (state.wipe) return;
  const walking = state.walking;
  if (!walking) return;
  const now = performance.now();
  if (now < walking.nextFrameAt) return;
  const dueMoveCount = Math.min(
    ORIGINAL_WALKING_SUB_STEPS,
    Math.floor((now - walking.startedAt) / WALK_LOGIC_STEP_INTERVAL_MS)
  );
  for (let moveCount = walking.moveCount + 1; moveCount <= dueMoveCount; moveCount += 1) {
    originalWalkingStepFrame(moveCount, walking.moveX, walking.moveY);
  }
  walking.moveCount = Math.max(walking.moveCount, dueMoveCount);
  walking.nextFrameAt = walking.startedAt + (walking.moveCount + 1) * WALK_LOGIC_STEP_INTERVAL_MS;
  if (walking.moveCount > 3) completeWalking(walking);
}

function advanceWipe(): void {
  const wipe = state.wipe;
  if (!wipe) return;
  const now = performance.now();
  if (now < wipe.nextFrameAt) return;
  const dueCount = Math.min(wipe.duration, Math.floor((now - wipe.startedAt) / WIPE_LOGIC_STEP_INTERVAL_MS));
  wipe.count = Math.max(wipe.count, dueCount);
  wipe.nextFrameAt = wipe.startedAt + (wipe.count + 1) * WIPE_LOGIC_STEP_INTERVAL_MS;
  if (now <= wipe.startedAt + wipe.durationMs) return;
  const onDone = wipe.onDone;
  state.wipe = undefined;
  onDone?.();
}

function advanceTimerMessageWait(): void {
  const wait = state.timerMessageWait;
  if (!wait || performance.now() < wait.until) return;
  state.timerMessageWait = undefined;
  state.messages = [];
  wait.action();
  if (
    state.mode === "message" &&
    state.messages.length === 0 &&
    !state.waitClick &&
    !state.timerMessageWait
  ) {
    state.mode = state.battle ? "battle" : state.previousMode === "title" ? "map" : state.previousMode;
  }
}

function markOriginalBattleTurnFlag(battle = state.battle): void {
  if (battle) battle.turnPending = true;
}

function clearOriginalBattleTurnFlag(battle = state.battle): void {
  if (battle) battle.turnPending = false;
}

function originalBattleTurnFlagCanAdvance(): boolean {
  const battle = state.battle;
  return Boolean(
    battle?.turnPending &&
      !state.timerMessageWait &&
      !state.waitClick &&
      state.messages.length === 0 &&
      originalMessageQueueCount() === 0 &&
      !state.wipe
  );
}

function advanceOriginalBattleTurnFlag(): void {
  const battle = state.battle;
  if (!battle || !originalBattleTurnFlagCanAdvance()) return;
  battle.turnPending = false;
  continueBattleAfterAction();
}

function completeWalking(walking: WalkingState): void {
  state.x = walking.toX;
  state.y = walking.toY;
  state.walking = undefined;
  updateOriginalMapCacheAfterMove(walking.moveX, walking.moveY);
  adjustOriginalForcedWalkTargetAfterWrap(walking);
  adjustOriginalPointerTargetAfterWrap(walking);
  const eventResult = runEventAtWithResult(state.x, state.y, false);
  if (
    eventResult.queuedMessage &&
    (state.mode === "message" || state.waitClick || state.timerMessageWait || originalMessageQueueCount() > 0)
  ) {
    clearPointerIntent();
  }
  if (
    eventResult.fired &&
    state.forcedWalk &&
    (state.mode === "message" || state.waitClick || state.timerMessageWait || originalMessageQueueCount() > 0)
  ) {
    state.forcedWalk = undefined;
  }
  if (state.forcedWalk && state.x === state.forcedWalk.targetX && state.y === state.forcedWalk.targetY) {
    state.forcedWalk = undefined;
  }
  if (
    !walking.suppressRandomEncounter &&
    !eventResult.queuedMessage &&
    state.mode === "map" &&
    state.messages.length === 0 &&
    originalMessageQueueCount() === 0
  ) {
    maybeRandomBattle();
  }
}

function adjustOriginalForcedWalkTargetAfterWrap(walking: WalkingState): void {
  const forced = state.forcedWalk;
  if (!forced) return;
  const map = currentMap();
  if (walking.moveX > 0 && walking.fromX === map.width - 1 && walking.toX === 0) forced.targetX -= map.width;
  if (walking.moveX < 0 && walking.fromX === 0 && walking.toX === map.width - 1) forced.targetX += map.width;
  if (walking.moveY > 0 && walking.fromY === map.height - 1 && walking.toY === 0) forced.targetY -= map.height;
  if (walking.moveY < 0 && walking.fromY === 0 && walking.toY === map.height - 1) forced.targetY += map.height;
}

function adjustOriginalPointerTargetAfterWrap(walking: WalkingState): void {
  const target = state.pointerTarget;
  if (!target) return;
  const map = currentMap();
  if (walking.moveX > 0 && walking.fromX === map.width - 1 && walking.toX === 0) target.x -= map.width;
  if (walking.moveX < 0 && walking.fromX === 0 && walking.toX === map.width - 1) target.x += map.width;
  if (walking.moveY > 0 && walking.fromY === map.height - 1 && walking.toY === 0) target.y -= map.height;
  if (walking.moveY < 0 && walking.fromY === 0 && walking.toY === map.height - 1) target.y += map.height;
}

function interact(): void {
  if (!EVENT_COMMANDS_ENABLED) return;
  if (runEventAt(state.x, state.y, true)) return;
  executeTokens([printToken("source.main.0d099555"), "Click"], { checkMode: true, eventX: state.x, eventY: state.y });
}

function directionDelta(): [number, number] {
  if (state.direction === "up") return [0, -1];
  if (state.direction === "down") return [0, 1];
  if (state.direction === "left") return [-1, 0];
  return [1, 0];
}

function originalFacingEventContext(): EventScriptContext {
  const map = currentMap();
  const [dx, dy] = directionDelta();
  return {
    checkMode: false,
    eventX: wrapMapCoordinate(state.x + dx, map.width),
    eventY: wrapMapCoordinate(state.y + dy, map.height)
  };
}

function runEventAt(x: number, y: number, checkMode: boolean): boolean {
  return runEventAtWithResult(x, y, checkMode).fired;
}

function runEventAtWithResult(x: number, y: number, checkMode: boolean): OriginalEventRunResult {
  if (!EVENT_COMMANDS_ENABLED) return { fired: false, queuedMessage: false };
  const map = currentMap();
  const eventX = wrapMapCoordinate(x, map.width);
  const eventY = wrapMapCoordinate(y, map.height);
  const event = map.events.find((candidate) => candidate.x === eventX && candidate.y === eventY);
  if (!event) return { fired: false, queuedMessage: false };
  const queuedMessage = executeEventScript(event.script, { checkMode, eventX, eventY }) > 0;
  return { fired: true, queuedMessage };
}

function currentMapStartupEvent(): MapEvent | undefined {
  const defaultEvents = currentMap().events.filter((candidate) => candidate.y < 0);
  return defaultEvents[defaultEvents.length - 1];
}

function compileCurrentMapStartupTokens(): { tokens: string[]; context: EventScriptContext } | undefined {
  if (!EVENT_COMMANDS_ENABLED) return undefined;
  const event = currentMapStartupEvent();
  if (!event) return undefined;
  const context: EventScriptContext = { checkMode: false, eventX: event.x, eventY: event.y, defaultMessageProcessing: true };
  return { tokens: compileOriginalEventMessageQueue(tokenize(event.script), context), context };
}

function executeOriginalLoadMpdSequence(prefixTokens: string[] = [], suffixTokens: string[] = []): void {
  const startup = compileCurrentMapStartupTokens();
  executeTokens(
    [...prefixTokens, ...(startup?.tokens ?? []), ...suffixTokens],
    startup?.context ?? { checkMode: false }
  );
}

function runMapStartup(): void {
  const startup = compileCurrentMapStartupTokens();
  if (startup) executeTokens(startup.tokens, startup.context);
}

function executeEventScript(script: string, context: EventScriptContext): number {
  if (!EVENT_COMMANDS_ENABLED) return 0;
  const tokens = compileOriginalEventMessageQueue(tokenize(script), context);
  executeTokens(tokens, context);
  return tokens.length;
}

function tokenize(script: string): string[] {
  return originalEventTokens(script);
}

function isOriginalEventLabel(token: string): boolean {
  return originalMsgSubIsColonLabelLine(token);
}

function compileOriginalEventMessageQueue(tokens: string[], context: EventScriptContext): string[] {
  const labels = originalEventLabels(tokens);
  void labels;
  const queued: string[] = [];
  let pc = 0;
  let guard = 0;
  while (pc < tokens.length && guard < 2000) {
    guard += 1;
    const token = tokens[pc];
    if (!token || isOriginalEventLabel(token)) {
      pc += 1;
      continue;
    }
    const command = originalMessageCommandName(token);
    if (command === "END") break;
    if (command === "GOTO") {
      const jump = resolveOriginalMsgSubJump(tokens, parseEventArgs(token)[0]);
      if (jump === -1) break;
      if (jump === -2) queued.push(token);
      if (jump !== undefined) {
        if (jump >= 0) pc = jump;
        else pc += 1;
        continue;
      }
    } else if (command === "IF") {
      const jump = evaluateIf(token, tokens, context);
      if (jump === -1) break;
      if (jump === -2) queued.push(token);
      if (jump !== undefined) {
        if (jump >= 0) pc = jump;
        else pc += 1;
        continue;
      }
    } else if (command === "MOVE") {
      queued.push("WIPEOUT", token, "WIPEIN");
    } else {
      queued.push(token);
    }
    pc += 1;
  }
  return queued;
}

function executeTokens(tokens: string[], context: EventScriptContext): void {
  enqueueOriginalMessageCache(tokens, context);
  drainOriginalMessageCache();
}

function enqueueOriginalMessageCache(tokens: string[], context: EventScriptContext): void {
  tokens.forEach((token) => {
    appendOriginalMessageCacheEntry({ token, context: { ...context } });
  });
  enterOriginalTimerMessageMode();
}

function enqueueOriginalMessageCacheEntries(entries: OriginalMessageQueueEntry[]): void {
  entries.forEach((entry) => {
    appendOriginalMessageCacheEntry({ token: entry.token, context: { ...entry.context } });
  });
  enterOriginalTimerMessageMode();
}

function enterOriginalTimerMessageMode(): void {
  if (originalMessageQueueCount() <= 0) return;
  if (state.mode === "map" || state.mode === "title" || state.mode === "shop" || state.mode === "inn" || state.mode === "battle") {
    state.previousMode = state.mode === "title" ? "map" : state.mode;
    state.mode = "message";
  }
}

function shiftOriginalMessageCacheEntry(): OriginalMessageQueueEntry | undefined {
  const queue = state.originalMessageQueue;
  if (queue.count <= 0) return undefined;
  const entry = queue.slots[0];
  for (let index = 1; index < queue.count; index += 1) {
    queue.slots[index - 1] = queue.slots[index];
  }
  queue.count -= 1;
  queue.slots[queue.count] = undefined;
  if (entry) {
    queue.shiftedCount += 1;
    queue.lastShifted = entry.token;
    queue.timerSteps += 1;
    queue.lastTimerStepAt = performance.now();
  }
  return entry;
}

function drainOriginalMessageCache(): void {
  if (originalMessageQueueDraining) return;
  if (state.timerMessageWait) {
    return;
  }
  if (!originalTimerMessageCanRun()) {
    if (state.wipe) return;
    state.mode = "message";
    return;
  }
  originalMessageQueueDraining = true;
  let guard = 0;
  try {
    const now = performance.now();
    if (state.originalMessageQueue.nextTimerAt <= 0) state.originalMessageQueue.nextTimerAt = now;
    while (
      originalMessageQueueCount() > 0 &&
      guard < TIMER_MESSAGE_MAX_STEPS_PER_DRAIN &&
      performance.now() >= state.originalMessageQueue.nextTimerAt
    ) {
      guard += 1;
      if (state.timerMessageWait || !originalTimerMessageCanRun()) {
        if (!state.wipe && !originalTimerMessageCanRun()) state.mode = "message";
        return;
      }
      const entry = shiftOriginalMessageCacheEntry();
      state.originalMessageQueue.nextTimerAt += TIMER_MESSAGE_INTERVAL_MS;
      if (!entry) return;
      if (!processOriginalMessageCacheEntry(entry)) return;
    }
    settleOriginalTimerMessageMode();
  } finally {
    originalMessageQueueDraining = false;
  }
}

function settleOriginalTimerMessageMode(): void {
  if (originalMessageQueueCount() > 0 || state.timerMessageWait || state.waitClick || state.wipe) return;
  state.originalMessageQueue.nextTimerAt = 0;
  state.messages = [];
  if (state.mode === "message") {
    restorePreviousModeAfterMessage();
  }
}

function resumeDeferredIfIdle(): boolean {
  if (
    state.deferred.length === 0 ||
    originalMessageQueueDraining ||
    originalMessageQueueCount() > 0 ||
    state.timerMessageWait ||
    state.waitClick ||
    state.wipe
  ) {
    return false;
  }
  const next = state.deferred.shift();
  if (!next) return false;
  next();
  return true;
}

function processOriginalMessageCacheEntry(entry: OriginalMessageQueueEntry): boolean {
  const token = entry.token;
  const context = entry.context;
  if (!token || isOriginalEventLabel(token)) return true;
  const command = originalMessageCommandName(token);
  const eventCommand = originalEventCommandName(token);
  const eventDispatch = originalEventDispatch(eventCommand);
  const dispatch = originalMessageCommandDispatch(command);
  void eventDispatch;
  void dispatch;
  if (!originalMessageCommandHasSourceBackedEffect(command) && !eventDispatch) return true;
  if (command === "PRINT") {
    const text = token.replace(/^Print\s+/i, "");
    enqueueMessage(text);
    if (state.battle) state.battle.battleLog.push(replaceNames(text));
  } else if (command === "MESSAGECLEAR") {
    clearMessageWindow();
  } else if (command === "CLICK") {
    waitForMessageAdvance();
    return false;
  } else if (command === "WAIT") {
    scheduleTimerMessageWait(asInt(parseEventArgs(token)[0], 1), drainOriginalMessageCache);
    return false;
  } else if (command === "CHANGEBGM") {
    changeBgmFromToken(parseEventArgs(token)[0]);
  } else if (command === "STOPBGM") {
    stopBgm(false, true);
  } else if (command === "SOUND") {
    playSe(parseEventArgs(token)[0] ?? "");
  } else if (command === "END") {
    clearOriginalMessageQueueEntries();
    return false;
  } else if (command === "CHAR") {
    performChar(token);
  } else if (command === "DRAW") {
    performDraw(token);
  } else if (command === "DRAWSTATE") {
    performDrawState(token);
  } else if (command === "MOVE") {
    performMove(token, context);
  } else if (command === "SILENTMOVE") {
    performSilentMove(token, context);
  } else if (command === "WALK") {
    performWalk(token);
  } else if (command === "WALKSTART") {
    performWalkStart();
  } else if (command === "MPD") {
    performMpd(token, context);
  } else if (command === "FLAG") {
    performFlag(token);
  } else if (command === "ITEM") {
    performItem(token);
  } else if (command === "MONEY") {
    performMoney(token);
  } else if (command === "HP") {
    performHp(token);
  } else if (command === "MP") {
    performMp(token);
  } else if (command === "EXIST") {
    performExist(token);
  } else if (command === "DAMAGE" || command === "ENEMYDAMAGE") {
    performMessageDamage(token, command, drainOriginalMessageCache);
    return false;
  } else if (command === "SHOWENEMY") {
    if (state.battle) state.battle.enemiesVisible = true;
  } else if (command === "STARTENEMYDAMAGE") {
    performStartEnemyDamage(token);
  } else if (command === "ENDENEMYDAMAGE") {
    performEndEnemyDamage(token);
  } else if (command === "STARTFIGHT") {
    startBattleTurn();
  } else if (command === "TURN") {
    if (state.battle) scheduleTimerMessageWait(1, startBattleTurn);
    return false;
  } else if (command === "VICTORY") {
    queueBattleVictory();
  } else if (command === "ENDFIGHT") {
    finishBattle(asInt(parseEventArgs(token)[0], 0));
  } else if (command === "GAMEOVER") {
    clearOriginalBattleTurnFlag();
    state.gameOverQueued = true;
    executeTokens(["WipeOut", "Menu First"], context);
    return false;
  } else if (command === "COMMAND") {
    if (state.battle) {
      const actorIndex = clamp(asInt(parseEventArgs(token)[0], state.battle.commandActorIndex), 0, 3);
      applyOriginalBattleCommandTurn(actorIndex);
    }
  } else if (command === "MENU") {
    applyOriginalMenuCommand(parseEventArgs(token)[0]);
  } else if (command === "FIGHT") {
    const partyId = asInt(parseEventArgs(token)[0], 0);
    const restEntries = originalMessageQueueEntries();
    clearOriginalMessageQueueEntries();
    const restTokens = restEntries.map((restEntry) => restEntry.token);
    const restContext = { ...context };
    playSe("Enemy");
    deferOrRun(() => startBattle(partyId, undefined, restContext, restTokens));
    return false;
  } else if (command === "SHOP") {
    const ids = parseEventArgs(token).map((value) => asInt(value, 0)).filter(Boolean);
    deferOrRun(() => showShop(ids));
    return false;
  } else if (command === "INN") {
    const price = asInt(parseEventArgs(token)[0], 0);
    deferOrRun(() => showInn(price));
    return false;
  } else if (command === "SILENTCHAR") {
    performSilentChar(token);
  } else if (command === "SILENTWAY") {
    performSilentWay(token);
  } else if (command === "WAY") {
    performWay(token);
  } else if (command === "WIPEOUT") {
    startWipe("out", drainOriginalMessageCache);
    return false;
  } else if (command === "WIPEIN") {
    startWipe("in", drainOriginalMessageCache);
    return false;
  } else if (command === "BACKTOTITLE") {
    deferOrRun(() => backToTitle());
    return false;
  }
  return true;
}

function waitForMessageAdvance(): void {
  state.waitClick = true;
  clickAnimationStartedAt = performance.now();
  state.timerMessageWait = undefined;
  if (state.mode === "map" || state.mode === "title" || state.mode === "shop" || state.mode === "inn" || state.mode === "battle") {
    state.previousMode = state.mode === "title" ? "map" : state.mode;
    state.mode = "message";
  }
}

function clearMessageWindow(): void {
  state.messages = [];
  state.messageHistory = [];
}

function scheduleTimerMessageWait(delayMs: number, action: () => void): void {
  const multiplier = state.battle ? battleTextWaitMultiplier() : 1;
  const delay = Math.max(1, Math.round(delayMs * multiplier));
  state.timerMessageWait = {
    until: performance.now() + delay,
    action
  };
  if (state.mode !== "message") {
    state.previousMode = state.mode === "title" ? "map" : state.mode;
    state.mode = "message";
  }
}

function evaluateIf(token: string, tokens: readonly string[], context: EventScriptContext): number | undefined {
  const args = parseEventArgs(token);
  const type = originalIfPredicateType(token);
  let cond = false;
  let target = originalMessageCommandName(token);
  if (type === "FLAG") {
    const id = asInt(args[1], 0);
    cond = state.flags.has(id);
    target = args[cond ? 2 : 3] ?? "";
  } else if (type === "ITEM") {
    if ((args[1] ?? "").toUpperCase() === "FULL") {
      cond = inventoryFull(targetActorIndex());
    } else {
      cond = originalIfItemPredicate(args[1]);
    }
    target = args[cond ? 2 : 3] ?? "";
  } else if (type === "EXIST") {
    const id = asInt(args[1], 0);
    cond = (state.party[id]?.state ?? 0) > 0;
    target = args[cond ? 2 : 3] ?? "";
  } else if (type === "CHECK") {
    cond = context.checkMode;
    target = args[cond ? 1 : 2] ?? "";
  } else if (type === "MPD") {
    const x = resolveEventX(args[1], context, state.x);
    const y = resolveEventY(args[2], context, state.y);
    const expected = asInt(args[3], -999);
    cond = tileIndexFor(state.mapId, x, y) === expected;
    target = args[cond ? 4 : 5] ?? "";
  }
  return resolveOriginalMsgSubJump(tokens, target);
}

function parseEventArgs(token: string): string[] {
  return originalEventArgs(token);
}

function resolveOriginalMsgSubJump(tokens: readonly string[], target = ""): number | undefined {
  const normalized = originalMsgSubJumpTarget(target);
  if (!normalized || normalized === "-") return undefined;
  if (normalized === "END") return -1;
  const index = originalMsgSubJumpLine(tokens, normalized);
  return index === -1 ? -2 : index;
}

function startWipe(type: WipeState["type"], onDone?: () => void): void {
  const now = performance.now();
  state.wipe = {
    type,
    count: 0,
    duration: WIPE_DURATION_STEPS,
    startedAt: now,
    durationMs: WIPE_DURATION_MS,
    nextFrameAt: now + WIPE_LOGIC_STEP_INTERVAL_MS,
    onDone
  };
}

function performMove(token: string, context: EventScriptContext): void {
  const args = parseEventArgs(token);
  const shouldLoadMap = (args[0] ?? "-") !== "-";
  const nextMap = normalizeMapArg(args[0], state.mapId);
  const x = resolveCoord(args[1], state.x);
  const y = resolveCoord(args[2], state.y);
  const bgmArg = args[3] ?? "-";
  const levelArg = args[4] ?? "-";
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  state.mapId = nextMap;
  state.x = wrapMapCoordinate(x, currentMap().width);
  state.y = wrapMapCoordinate(y, currentMap().height);
  updateOriginalMoveEventContext(context, args, x, y);
  if (bgmArg !== "-") {
    state.bgm = normalizeBgm(bgmArg);
    playBgm(state.bgm);
  }
  if (levelArg !== "-") {
    state.enemyLevel = asInt(levelArg, state.enemyLevel);
  }
  if (shouldLoadMap) {
    reloadOriginalMpdRuntime();
    runMapStartup();
    queueAutoSaveAfterIdle("map_transition");
  }
  performOriginalMapDrawFirst("MOVE");
  performOriginalMapDrawSecond("MOVE");
}

function performSilentMove(token: string, context: EventScriptContext): void {
  const args = parseEventArgs(token);
  const shouldLoadMap = (args[0] ?? "-") !== "-";
  const nextMap = normalizeMapArg(args[0], state.mapId);
  const x = resolveCoord(args[1], state.x);
  const y = resolveCoord(args[2], state.y);
  const bgmArg = args[3] ?? "-";
  const levelArg = args[4] ?? "-";
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  state.mapId = nextMap;
  state.x = wrapMapCoordinate(x, currentMap().width);
  state.y = wrapMapCoordinate(y, currentMap().height);
  updateOriginalMoveEventContext(context, args, x, y);
  if (bgmArg !== "-") {
    state.bgm = normalizeBgm(bgmArg);
    playBgm(state.bgm);
  }
  if (levelArg !== "-") {
    state.enemyLevel = asInt(levelArg, state.enemyLevel);
  }
  if (shouldLoadMap) {
    reloadOriginalMpdRuntime();
    runMapStartup();
    queueAutoSaveAfterIdle("map_transition");
  }
}

function reloadOriginalMpdRuntime(): void {
  state.tileOverrides = {};
  invalidateOriginalMapCache();
}

function updateOriginalMoveEventContext(context: EventScriptContext, args: string[], x: number, y: number): void {
  const updates: Partial<Pick<EventScriptContext, "eventX" | "eventY">> = {};
  if ((args[1] ?? "-") !== "-") updates.eventX = x;
  if ((args[2] ?? "-") !== "-") updates.eventY = y;
  if (updates.eventX === undefined && updates.eventY === undefined) return;
  Object.assign(context, updates);
  for (const entry of state.originalMessageQueue.slots) {
    if (entry) Object.assign(entry.context, updates);
  }
}

function performWalk(token: string): void {
  const args = parseEventArgs(token);
  state.walking = undefined;
  clearPointerIntent();
  const targetX = state.x + asInt(args[0], 0);
  const targetY = state.y + asInt(args[1], 0);
  if (targetX === state.x && targetY === state.y) {
    state.forcedWalk = undefined;
    return;
  }
  state.forcedWalk = { targetX, targetY };
}

function performWalkStart(): void {
  if (state.forcedWalk) return;
}

function performMpd(token: string, context: EventScriptContext): void {
  const args = parseEventArgs(token);
  const x = resolveEventX(args[0], context, state.x);
  const y = resolveEventY(args[1], context, state.y);
  const tile = asInt(args[2], 0);
  state.tileOverrides[tileKey(state.mapId, x, y)] = 128 + clamp(tile, 0, 63);
  performOriginalMapDrawFirst("MPD");
  performOriginalMapDrawSecond("MPD");
  if (context.defaultMessageProcessing) suppressOriginalMapDrawThirdForDefaultMessage("MPD");
  else performOriginalMapDrawThird("MPD");
}

function performFlag(token: string): void {
  const args = parseEventArgs(token);
  const id = asInt(args[0], 0);
  const value = asInt(args[1], 1);
  if (id <= 0) return;
  if (value) state.flags.add(id);
  else state.flags.delete(id);
  if (id === 64 && value) {
    trackEvent("game_clear_level", { progress_id: "final_boss_clear", map_id: state.mapId, party_level_max: maxLevel(), elapsed_seconds: 0 });
  }
}

function performItem(token: string): void {
  const id = asInt(parseEventArgs(token)[0], 0);
  if (id === 0) return;
  if (id < 0) {
    removeInventory(Math.abs(id));
  } else {
    addInventory(id, targetActorIndex());
  }
}

function performMoney(token: string): void {
  state.money = clamp(state.money + asInt(parseEventArgs(token)[0], 0), 0, 9999999);
}

function performChar(token: string): void {
  const index = clamp(asInt(parseEventArgs(token)[0], state.charType), 0, 3);
  state.charType = index;
  performOriginalMapDrawSecond("CHAR");
  performOriginalMapDrawThird("CHAR");
}

function performSilentChar(token: string): void {
  const index = clamp(asInt(parseEventArgs(token)[0], state.charType), 0, 3);
  state.charType = index;
}

function directionFromOriginalWay(value: number, fallback: GameState["direction"]): GameState["direction"] {
  if (value === 0) return "up";
  if (value === 1) return "right";
  if (value === 2) return "down";
  if (value === 3) return "left";
  return fallback;
}

function performWay(token: string): void {
  state.direction = directionFromOriginalWay(asInt(parseEventArgs(token)[0], originalWayForDirection(state.direction)), state.direction);
  performOriginalMapDrawSecond("WAY");
  performOriginalMapDrawThird("WAY");
}

function performSilentWay(token: string): void {
  state.direction = directionFromOriginalWay(asInt(parseEventArgs(token)[0], originalWayForDirection(state.direction)), state.direction);
}

function performDraw(token: string): void {
  const target = (parseEventArgs(token)[0] ?? "").trim();
  if (!target || target.includes("1")) performOriginalMapDrawFirst("DRAW");
  if (!target || target.includes("2")) performOriginalMapDrawSecond("DRAW");
  if (!target || target.includes("3")) performOriginalMapDrawThird("DRAW");
  if (target.includes("4")) state.activeInspection = undefined;
  if (target.includes("4") && state.battle) {
    state.battle.enemiesVisible = true;
    state.battle.hiddenEnemyIndexes = [];
    applyOriginalMenuCommand("FIGHT");
  }
}

function performDrawState(token: string): void {
  void asInt(parseEventArgs(token)[0], state.charType);
  refreshActorDependentMenu();
}

function messageHpTargets(target: string | undefined): Actor[] {
  const normalized = (target ?? "").toUpperCase();
  if (normalized === "A") return state.party.filter((actor) => actor.state > 0);
  if (normalized === "R") {
    const fallback = state.party.findIndex((actor) => actor.state > 0);
    const index = originalRandomIndex(4, (candidate) => (state.party[candidate]?.state ?? 0) > 0, Math.max(0, fallback));
    const actor = state.party[index];
    return actor?.state ? [actor] : [];
  }
  const actor = state.party[asInt(target, selectedActorIndex())];
  return actor?.state ? [actor] : [];
}

function messageMpTargets(target: string | undefined): Actor[] {
  const normalized = (target ?? "").toUpperCase();
  if (normalized === "A") return state.party.filter((actor) => actor.state === 1);
  if (normalized === "R") {
    const fallback = state.party.findIndex((actor) => actor.state === 1);
    const index = originalRandomIndex(4, (candidate) => (state.party[candidate]?.state ?? 0) === 1, Math.max(0, fallback));
    const actor = state.party[index];
    return actor?.state === 1 ? [actor] : [];
  }
  const actor = state.party[asInt(target, selectedActorIndex())];
  return actor?.state === 1 ? [actor] : [];
}

function mathHp(actor: Actor, amount: number, battle?: BattleState): void {
  if (actor.state === 0) return;
  if (amount < 0) {
    damageActor(actor, -amount, battle);
    return;
  }
  actor.hp = Math.min(actor.maxHp, actor.hp + amount);
  if (actor.hp < 1) {
    damageActor(actor, 0, battle);
    return;
  }
  actor.state = 1;
}

function mathMp(actor: Actor, amount: number): void {
  if (actor.state !== 1) return;
  actor.mp += amount;
  if (actor.mp > actor.maxMp) actor.mp = actor.maxMp;
  if (actor.hp < 1) actor.mp = 0;
}

function performHp(token: string): void {
  const args = parseEventArgs(token);
  const amount = asInt(args[1], 0);
  const targets = messageHpTargets(args[0]);
  targets.forEach((actor) => mathHp(actor, amount, state.battle));
  if (targets.length > 0 && livingParty().length === 0) triggerGameOver();
}

function performMp(token: string): void {
  const args = parseEventArgs(token);
  const amount = asInt(args[1], 0);
  messageMpTargets(args[0]).forEach((actor) => mathMp(actor, amount));
}

function performMessageDamage(token: string, command: string, after?: () => void): void {
  const args = parseEventArgs(token);
  const index = asInt(args[0], 0);
  const amount = Math.max(0, asInt(args[1], 0));
  if (command === "ENEMYDAMAGE") {
    performOriginalEnemyDamage(index, amount, args[2] ?? "DAMAGE", after);
    return;
  }
  performOriginalActorDamage(index, amount, args[2] ?? "DAMAGE", after);
}

function performOriginalActorDamage(index: number, amount: number, sound: string, after?: () => void): void {
  const actor = state.party[index] ?? livingParty()[0];
  const battle = state.battle;
  if (!actor || actor.state <= 0 || actor.hp <= 0) {
    after?.();
    return;
  }
  let died = false;
  const roll = rng() * 101;
  if (roll <= 96) {
    const damage = Math.max(1, originalVbInteger(amount * 0.8 + amount * rng() * 0.4));
    playSe(sound);
    died = applyActorDamageState(actor, damage);
    const text = gameText("source.main.0c3a3bb6", { actor_name: actor.name, damage: damage });
    if (battle) {
      battle.battleLog.push(text);
      enqueueMessage(text);
    } else {
      enqueueMessage(text);
    }
  } else if (roll <= 98) {
    playSe("Dodge");
    const text = gameText("source.main.e96230b2", { actor_name: actor.name });
    if (battle) {
      battle.battleLog.push(text);
      enqueueMessage(text);
    } else {
      enqueueMessage(text);
    }
  } else {
    playSe("Defense");
    const text = gameText("source.main.21d8e45c", { actor_name: actor.name });
    if (battle) {
      battle.battleLog.push(text);
      enqueueMessage(text);
    } else {
      enqueueMessage(text);
    }
  }
  scheduleTimerMessageWait(100, () => {
    if (died) queueOriginalActorDeathMessage(actor);
    if (after) {
      after();
      return;
    }
    if (livingParty().length === 0) triggerGameOver();
  });
}

function applyActorDamageState(actor: Actor, amount: number): boolean {
  if (actor.state <= 0) return false;
  actor.hp = Math.max(0, actor.hp - Math.max(0, amount));
  if (actor.hp > 0) {
    actor.state = 1;
    return false;
  }
  actor.state = 2;
  return true;
}

function performOriginalEnemyDamage(index: number, amount: number, sound: string, after?: () => void): void {
  const battle = state.battle;
  const enemy = battle?.enemies[index];
  if (!battle || !enemy || enemy.currentHp <= 0) {
    after?.();
    return;
  }
  const roll = rng() * 101;
  if (roll <= 96) {
    const damage = Math.max(1, originalVbInteger(amount * 0.8 + amount * rng() * 0.4));
    performStartEnemyDamageToken(index, sound);
    enemy.currentHp = Math.max(0, enemy.currentHp - damage);
    enqueueMessage(gameText("source.main.484fd6ed", { enemy_name: enemy.name, damage: damage }));
    battle.battleLog.push(gameText("source.main.484fd6ed", { enemy_name: enemy.name, damage: damage }));
    if (enemy.currentHp < 1) {
      enqueueMessage(gameText("source.main.a05daaa1", { enemy_name: enemy.name }));
      battle.battleLog.push(gameText("source.main.a05daaa1", { enemy_name: enemy.name }));
      battle.rewardExp += enemy.exp;
      battle.rewardGold += enemy.gold;
    }
  } else if (roll <= 98) {
    playSe("Dodge");
    enqueueMessage(gameText("source.main.574d81ef", { enemy_name: enemy.name }));
    battle.battleLog.push(gameText("source.main.60d409b5", { enemy_name: enemy.name }));
  } else {
    playSe("Defense");
    enqueueMessage(gameText("source.main.86c3a52f", { enemy_name: enemy.name }));
    battle.battleLog.push(gameText("source.main.ebc546c5", { enemy_name: enemy.name }));
  }
  scheduleTimerMessageWait(100, () => {
    if (enemy.currentHp > 0) performEndEnemyDamageToken(index);
    if (after) {
      after();
      return;
    }
    if (checkBattleVictory()) return;
    startBattleTurn();
  });
}

function performStartEnemyDamage(token: string): void {
  const args = parseEventArgs(token);
  performStartEnemyDamageToken(asInt(args[0], 0), args[1] ?? "DAMAGE");
}

function performStartEnemyDamageToken(index: number, sound: string): void {
  const battle = state.battle;
  if (!battle) return;
  if (!battle.hiddenEnemyIndexes.includes(index)) battle.hiddenEnemyIndexes.push(index);
  playSe(sound);
}

function performEndEnemyDamage(token: string): void {
  performEndEnemyDamageToken(asInt(parseEventArgs(token)[0], 0));
}

function performEndEnemyDamageToken(index: number): void {
  const battle = state.battle;
  if (!battle) return;
  battle.hiddenEnemyIndexes = battle.hiddenEnemyIndexes.filter((candidate) => candidate !== index);
}

function applyOriginalMenuCommand(menuName: string | undefined): void {
  const name = (menuName ?? "").toUpperCase();
  if (name === "WALK") {
    setOriginalMenuMode("walk");
    clearOriginalNativeMenuOverlay();
  } else if (name === "FIGHT" && state.battle) {
    setOriginalMenuMode("fight");
    state.battle.phase = "command";
    state.battle.battleItemMenuMode = undefined;
    state.battle.battleMagicMenuMode = undefined;
    state.battle.battleRootSelected = 0;
    state.battle.battleLeafSelected = 0;
    state.battle.battlePopupFocus = "root";
    state.battle.selected = 0;
    queueOriginalMenuCommandFocus();
    if (state.mode !== "message") {
      state.mode = "battle";
      state.previousMode = "battle";
    }
  } else if (name === "FIRST") {
    setOriginalMenuMode("first");
    if (state.gameOverQueued) applyOriginalMenuFirst();
  } else if (name === "ET") {
    setOriginalMenuMode("et");
    if (state.mode === "message") {
      return;
    }
    if (state.battle) {
      state.mode = "battle";
      state.previousMode = "battle";
    } else if (state.mode === "shop" || state.mode === "inn") {
      state.mode = "map";
      state.previousMode = "map";
    }
  }
}

function clearOriginalNativeMenuOverlay(): void {
  cancelOriginalFieldPopup(false);
  cancelOriginalSystemPopup(false);
  if (state.mode === "shop" || state.mode === "inn") {
    state.mode = state.battle ? "battle" : "map";
    state.previousMode = state.mode;
  }
}

function applyOriginalMenuFirst(): void {
  state.battle = undefined;
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  setOriginalMenuMode("first");
  state.mode = "title";
  state.previousMode = "title";
  state.started = false;
  state.gameOverQueued = false;
}

function finishBattle(result: number): void {
  const battle = state.battle;
  if (result === 1) {
    if (battle?.endingQueued === "victory") {
      completeBattleExit(200, battle.onWinScript, battle.onWinContext, battle.onWinMessageQueue);
      return;
    }
    completeBattleExit(200);
    return;
  }
  if (result === 0) {
    queueBattleEndTokens(1);
    return;
  }
  if (result === 2) {
    queueBattleEscape();
    return;
  }
  if (result === 3) {
    if (battle?.endingQueued === "escape") {
      completeBattleExit(180);
      return;
    }
    queueBattleEscape();
    return;
  }
  completeBattleExit(200);
}

function battleEndTokens(result: 1 | 3): string[] {
  return ["MessageClear", `ChangeBGM ${state.bgm}`, "WipeIn", `EndFight ${result}`];
}

function queueBattleEndTokens(result: 1 | 3): void {
  hideOriginalBattleEnemyPictures();
  deferOrRun(() => executeTokens(battleEndTokens(result), { checkMode: false }));
}

function performExist(token: string): void {
  const args = parseEventArgs(token);
  const id = asInt(args[0], -1);
  const value = asInt(args[1], 1);
  if (state.party[id]) state.party[id].state = value;
}

function normalizeMapArg(arg: string | undefined, fallback: string): string {
  if (!arg || arg === "-") return fallback;
  return arg.replace(/\.MPD$/i, "").toUpperCase();
}

function normalizeBgm(arg: string): string {
  if (!arg || arg === "-") return state.bgm;
  const upper = arg.toUpperCase();
  return upper.endsWith(".MID") ? upper : `${upper}.MID`;
}

function changeBgmFromToken(raw: string | undefined): void {
  const arg = (raw ?? "").trim();
  if (!arg) {
    requestedBgm = undefined;
    stopBgm(false, true);
    return;
  }
  playBgm(arg);
}

function resolveCoord(value: string | undefined, fallback: number): number {
  if (!value || value === "-") return fallback;
  return asInt(value, fallback);
}

function resolveEventX(value: string | undefined, context: EventScriptContext, fallback: number): number {
  return resolveCoord(value, context.eventX ?? fallback);
}

function resolveEventY(value: string | undefined, context: EventScriptContext, fallback: number): number {
  return resolveCoord(value, context.eventY ?? fallback);
}

function tileKey(mapId: string, x: number, y: number): string {
  return `${mapId}:${x},${y}`;
}

function tileIndexFor(mapId: string, x: number, y: number): number {
  const map = maps.get(mapId) ?? currentMap();
  const mapX = wrapMapCoordinate(x, map.width);
  const mapY = wrapMapCoordinate(y, map.height);
  const override = state.tileOverrides[tileKey(mapId, mapX, mapY)];
  const raw = override ?? map.rawRows[mapY]?.[mapX] ?? 128;
  if (raw >= 128 && raw <= 191) return raw - 128;
  return 0;
}

function wrapMapCoordinate(value: number, size: number): number {
  if (size <= 0) return value;
  return ((value % size) + size) % size;
}

function enqueueMessage(text: string): void {
  const replaced = replaceNames(text);
  if (!replaced.trim()) return;
  state.messages.push(replaced);
  state.messageHistory = originalMessagePrintLines(state.messageHistory, replaced);
  if (state.mode === "map" || state.mode === "title" || state.mode === "shop" || state.mode === "inn" || state.mode === "battle") {
    state.previousMode = state.mode === "title" ? "map" : state.mode;
    state.mode = "message";
  }
}

function advanceMessage(): void {
  if (state.timerMessageWait && !state.waitClick) return;
  const wasWaitingForClick = state.waitClick;
  if (wasWaitingForClick) {
    state.waitClick = false;
    clickAnimationStartedAt = 0;
    state.timerMessageWait = undefined;
    state.messages = [];
    playSe("Beep");
    drainOriginalMessageCache();
    if (originalMessageQueueCount() > 0 || state.timerMessageWait || state.waitClick || state.wipe) return;
    if (state.mode !== "message") {
      advanceOriginalBattleTurnFlag();
      return;
    }
    const next = state.deferred.shift();
    if (next) {
      next();
      return;
    }
    restorePreviousModeAfterMessage();
    advanceOriginalBattleTurnFlag();
    return;
  }
  state.messages.shift();
  if (state.messages.length > 0) return;
  drainOriginalMessageCache();
  if (originalMessageQueueCount() > 0 || state.timerMessageWait || state.waitClick || state.wipe) return;
  if (state.mode !== "message") {
    advanceOriginalBattleTurnFlag();
    return;
  }
  const next = state.deferred.shift();
  if (next) {
    next();
    return;
  }
  restorePreviousModeAfterMessage();
  advanceOriginalBattleTurnFlag();
}

function restorePreviousModeAfterMessage(): void {
  const restoredMode = state.previousMode === "title" ? "map" : state.previousMode;
  state.mode = restoredMode;
  if (restoredMode === "shop" || restoredMode === "inn") {
    leaveOriginalMenuUsingToWalk();
  }
}

function deferOrRun(action: () => void): void {
  if (state.waitClick || state.timerMessageWait || originalMessageQueueCount() > 0 || state.wipe) {
    state.deferred.push(action);
    state.mode = "message";
  } else {
    action();
  }
}

function replaceNames(text: string): string {
  return replaceNamesWithParty(text, state.party, state.sourceActorIndex, targetActorIndex());
}

function replaceNamesWithParty(text: string, party: Actor[], sourceActorIndex: number, targetActorIndexValue: number): string {
  return text
    .replaceAll("%0", party[0]?.name ?? gameText("source.main.14645517"))
    .replaceAll("%1", party[1]?.name ?? gameText("source.main.a9a7b1ee"))
    .replaceAll("%2", party[2]?.name ?? gameText("source.main.6e419dce"))
    .replaceAll("%3", party[3]?.name ?? gameText("source.main.c45b2579"))
    .replaceAll("%4", party[sourceActorIndex]?.name ?? party[0]?.name ?? gameText("source.main.14645517"))
    .replaceAll("%5", party[targetActorIndexValue]?.name ?? party[0]?.name ?? gameText("source.main.14645517"));
}

function triggerOriginalCheckMenu(): void {
  if (!originalMainStateIsWalk()) return;
  interact();
}

function triggerOriginalFightMenu(): void {
  const battle = state.battle;
  if (!battle || !originalBattleNativeMenuReady()) return;
  playerAttackRound();
}

function triggerOriginalItemMenu(): void {
  const battle = state.battle;
  if (battle && originalBattleNativeMenuReady()) {
    openOriginalBattleItemRootMenu(battle);
    return;
  }
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
}

function triggerOriginalMagicMenu(): void {
  const battle = state.battle;
  if (battle && originalBattleNativeMenuReady()) {
    openOriginalBattleMagicRootMenu(battle);
    return;
  }
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("magic");
}

function triggerOriginalShopMenu(): void {
  if ((originalMainStateMask() & ORIGINAL_MAIN_STATE_MENU_USING) === 0 || state.mode !== "shop") return;
  openOriginalFieldPopup("shop");
}

function triggerOriginalInnMenu(): void {
  if ((originalMainStateMask() & ORIGINAL_MAIN_STATE_MENU_USING) === 0 || state.mode !== "inn") return;
  const total = state.innPrice ?? 0;
  cancelOriginalFieldPopup(false);
  if (state.money < total) {
    executeTokens([printToken("source.main.98133093")], { checkMode: false });
    return;
  }
  closeMenu();
  executeTokens(["WipeOut", "HP A 9999", "MP A 9999", `Money -${total}`, "WipeIn"], { checkMode: false });
}

function openMainMenu(): void {
  if (!originalMainStateIsWalk()) return;
  clearHeldMovementKeys();
  cancelOriginalSystemPopup(false);
  cancelOriginalFieldPopup(false);
  cancelOriginalBattlePopup(false);
  originalMenuBarTracking = true;
  renderOriginalMenuBar();
  focusFirstOriginalTopMenuSelection();
  draw();
}

function openOriginalSystemRootMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalSystemPopup();
  renderOriginalMenuBar();
  draw();
}

function closeMenu(): void {
  state.mode = "map";
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  originalFieldMenuOpen = undefined;
  originalFieldSubmenuOpen = undefined;
  originalMenuBarTracking = false;
  originalMenuBarSelectedKey = undefined;
  originalPointerMenuPreviewArmed = false;
  pendingOriginalMenuCommandFocus = false;
  if (originalMenuBarContainsFocus()) (document.activeElement as HTMLElement | null)?.blur?.();
  if (!state.battle) setOriginalMenuMode("walk");
}

function openItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
}

function openUseItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
  setOriginalFieldSubmenu("use");
}

function openTransferItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
  setOriginalFieldSubmenu("transfer");
}

function openDiscardItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
  setOriginalFieldSubmenu("discard");
}

function openSeeItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
  setOriginalFieldSubmenu("see");
}

function openImportantItemMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("item");
  setOriginalFieldSubmenu("important");
}

function openMagicMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("magic");
}

function openMagicChantMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalFieldPopup("magic");
  setOriginalFieldSubmenu("chant");
}

function openStatusMenu(): void {
  if (!originalMainStateIsWalk()) return;
  state.activeInspection = originalStatusInspection(originalSourceActor(), originalSourceActorIndex());
  closeMenu();
  executeTokens(["Click", "Draw 3"], { checkMode: false });
}

function originalItemInspection(item: Item): OriginalInspection {
  const lines: OriginalInspectionLine[] = [
    { text: gameText("source.main.719246da", { item_name: item.name }), x: 16, y: 16 },
    { text: gameText("source.main.91671e21", { originalItemEffectDescription_item: originalItemEffectDescription(item) }), x: 16, y: 40 },
    { text: gameText("source.main.f79f2856"), x: 16, y: 72 },
    { text: gameText("source.main.a8910c01"), x: 16, y: 142 },
    { text: gameText("source.main.38e74436", { usageTextFromState_item_state: usageTextFromState(item.state) }), x: 16, y: 224 }
  ];
  originalAllowedActorNameSlots(item.state, "source").forEach(({ name, actorIndex }) => {
    lines.push({ text: name, x: 32 + (actorIndex % 2) * 144, y: 96 + Math.floor(actorIndex / 2) * 24 });
  });
  originalAllowedActorNameSlots(item.state, "target").forEach(({ name, actorIndex }) => {
    lines.push({ text: name, x: 32 + (actorIndex % 2) * 144, y: 166 + Math.floor(actorIndex / 2) * 24 });
  });
  return { kind: "item", lines };
}

function originalItemEffectDescription(item: Item): string {
  const value = String(item.value);
  const scope = targetScopeText(item.state);
  if (item.type === 0) return gameText("source.main.7a4e386c", { scope: scope, value: value });
  if (item.type === 1) return gameText("source.main.e31bbe83", { scope: scope, value: value });
  if (item.type === 2) return gameText("source.main.941f4a6b", { scope: scope, value: value });
  if (item.type === 3) return gameText("source.main.d8fdc81b", { scope: scope, value: value });
  if (item.type === 4) return gameText("source.main.39096304", { scope: scope, value: value });
  if (item.type === 5) return gameText("source.main.09caab10", { scope: scope, value: value });
  if (item.type === 6) return gameText("source.main.fe06b0c7", { scope: scope, value: value });
  if (item.type === 7) return gameText("source.main.c19457f1", { scope: scope, value: value });
  if (item.type === 8) return gameText("source.main.4266e306", { scope: scope, value: value });
  if (item.type === 9) return gameText("source.main.c0067965", { scope: scope, value: value });
  if (item.type === 10) return gameText("source.main.b91abf65", { scope: scope, value: value });
  if (item.type === 11) return gameText("source.main.f5b08207", { scope: scope, value: value });
  if (item.type === 12) return gameText("source.main.6594fac4", { scope: scope, value: value });
  if (item.type === 13) return gameText("source.main.48d5e8f8", { scope: scope, value: value });
  if (item.type === 14) return gameText("source.main.0ff5ee16", { scope: scope, value: value });
  if (item.type === 15) return gameText("source.main.12e522c3", { value: value });
  if (item.type === 16) return gameText("source.main.e1043507", { value: value });
  if (item.type === 17) return gameText("source.main.c78c622a", { magic_get_item_value_name: magic.get(item.value)?.name ?? gameText("message.common.magic") });
  return gameText("source.main.0a019bb6");
}

function originalMagicInspection(spell: Magic): OriginalInspection {
  return {
    kind: "magic",
    lines: [
      { text: gameText("source.main.4e70f488", { spell_name: spell.name }), x: 16, y: 16 },
      { text: gameText("source.main.bea28a4f", { spell_mp: spell.mp }), x: 16, y: 40 },
      { text: gameText("source.main.5317bb28", { originalMagicEffectDescription_spell: originalMagicEffectDescription(spell) }), x: 16, y: 64 },
      { text: gameText("source.main.6ada44ad", { usageTextFromState_spell_state: usageTextFromState(spell.state) }), x: 16, y: 224 }
    ]
  };
}

function originalMagicEffectDescription(spell: Magic): string {
  const value = String(spell.value);
  const scope = targetScopeText(spell.state);
  if (spell.type === 0) return gameText("source.main.7a4e386c", { scope: scope, value: value });
  if (spell.type === 1) return gameText("source.main.e31bbe83", { scope: scope, value: value });
  if (spell.type === 2) return gameText("source.main.cad8f793", { scope: scope, value: value });
  if (spell.type === 8) return gameText("source.main.4266e306", { scope: scope, value: value });
  if (spell.type === 9) return gameText("source.main.c0067965", { scope: scope, value: value });
  if (spell.type === 10) return gameText("source.main.b91abf65", { scope: scope, value: value });
  if (spell.type === 11) return gameText("source.main.f5b08207", { scope: scope, value: value });
  if (spell.type === 12) return gameText("source.main.6594fac4", { scope: scope, value: value });
  if (spell.type === 13) return gameText("source.main.48d5e8f8", { scope: scope, value: value });
  if (spell.type === 14) return gameText("source.main.0ff5ee16", { scope: scope, value: value });
  return gameText("source.main.0a019bb6");
}

function usageTextFromState(stateValue: number): string {
  const walk = (stateValue & ORIGINAL_STATE_WALK_USABLE) !== 0;
  const battle = (stateValue & ORIGINAL_STATE_BATTLE_USABLE) !== 0;
  if (walk && battle) return gameText("source.main.a35f4a1b");
  if (walk) return gameText("source.main.990744a7");
  if (battle) return gameText("source.main.64836de9");
  return gameText("source.main.e537f8a1");
}

function actorSourceAllowed(stateValue: number, actorIndex: number): boolean {
  return (stateValue & (1 << actorIndex)) !== 0;
}

function actorTargetAllowed(stateValue: number, actorIndex: number): boolean {
  return (stateValue & (1 << (actorIndex + 4))) !== 0;
}

function modeAllowed(stateValue: number, inBattle: boolean): boolean {
  return (stateValue & (inBattle ? ORIGINAL_STATE_BATTLE_USABLE : ORIGINAL_STATE_WALK_USABLE)) !== 0;
}

function itemUsabilityFailures(item: Item, inBattle: boolean, sourceIndex: number, targetIndex = sourceIndex): string[] {
  const failures: string[] = [];
  if (!actorSourceAllowed(item.state, sourceIndex)) failures.push(gameText("source.main.8244b0cf", { state_party_sourceIndex_name: state.party[sourceIndex]?.name ?? gameText("message.common.companion"), item_name: item.name }));
  if (!actorTargetAllowed(item.state, targetIndex)) failures.push(gameText("source.main.b760f4e8", { state_party_targetIndex_name: state.party[targetIndex]?.name ?? gameText("message.common.companion"), item_name: item.name }));
  if (!modeAllowed(item.state, inBattle)) failures.push(gameText("source.main.ba61f388", { item_name: item.name, inBattle: inBattle ? gameText("message.common.battle") : gameText("message.common.walk") }));
  return failures;
}

function magicUsabilityFailures(spell: Magic, caster: Actor, inBattle: boolean): string[] {
  const failures: string[] = [];
  if (caster.mp < spell.mp) failures.push(gameText("source.main.ec57852b"));
  if (!modeAllowed(spell.state, inBattle)) failures.push(gameText("source.main.6869b4b9", { spell_name: spell.name, inBattle: inBattle ? gameText("message.common.battle") : gameText("message.common.walk") }));
  return failures;
}

function originalItemUseSound(item: Item): string | undefined {
  return item.state & ORIGINAL_STATE_CUSTOM_SOUND ? item.wave : undefined;
}

function originalItemConsumed(item: Item): boolean {
  return (item.state & ORIGINAL_STATE_REUSABLE_ITEM) === 0;
}

function originalUseItemPrefixTokens(item: Item, user: Actor, target: Actor): string[] {
  const tokens: string[] = [];
  const itemSound = originalItemUseSound(item);
  if (itemSound !== undefined) tokens.push(`Sound ${itemSound}`);
  if ((item.type >= 0 && item.type <= 14) || item.type === 17 || item.type === 18) {
    tokens.push(printToken("source.main.55e4d852", { user_name: user.name, item_name: item.name }));
  } else if (item.type === 15 || item.type === 16) {
    tokens.push(printToken("source.main.cd3612c5", { target_name: target.name, item_name: item.name }));
  }
  return tokens;
}

function originalMagicUseSound(spell: Magic): string {
  return spell.state & ORIGINAL_STATE_CUSTOM_SOUND ? spell.wave : "Magic";
}

function originalUseMagicPrefixTokens(spell: Magic, caster: Actor, casterIndex: number): string[] {
  return [
    `Sound ${originalMagicUseSound(spell)}`,
    printToken("source.main.c33b8ef3", { caster_name: caster.name, spell_name: spell.name }),
    "MP " + String(casterIndex) + " -" + String(spell.mp)
  ];
}

function originalStatusInspection(actor: Actor, index: number): OriginalInspection {
  void index;
  const weapon = actor.weapon > 0 ? items.get(actor.weapon)?.name ?? gameText("source.main.afff9c34") : gameText("source.main.afff9c34");
  const protector = actor.protector > 0 ? items.get(actor.protector)?.name ?? gameText("source.main.1ccea3a9") : gameText("source.main.1ccea3a9");
  const weaponPower = actor.weapon > 0 ? items.get(actor.weapon)?.value ?? 0 : 0;
  const protectorPower = actor.protector > 0 ? items.get(actor.protector)?.value ?? 0 : 0;
  return {
    kind: "status",
    lines: [
      { text: gameText("source.main.571ac100", { actor_name: actor.name }), x: 16, y: 16 },
      { text: gameText("source.main.04e1bf7b", { actor_level: actor.level }), x: 232, y: 16 },
      { text: gameText("source.main.89661026", { actor_hp: actor.hp, actor_maxHp: actor.maxHp }), x: 16, y: 48 },
      { text: gameText("source.main.1bbf22cd", { actor_mp: actor.mp, actor_maxMp: actor.maxMp }), x: 168, y: 48 },
      { text: gameText("source.main.e1680e7a", { actor_offense: actor.offense, weaponPower_0_weaponPower: weaponPower > 0 ? ` + ${weaponPower}` : "" }), x: 16, y: 80 },
      { text: gameText("source.main.815891d1", { weapon: weapon }), x: 64, y: 104 },
      { text: gameText("source.main.16ab5821", { actor_defense: actor.defense, protectorPower_0_protectorPower: protectorPower > 0 ? ` + ${protectorPower}` : "" }), x: 16, y: 136 },
      { text: gameText("source.main.5e2863aa", { protector: protector }), x: 64, y: 160 },
      { text: gameText("source.main.49aa3412", { actor_speed: actor.speed }), x: 16, y: 192 },
      { text: gameText("source.main.f45af9b9", { actor_experience: actor.experience, actor_nextExp: actor.nextExp }), x: 16, y: 224 }
    ].filter((line) => line.text.trim())
  };
}

function originalAllowedActorNameSlots(stateValue: number, role: "source" | "target"): Array<{ name: string; actorIndex: number }> {
  const offset = role === "source" ? 0 : 4;
  return state.party.flatMap((actor, actorIndex) =>
    actor.state > 0 && (stateValue & (1 << (actorIndex + offset))) !== 0 ? [{ name: actor.name, actorIndex }] : []
  );
}

function saveCurrentDataSlot(): void {
  if (!originalMainStateIsWalk()) return;
  if (state.saveDataSlot === 0) {
    closeOriginalNativeMenuPopups();
    executeTokens([printToken("source.main.ac5f8da9")], { checkMode: false });
    return;
  }
  saveGame(state.saveDataSlot);
  closeOriginalNativeMenuPopups();
  executeTokens([printToken("source.main.e9b49f7a")], { checkMode: false });
}

function openNewSaveMenu(): void {
  if (!originalMainStateIsWalk()) return;
  openOriginalSystemSubmenuNewSave();
}

function confirmNewSaveSlot(slot: number): void {
  if (!originalMainStateIsWalk()) return;
  showOriginalConfirmDialog(
    gameText("source.main.6aca3493", { originalDataSlotLabel_slot: originalDataSlotLabel(slot) }),
    () => {
      saveGame(slot);
      state.saveDataSlot = slot;
      closeOriginalNativeMenuPopups();
      executeTokens([printToken("source.main.e9b49f7a")], { checkMode: false });
    },
    loadSlot(slot) ? gameText("source.main.b24a5106") : undefined
  );
}

function openLoadMenu(): void {
  if (!originalMainStateAllowsStartOrWalkMenu()) return;
  openOriginalSystemSubmenuLoad();
}

function continueFromLatestAutoSaveOrNewGame(): void {
  if (!latestAutoSave()) {
    newGame();
    return;
  }
  loadAutoSaveFromOriginalMenu();
}

function confirmLoadSlot(slot: number): void {
  if (!originalMainStateAllowsStartOrWalkMenu()) return;
  resumeAudio();
  showOriginalConfirmDialog(gameText("source.main.3cf735cf", { originalDataSlotLabel_slot: originalDataSlotLabel(slot) }), () => {
    loadDataSlotFromOriginalMenu(slot);
  });
}

function loadDataSlotFromOriginalMenu(slot: number): void {
  if (!originalMainStateAllowsStartOrWalkMenu()) return;
  const shouldWipeOut = state.started;
  const loaded = loadGame(slot);
  if (!loaded) {
    closeOriginalNativeMenuPopups();
    executeTokens([printToken("source.main.a820d93f")], { checkMode: false });
    return;
  }
  closeOriginalNativeMenuPopups(false);
  state.mode = "map";
  state.previousMode = "map";
  setOriginalMenuMode("walk");
  forceOriginalSourceActor(0);
  resumeAudio();
  playBgm(state.bgm);
  resetOriginalMapDrawTrace();
  invalidateOriginalMapCache();
  executeOriginalLoadMpdSequence(shouldWipeOut ? ["WipeOut"] : [], ["WipeIn"]);
}

function loadAutoSaveFromOriginalMenu(): void {
  if (!originalMainStateAllowsStartOrWalkMenu()) return;
  const shouldWipeOut = state.started;
  const loaded = loadAutoSave();
  if (!loaded) {
    newGame();
    return;
  }
  closeOriginalNativeMenuPopups(false);
  state.mode = "map";
  state.previousMode = "map";
  setOriginalMenuMode("walk");
  forceOriginalSourceActor(0);
  resumeAudio();
  playBgm(state.bgm);
  resetOriginalMapDrawTrace();
  invalidateOriginalMapCache();
  executeOriginalLoadMpdSequence(shouldWipeOut ? ["WipeOut"] : [], ["WipeIn"]);
}

function originalDataSlotLabel(slot: number): string {
  return gameText("source.main.c38424c7", { slot: slot });
}

function originalDataSlotLevelRowDetail(slot: number, warning: boolean): Pick<OriginalPopupRow, "detail" | "detailWarning"> {
  const level = originalSavedDataSlotFirstActorLevel(slot);
  return level === undefined ? {} : { detail: gameText("source.main.8d9f4335", { level: level }), detailWarning: warning };
}

function originalDataSlotHasData(slot: number): boolean {
  return Boolean(loadSlot(slot));
}

function originalSavedDataSlotFirstActorLevel(slot: number): number | undefined {
  try {
    const saved = loadSlot(slot);
    if (!saved) return undefined;
    const level = serializedFromStoredSlot(saved).party[0]?.level;
    return Number.isFinite(level) ? level : undefined;
  } catch {
    return undefined;
  }
}

function openHelp(): void {
  openHelpDialog();
}

function confirmBackToTitle(): void {
  if (!originalMainStateIsWalk()) return;
  showOriginalConfirmDialog(gameText("source.main.584fac3a"), () => {
    closeOriginalNativeMenuPopups();
    executeTokens(["WipeOut", "Menu First", "BackToTitle"], { checkMode: false });
  });
}

function triggerOriginalNewGameMenu(): void {
  const mask = originalMainStateMask();
  if (mask === ORIGINAL_MAIN_STATE_START) {
    newGame();
    return;
  }
  if (mask !== ORIGINAL_MAIN_STATE_WALK) return;
  confirmNewGame();
}

function confirmNewGame(): void {
  if (!originalMainStateIsWalk()) return;
  showOriginalConfirmDialog(gameText("source.main.c1905e61"), () => {
    closeOriginalNativeMenuPopups();
    newGame();
  });
}

function triggerOriginalTitleStart(): void {
  if (originalMainStateMask() !== ORIGINAL_MAIN_STATE_START) return;
  newGame();
}

function backToTitle(): void {
  state.mode = "title";
  state.previousMode = "title";
  state.started = false;
  state.battle = undefined;
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  setOriginalMenuMode("first");
  state.messages = [];
  state.messageHistory = [];
  state.originalMessageQueue = makeOriginalMessageQueueState();
  state.deferred = [];
  state.waitClick = false;
  clickAnimationStartedAt = 0;
  state.timerMessageWait = undefined;
  state.activeInspection = undefined;
  state.gameOverQueued = false;
  if (normalizeBgm(requestedBgm ?? "") !== TITLE_BGM) playBgm(TITLE_BGM);
  focusOriginalTitleMenuSelection();
}

function closeOriginalNativeMenuPopups(redraw = true): void {
  originalSystemMenuOpen = false;
  originalSystemSubmenuOpen = undefined;
  cancelOriginalFieldPopup(false);
  originalMenuBarTracking = false;
  originalMenuBarSelectedKey = undefined;
  originalPointerMenuPreviewArmed = false;
  pendingOriginalMenuCommandFocus = false;
  if (redraw) renderOriginalMenuBar();
}

function useMapItemFromNativeMenu(entry: InventoryEntry): void {
  closeOriginalNativeMenuPopups();
  useMapItemEntry(entry, () => undefined);
}

function transferMapItemFromNativeMenu(entry: InventoryEntry, itemName: string): void {
  if (!originalMainStateIsWalk()) return;
  closeOriginalNativeMenuPopups();
  transferInventoryEntry(entry, itemName);
}

function confirmDiscardMapItem(entry: InventoryEntry, itemName: string): void {
  if (!originalMainStateIsWalk()) return;
  showOriginalConfirmDialog(gameText("source.main.78dcb0a0", { itemName: itemName }), () => discardMapItemFromNativeMenu(entry, itemName));
}

function discardMapItemFromNativeMenu(entry: InventoryEntry, itemName: string): void {
  if (!originalMainStateIsWalk()) return;
  removeInventoryEntry(entry);
  closeOriginalNativeMenuPopups();
  executeTokens([printToken("source.main.621e7e59", { originalSourceActor_name: originalSourceActor().name, itemName: itemName }), "Click"], { checkMode: false });
}

function seeMapItemFromNativeMenu(entry: InventoryEntry): void {
  closeOriginalNativeMenuPopups();
  seeMapItemEntry(entry);
}

function useMapMagicFromNativeMenu(id: number): void {
  closeOriginalNativeMenuPopups();
  useMapMagic(id, () => undefined);
}

function seeMapMagicFromNativeMenu(id: number): void {
  closeOriginalNativeMenuPopups();
  seeMapMagic(id);
}

function buyShopItemFromNativeMenu(id: number): void {
  closeOriginalNativeMenuPopups();
  buyShopItem(id);
}

function seeShopItemFromNativeMenu(id: number): void {
  closeOriginalNativeMenuPopups();
  seeShopItem(id);
}

function useMapItemEntry(entry: InventoryEntry, reopenMenu: () => void): void {
  if (!originalMainStateIsWalk()) return;
  const id = entry?.id ?? 0;
  const item = items.get(id);
  if (!item || !entry) return;
  const sourceIndex = originalSourceActorIndex();
  const targetIndex = targetActorIndex();
  const user = originalSourceActor();
  const target = targetActor();
  if (entry.group !== 4 && user.state === 2) {
    executeTokens([printToken("source.main.0ae7eb34", { user_name: user.name })], { checkMode: false });
    deferOrRun(reopenMenu);
    return;
  }
  const failures = itemUsabilityFailures(item, false, sourceIndex, targetIndex);
  if (failures.length) {
    executeTokens([...failures.map((failure) => `Print ${failure}`), "Click"], { checkMode: false });
    deferOrRun(reopenMenu);
    return;
  }
  const result = applyOriginalItemEffectTokens(item, target);
  settleOriginalItemUseInventory(entry, item, result.returnItemId, "field");
  executeTokens([...originalUseItemPrefixTokens(item, user, target), ...result.tokens, "Click"], result.context ?? { checkMode: false });
  deferOrRun(reopenMenu);
}

function seeMapItemEntry(entry: InventoryEntry): void {
  if (!originalMainStateIsWalk()) return;
  const item = items.get(entry.id);
  if (!item) return;
  state.activeInspection = originalItemInspection(item);
  executeTokens(["Click", "Draw 3"], { checkMode: false });
}

function useMapMagic(id: number, reopenMenu: () => void = openMagicChantMenu): void {
  if (!originalMainStateIsWalk()) return;
  const spell = magic.get(id);
  const sourceIndex = originalSourceActorIndex();
  const actor = originalSourceActor();
  const target = targetActor();
  if (!spell) return;
  if (actor.state === 2) {
    executeTokens([printToken("source.main.491594a6", { actor_name: actor.name })], { checkMode: false });
    deferOrRun(reopenMenu);
    return;
  }
  const failures = magicUsabilityFailures(spell, actor, false);
  if (failures.length) {
    executeTokens([...failures.map((failure) => `Print ${failure}`), "Click"], { checkMode: false });
    deferOrRun(reopenMenu);
    return;
  }
  const targetName = targetNameForMagic(spell, target);
  const targetArg = isWholeGroupState(spell.state) ? "A" : String(targetActorIndex());
  const prefixTokens = originalUseMagicPrefixTokens(spell, actor, sourceIndex);
  const effectTokens: string[] = [];
  if (spell.type === 0) {
    effectTokens.push(printToken("source.main.51ae748d", { targetName: targetName, spell_value: spell.value }), "HP " + String(targetArg) + " " + String(spell.value));
  } else if (spell.type === 1) {
    effectTokens.push(printToken("source.main.50fb43c0", { targetName: targetName, spell_value: spell.value }), "MP " + String(targetArg) + " " + String(spell.value));
  } else if (spell.type === 2) {
    effectTokens.push(
      printToken("source.main.ad581a9d", { targetName: targetName, spell_value: spell.value }),
      "HP " + String(targetArg) + " " + String(spell.value),
      "MP " + String(targetArg) + " " + String(spell.value)
    );
  } else if (spell.type === 18) {
    if (spell.script) {
      const context = originalFacingEventContext();
      effectTokens.push(...compileOriginalEventMessageQueue(tokenize(spell.script), context), "Click");
      executeTokens([...prefixTokens, ...effectTokens], context);
      deferOrRun(reopenMenu);
      return;
    } else {
      effectTokens.push(printToken("source.main.633561e5"));
    }
  }
  executeTokens([...prefixTokens, ...effectTokens, "Click"], { checkMode: false });
  deferOrRun(reopenMenu);
}

function seeMapMagic(id: number): void {
  if (!originalMainStateIsWalk()) return;
  const spell = magic.get(id);
  if (!spell) return;
  state.activeInspection = originalMagicInspection(spell);
  executeTokens(["Click", "Draw 3"], { checkMode: false });
}

function showShop(ids: number[]): void {
  setOriginalMenuMode("shop");
  state.shopItems = ids.filter(Boolean).slice(0, 8);
  state.mode = "shop";
  state.previousMode = "map";
  focusOriginalMenuUsingCommand("shop");
}

function shopEntries(): Array<{ id: number; item?: Item }> {
  return (state.shopItems ?? []).map((id) => ({ id, item: items.get(id) })).filter((entry) => entry.id > 0);
}

function shopItemLabel(id: number, item?: Item): string {
  return gameText("source.main.2eaf48b4", { item_name_Item_id: item?.name ?? `Item ${id}`, item_price_0: item?.price ?? 0 });
}

function buyShopItem(id: number): void {
  const item = items.get(id);
  if (!item) return;
  if ((item.state & 256) === 0 && (item.state & 1024) === 0 && allInventoryEntries().some((entry) => entry.id === id)) {
    executeTokens([printToken("source.main.d37f8600", { item_name: item.name }), "Click"], { checkMode: false });
    return;
  }
  const targetIndex = targetActorIndex();
  if ((item.state & 256) === 0 && inventoryFull(targetIndex)) {
    executeTokens([printToken("source.main.756b67bc"), "Click"], { checkMode: false });
    return;
  }
  if (state.money < item.price) {
    executeTokens([printToken("source.main.5dd07ad7"), "Click"], { checkMode: false });
    return;
  }
  addInventory(id, targetIndex);
  state.money -= item.price;
  executeTokens([printToken("source.main.45c7b659", { item_name: item.name }), "Click"], { checkMode: false });
}

function seeShopItem(id: number): void {
  const item = items.get(id);
  if (!item) return;
  state.activeInspection = originalItemInspection(item);
  executeTokens(["Click", "Draw 3"], { checkMode: false });
}

function showInn(price: number): void {
  setOriginalMenuMode("inn");
  const total = price * state.party.filter((actor) => actor.state > 0).length;
  state.innPrice = total;
  state.mode = "inn";
  state.previousMode = "map";
  focusOriginalMenuUsingCommand("inn");
}

function startBattle(partyId: number, onWinScript?: string, onWinContext?: EventScriptContext, onWinTokens?: string[]): void {
  if (!BATTLE_RUNTIME_ENABLED) return;
  const party = enemyParties.get(partyId);
  const sourceEnemyIds = Array.from({ length: 8 }, (_, index) => party?.enemyIds[index] ?? 0);
  const battleEnemies = sourceEnemyIds.map((id) => {
    const enemy = id > 0 ? enemies.get(id) : undefined;
    return enemy ? makeBattleEnemyFromOriginal(enemy) : makeEmptyBattleEnemy();
  });
  const hasEnemies = battleEnemies.some((enemy) => isOriginalBattleEnemySlot(enemy));
  state.mode = "battle";
  state.previousMode = "map";
  state.gameOverQueued = false;
  setOriginalMenuMode("et");
  state.walking = undefined;
  state.forcedWalk = undefined;
  state.battle = {
    partyId,
    enemies: battleEnemies,
    enemiesVisible: false,
    hiddenEnemyIndexes: [],
    selected: 0,
    battleRootSelected: 0,
    battleLeafSelected: 0,
    battlePopupFocus: "root",
    targetEnemyIndex: 0,
    phase: "entry",
    commandActorIndex: selectedActorIndex(),
    turnSpeeds: Array(12).fill(0),
    turnPending: false,
    statBuffers: {
      actorOffense: state.party.map((actor) => actor.offense),
      actorDefense: state.party.map((actor) => actor.defense),
      actorSpeed: state.party.map((actor) => actor.speed),
      enemyDefense: battleEnemies.map((enemy) => enemy.defense),
      enemySpeed: battleEnemies.map((enemy) => enemy.speed),
      enemyActionEffects: battleEnemies.map((enemy) => enemy.actions.map((action) => action.power))
    },
    onWinScript,
    onWinMessageQueue: onWinTokens
      ? makeOriginalMessageQueueStateFromTokens(onWinTokens, onWinContext ?? { checkMode: false })
      : undefined,
    onWinContext,
    battleLog: [],
    rewardExp: 0,
    rewardGold: 0
  };
  playBgm(party?.battleBgm ? party.battleBgm : DEFAULT_BATTLE_BGM);
  queueBattleEntrySequence(hasEnemies);
}

function makeBattleEnemyFromOriginal(enemy: Enemy): BattleEnemy {
  return { ...enemy, currentHp: Math.max(1, enemy.hp), powerScale: 1, powerModifier: 0 };
}

function makeEmptyBattleEnemy(): BattleEnemy {
  return { ...EMPTY_BATTLE_ENEMY, actions: EMPTY_BATTLE_ENEMY.actions.map((action) => ({ ...action })) };
}

function battleEntryTokens(hasEnemies = true): string[] {
  return ["MessageClear", "", "Menu ET", "WipeOut", "ShowEnemy", printToken("source.main.d0d2b24f"), "Click", hasEnemies ? "StartFight" : "EndFight 0"];
}

function queueBattleEntrySequence(hasEnemies = true): void {
  executeTokens(battleEntryTokens(hasEnemies), { checkMode: false });
}

function pushBattleLog(battle: BattleState, text: string): void {
  const replaced = replaceNames(text);
  if (!replaced.trim()) return;
  battle.battleLog.push(replaced);
  enqueueMessage(replaced);
}

function applyOriginalBattleCommandTurn(actorIndex: number): void {
  const battle = state.battle;
  if (!battle) return;
  const actor = state.party[actorIndex];
  if (!actor || actor.state !== 1 || actor.hp <= 0) return;
  clearBattleEnemyCommandClickCandidate();
  battle.commandActorIndex = actorIndex;
  battle.phase = "command";
  battle.battleItemMenuMode = undefined;
  battle.battleMagicMenuMode = undefined;
  battle.selected = 0;
  battle.battleRootSelected = 0;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = "root";
  battle.targetEnemyIndex = preferredBattleEnemyIndex(battle);
  clearOriginalBattleTurnFlag(battle);
  changeOriginalSourceActor(actorIndex);
  state.mode = "battle";
  state.previousMode = "map";
  enqueueOriginalMessageCache([printToken("source.main.274fcc25", { actor_name: actor.name }), "Menu Fight"], { checkMode: false });
}

function beginBattleActionResolution(battle: BattleState): void {
  clearBattleEnemyCommandClickCandidate();
  battle.phase = "entry";
  battle.battleItemMenuMode = undefined;
  battle.battleMagicMenuMode = undefined;
  battle.battleRootSelected = 0;
  battle.battleLeafSelected = 0;
  battle.battlePopupFocus = "root";
  setOriginalMenuMode("et");
}

function hideOriginalBattleEnemyPictures(battle: BattleState | undefined = state.battle): void {
  if (!battle) return;
  battle.enemiesVisible = false;
  battle.hiddenEnemyIndexes = battle.enemies.map((_, enemyIndex) => enemyIndex);
}

function playerAttackRound(targetIndex = state.battle?.targetEnemyIndex ?? 0): void {
  const battle = state.battle;
  if (!battle) return;
  if (!BATTLE_FORMULAS_ENABLED) {
    battle.phase = "command";
    battle.selected = 0;
    return;
  }
  const actor = state.party[battle.commandActorIndex] ?? selectedActor();
  if (!actor || actor.state !== 1 || actor.hp <= 0) {
    pushBattleLog(battle, gameText("source.main.491594a6", { actor_name: actor?.name ?? gameText("message.common.companion") }));
    startBattleTurn();
    return;
  }
  const selectedTarget = battle.enemies[targetIndex];
  const target = selectedTarget?.currentHp > 0 ? selectedTarget : battle.enemies.find((enemy) => enemy.currentHp > 0);
  if (!target) {
    checkBattleVictory();
    return;
  }
  beginBattleActionResolution(battle);
  markOriginalBattleTurnFlag(battle);
  battle.targetEnemyIndex = battle.enemies.indexOf(target);
  const rawDamage = originalBattlePlayerRawDamage(battleTotalOffense(actor, battle), battleEnemyDefense(target, battle));
  const weapon = actor.weapon > 0 ? items.get(actor.weapon) : undefined;
  const attackSound = weapon && (weapon.state & ORIGINAL_STATE_CUSTOM_SOUND) ? weapon.wave : "Attack";
  executeTokens(
    [printToken("source.main.d896a255", { actor_name: actor.name }), "Wait 100", `EnemyDamage ${battle.targetEnemyIndex} ${rawDamage} ${attackSound} 1`],
    { checkMode: false }
  );
}

function useBattleMagic(index: number): void {
  if (!BATTLE_FORMULAS_ENABLED) {
    if (state.battle) {
      state.battle.phase = "command";
      state.battle.selected = 0;
    }
    return;
  }
  const battle = state.battle;
  const ids = battle ? availableMagic(battle.commandActorIndex) : availableMagic();
  const spell = magic.get(ids[index]);
  const caster = battle ? state.party[battle.commandActorIndex] ?? selectedActor() : selectedActor();
  if (!spell || !caster || !battle) return;
  beginBattleActionResolution(battle);
  const failures = magicUsabilityFailures(spell, caster, true);
  if (failures.length) {
    executeTokens([...failures.map((failure) => `Print ${failure}`), "Menu Fight"], { checkMode: false });
    return;
  }
  markOriginalBattleTurnFlag(battle);
  const result = applyBattleMagicResult(spell, battle, targetActor());
  executeTokens([...originalUseMagicPrefixTokens(spell, caster, battle.commandActorIndex), ...result.tokens, "Wait 100"], result.context ?? { checkMode: false });
}

function battleMagicIdsForCurrentMode(battle: BattleState): number[] {
  return availableMagic(battle.commandActorIndex);
}

function seeBattleMagic(index: number): void {
  const battle = state.battle;
  if (!battle) return;
  const spell = magic.get(battleMagicIdsForCurrentMode(battle)[index]);
  if (!spell) return;
  beginBattleActionResolution(battle);
  hideOriginalBattleEnemyPictures(battle);
  state.activeInspection = originalMagicInspection(spell);
  executeTokens(["Click", "Draw 4"], { checkMode: false });
}

function battleItemEntriesForCurrentMode(battle: BattleState): InventoryEntry[] {
  return battle.battleItemMenuMode === "important"
    ? sharedInventoryEntries()
    : personalInventoryEntriesForActor(battle.commandActorIndex);
}

function useBattleItem(index: number): void {
  if (!BATTLE_FORMULAS_ENABLED) {
    if (state.battle) {
      state.battle.phase = "command";
      state.battle.selected = 0;
    }
    return;
  }
  const battle = state.battle;
  const entry = battle ? battleItemEntriesForCurrentMode(battle)[index] : undefined;
  const id = entry?.id ?? 0;
  const item = items.get(id);
  if (!item || !battle || !entry) return;
  const user = state.party[battle.commandActorIndex] ?? selectedActor();
  beginBattleActionResolution(battle);
  const failures = itemUsabilityFailures(item, true, battle.commandActorIndex, targetActorIndex());
  if (failures.length) {
    executeTokens([...failures.map((failure) => `Print ${failure}`), "Menu Fight"], { checkMode: false });
    return;
  }
  markOriginalBattleTurnFlag(battle);
  const target = targetActor();
  const result = applyOriginalItemEffectTokens(item, target, battle);
  settleOriginalItemUseInventory(entry, item, result.returnItemId, "battle");
  executeTokens([...originalUseItemPrefixTokens(item, user, target), ...result.tokens, "Wait 100"], result.context ?? { checkMode: false });
}

function transferBattleInventoryEntry(index: number): void {
  const battle = state.battle;
  if (!battle) return;
  const entry = personalInventoryEntriesForActor(battle.commandActorIndex)[index];
  const item = entry ? items.get(entry.id) : undefined;
  if (!entry || !item) return;
  beginBattleActionResolution(battle);
  if (!moveInventoryEntryToTargetFirstEmpty(entry)) {
    executeTokens([printToken("source.main.c2258ed6"), "Menu Fight"], { checkMode: false });
    return;
  }
  markOriginalBattleTurnFlag(battle);
  executeTokens([printToken("source.main.4943c887", { item_name: item.name }), "Wait 100"], { checkMode: false });
}

function confirmDiscardBattleItem(index: number, itemName: string): void {
  if (!state.battle) return;
  showOriginalConfirmDialog(gameText("source.main.78dcb0a0", { itemName: itemName }), () => discardBattleInventoryEntry(index));
}

function discardBattleInventoryEntry(index: number): void {
  const battle = state.battle;
  if (!battle) return;
  const entry = personalInventoryEntriesForActor(battle.commandActorIndex)[index];
  const item = entry ? items.get(entry.id) : undefined;
  if (!entry || !item) return;
  beginBattleActionResolution(battle);
  removeInventoryEntry(entry);
  markOriginalBattleTurnFlag(battle);
  executeTokens([printToken("source.main.36b634e1", { state_party_battle_commandActorIndex: state.party[battle.commandActorIndex]?.name ?? originalSourceActor().name, item_name: item.name }), "Wait 100"], { checkMode: false });
}

function seeBattleItemEntry(index: number): void {
  const battle = state.battle;
  if (!battle) return;
  const entry = personalInventoryEntriesForActor(battle.commandActorIndex)[index];
  const item = entry ? items.get(entry.id) : undefined;
  if (!entry || !item) return;
  beginBattleActionResolution(battle);
  hideOriginalBattleEnemyPictures(battle);
  state.activeInspection = originalItemInspection(item);
  executeTokens(["Click", "Draw 4"], { checkMode: false });
}

function finishBattleActionAfterWait(delayMs = 100): void {
  markOriginalBattleTurnFlag();
  scheduleTimerMessageWait(delayMs, () => undefined);
}

function continueBattleAfterAction(): void {
  if (checkBattleVictory()) return;
  if (livingParty().length === 0) {
    triggerGameOver();
    return;
  }
  startBattleTurn();
}

function checkBattleVictory(): boolean {
  const battle = state.battle;
  if (!battle || battle.enemies.some((enemy) => enemy.currentHp > 0)) return false;
  return queueBattleVictory();
}

function queueBattleVictory(): boolean {
  const battle = state.battle;
  if (!battle) return false;
  if (battle.endingQueued) return true;
  clearOriginalBattleTurnFlag(battle);
  battle.endingQueued = "victory";
  const exp = battle.rewardExp;
  const gold = battle.rewardGold;
  state.money += gold;
  const levelTokens = livingParty().flatMap((actor) => gainExp(actor, exp));
  executeTokens(
    [
      "StopBGM",
      "Sound Victory",
      printToken("source.main.26b80264"),
      printToken("source.main.5a1f329e", { exp: exp, gold: gold }),
      "Click",
      ...levelTokens,
      ...(levelTokens.length ? ["Click"] : []),
      "EndFight 0"
    ],
    { checkMode: false }
  );
  if (battle.partyId >= 250) {
    trackEvent("game_clear_level", { progress_id: `battle_${battle.partyId}`, map_id: state.mapId, party_level_max: maxLevel(), elapsed_seconds: 0 });
  }
  return true;
}

function attemptEscape(): void {
  const battle = state.battle;
  if (!battle) return;
  if (!originalBattleNativeMenuReady()) return;
  if (!BATTLE_FORMULAS_ENABLED) {
    battle.phase = "command";
    battle.selected = 0;
    return;
  }
  beginBattleActionResolution(battle);
  const actor = state.party[battle.commandActorIndex] ?? originalSourceActor();
  const tokens = [printToken("source.main.4e8760a2", { actor_name: actor.name }), "Sound Escape"];
  if (originalBattleEscapeFails(rng())) {
    executeTokens([...tokens, printToken("source.main.a06a1b14"), "Turn"], { checkMode: false });
  } else {
    executeTokens([...tokens, "Click", "EndFight 2"], { checkMode: false });
  }
}

function queueBattleEscape(): void {
  const battle = state.battle;
  if (!battle) return;
  if (battle.endingQueued) return;
  clearOriginalBattleTurnFlag(battle);
  battle.endingQueued = "escape";
  queueBattleEndTokens(3);
}

function completeBattleExit(encounterCount: number, onWinScript?: string, onWinContext?: EventScriptContext, onWinMessageQueue?: OriginalMessageQueueState): void {
  state.battle = undefined;
  state.mode = "map";
  state.previousMode = "map";
  state.gameOverQueued = false;
  clearPointerIntent();
  setOriginalMenuMode("walk");
  state.encounterCount = encounterCount;
  playBgm(state.bgm);
  const onWinEntries = onWinMessageQueue ? originalMessageQueueEntries(onWinMessageQueue) : [];
  if (onWinEntries.length) {
    enqueueOriginalMessageCacheEntries(onWinEntries);
    drainOriginalMessageCache();
  } else if (onWinScript) {
    executeEventScript(onWinScript, onWinContext ?? { checkMode: false });
  }
  queueAutoSaveAfterIdle("battle_end");
}

function gainExp(actor: Actor, amount: number): string[] {
  const tokens: string[] = [];
  const actorIndex = state.party.indexOf(actor);
  actor.experience += amount;
  if (actor.level < 50 && actor.experience >= actor.nextExp) {
    const levelBeforeIncrease = actor.level;
    const requiredExp = actor.nextExp;
    actor.experience -= requiredExp;
    actor.nextExp += levelBeforeIncrease * 10;
    actor.maxHp = clamp(actor.maxHp + originalLevelUpGrowthAmount(actor.maxHpUp, levelBeforeIncrease), 0, 9999);
    actor.maxMp = clamp(actor.maxMp + originalLevelUpGrowthAmount(actor.maxMpUp, levelBeforeIncrease), 0, 9999);
    actor.offense = clamp(actor.offense + originalLevelUpGrowthAmount(actor.offenseUp, levelBeforeIncrease), 0, 9999);
    actor.defense = clamp(actor.defense + originalLevelUpGrowthAmount(actor.defenseUp, levelBeforeIncrease), 0, 9999);
    actor.speed = clamp(actor.speed + originalLevelUpGrowthAmount(actor.speedUp, levelBeforeIncrease), 0, 9999);
    tokens.push(printToken("source.main.449ecb43", { actor_name: actor.name }));
    if (actorIndex >= 0) tokens.push(`DrawState ${actorIndex}`);
    actor.level += 1;
  }
  if (actor.level > 49) {
    actor.experience = 0;
    actor.nextExp = 0;
  }
  return tokens;
}

function originalLevelUpGrowthAmount(baseGrowth: number, level: number): number {
  const scaled = originalVbInteger(Math.max(0, baseGrowth) * Math.max(0, level));
  return Math.max(0, originalVbInteger(scaled * 0.8 + scaled * rng() * 0.4));
}

function maybeRandomBattle(): void {
  if (!BATTLE_RUNTIME_ENABLED) return;
  if (state.mode !== "map" || state.enemyLevel <= 0) return;
  if (state.encounterCount > 0) state.encounterCount -= 1;
  if (state.encounterCount >= 190 || rng() * Math.max(1, state.encounterCount) >= 10) return;
  const enemyLevelOffset = originalRandomIndex(4, (index) => index < 4, 0);
  const partyId = 1 + enemyLevelOffset + (state.enemyLevel - 1) * 4;
  executeTokens([`Fight ${clamp(partyId, 1, 248)}`], { checkMode: false });
}

function startBattleTurn(): void {
  const battle = state.battle;
  if (!battle) return;
  if (state.waitClick || state.timerMessageWait || originalMessageQueueCount() > 0 || state.wipe) {
    state.deferred.push(() => startBattleTurn());
    state.mode = "message";
    return;
  }
  if (!BATTLE_FORMULAS_ENABLED) {
    battle.phase = "command";
    battle.selected = 0;
    return;
  }
  state.mode = "battle";
  state.previousMode = "map";
  if (checkBattleVictory()) return;
  if (livingParty().length === 0) {
    triggerGameOver();
    return;
  }
  for (let guard = 0; guard < 32; guard += 1) {
    const turnNo = nextBattleTurnNo(battle);
    if (turnNo === -2) {
      checkBattleVictory();
      return;
    }
    if (turnNo < 0) {
      executeTokens(["EndFight 0"], { checkMode: false });
      return;
    }
    if (turnNo < 4) {
      const actor = state.party[turnNo];
      if (!actor || actor.state !== 1 || actor.hp <= 0) continue;
      executeTokens([`Command ${turnNo}`], { checkMode: false });
      return;
    }
    const enemy = battle.enemies[turnNo - 4];
    if (!enemy || enemy.currentHp <= 0) continue;
    const actionScheduled = applyEnemyAction(enemy, chooseEnemyAction(enemy), battle);
    if (!actionScheduled) finishBattleActionAfterWait();
    return;
  }
}

function nextBattleTurnNo(battle: BattleState): number {
  for (let index = 0; index < 4; index += 1) {
    const actor = state.party[index];
    if (actor?.state === 1 && actor.hp > 0) {
      const speed = battleActorSpeed(actor, battle);
      battle.turnSpeeds[index] = originalVbLong((battle.turnSpeeds[index] ?? 0) + speed * 0.8 + speed * rng() * 0.4);
    } else {
      battle.turnSpeeds[index] = 0;
    }
  }
  let enemyCount = 0;
  for (let index = 0; index < 8; index += 1) {
    const enemy = battle.enemies[index];
    if (enemy?.currentHp > 0) {
      const speed = battleEnemySpeed(enemy, battle);
      battle.turnSpeeds[index + 4] = originalVbLong((battle.turnSpeeds[index + 4] ?? 0) + speed * 0.8 + speed * rng() * 0.4);
      enemyCount += 1;
    } else {
      battle.turnSpeeds[index + 4] = 0;
    }
  }
  if (enemyCount === 0) {
    return -2;
  }
  let maxSpeed = 0;
  let turnNo = -1;
  for (let index = 0; index < 12; index += 1) {
    const speed = battle.turnSpeeds[index] ?? 0;
    if (speed > maxSpeed) {
      maxSpeed = speed;
      turnNo = index;
    }
  }
  if (turnNo >= 0) battle.turnSpeeds[turnNo] = 0;
  return turnNo;
}

function chooseEnemyAction(enemy: BattleEnemy): { action: EnemyAction; slot: number } {
  const weightedSlots = "0000111223";
  const randomSlot = originalRandomIndex(10, (index) => index < 10, 0);
  const slot = Number(weightedSlots[randomSlot]);
  const slotted = enemy.actions[slot] ?? { type: -1, power: 0, aux: 0, text: "" };
  return { action: slotted, slot };
}

function enemyActionWave(enemy: BattleEnemy, action: EnemyAction, fallback = "Damage"): string {
  return enemyActionReplacementSound(enemy, action, fallback);
}

function enemyActionReplacementSound(enemy: BattleEnemy, action: EnemyAction, fallback = ""): string {
  return action.aux ? enemy.wave || fallback : fallback;
}

function applyEnemyAction(enemy: BattleEnemy, chosen: { action: EnemyAction; slot: number }, battle: BattleState): boolean {
  const { action, slot } = chosen;
  const value = battleEnemyActionEffect(enemy, battle, slot);
  const tokens: string[] = [];
  const randomLivingEnemyIndex = () => {
    const fallback = battle.enemies.findIndex((candidate) => candidate.currentHp > 0);
    return originalRandomIndex(8, (index) => (battle.enemies[index]?.currentHp ?? 0) > 0, Math.max(0, fallback));
  };
  const livingActorIndexes = () =>
    state.party.map((actor, index) => (actor.state === 1 && actor.hp > 0 ? index : -1)).filter((index) => index >= 0);
  const randomLivingActorIndex = () =>
    originalRandomIndex(4, (index) => (state.party[index]?.state ?? 0) === 1 && (state.party[index]?.hp ?? 0) > 0, selectedActorIndex());
  const addReplacementSound = (fallback = "") => {
    const sample = enemyActionReplacementSound(enemy, action, fallback);
    if (sample) tokens.push(`Sound ${sample}`);
  };
  const addActionTextWait = () => {
    if (action.text) tokens.push(`Print ${action.text}`, "Wait 100");
  };
  const finish = () => {
    executeTokens(tokens, { checkMode: false });
    return true;
  };
  markOriginalBattleTurnFlag(battle);
  if (action.type === 17) {
    tokens.push(`Print ${action.text || gameText("source.main.71b5634c", { enemy_name: enemy.name })}`, "Wait 100");
    return finish();
  }
  if (action.type === 16) {
    addReplacementSound("Escape");
    if (action.text) {
      tokens.push(`Print ${action.text}`, "Wait 100");
    } else {
      tokens.push(printToken("source.main.6d48cdea", { enemy_name: enemy.name }));
    }
    enemy.currentHp = 0;
    tokens.push("Wait 100");
    return finish();
  }
  if (action.type === 0) {
    const attackPower = value;
    const targetIndex = randomLivingActorIndex();
    const target = state.party[targetIndex];
    const rawDamage = originalBattleEnemyRawDamage(attackPower, battleTotalDefense(target, battle));
    const damage = Math.max(0, originalVbInteger(rawDamage < 1 ? attackPower * 0.1 : rawDamage));
    tokens.push(`Print ${action.text || gameText("source.main.3867db49", { enemy_name: enemy.name })}`, `DAMAGE ${targetIndex} ${damage} ${enemyActionWave(enemy, action)}`);
    return finish();
  }
  if (action.type === 1) {
    addReplacementSound();
    tokens.push(`Print ${action.text || gameText("source.main.3867db49", { enemy_name: enemy.name })}`, "Wait 100");
    const attackPower = value;
    livingActorIndexes().forEach((targetIndex) => {
      const target = state.party[targetIndex];
      const rawDamage = originalBattleEnemyRawDamage(attackPower, battleTotalDefense(target, battle));
      const damage = Math.max(0, originalVbInteger(rawDamage < 1 ? attackPower * 0.1 : rawDamage));
      tokens.push(`DAMAGE ${targetIndex} ${damage} Damage`);
    });
    return finish();
  }
  if (action.type === 2 || action.type === 3) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 3 ? livingEnemies(battle) : [battle.enemies[randomLivingEnemyIndex()]].filter((target): target is BattleEnemy => Boolean(target));
    targets.forEach((target) => {
      target.currentHp = clamp(target.currentHp + value, 0, Math.max(1, target.hp));
    });
    tokens.push(`Print ${action.type === 3 ? gameText("source.main.78043603", { value: value }) : gameText("source.main.4e3ac045", { targets_0_name_enemy_name: targets[0]?.name ?? enemy.name, value: value })}`, "Wait 100");
    return finish();
  }
  if (action.type === 4 || action.type === 5) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 5 ? livingEnemies(battle) : [battle.enemies[randomLivingEnemyIndex()]].filter((target): target is BattleEnemy => Boolean(target));
    let changed = false;
    targets.forEach((target) => {
      if (changeBattleEnemyActionEffects(battle, target, value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 5 ? gameText("source.main.b8000933", { value: value }) : gameText("source.main.be5cdd0d", { targets_0_name_enemy_name: targets[0]?.name ?? enemy.name, value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  if (action.type === 6 || action.type === 7) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 7 ? livingEnemies(battle) : [battle.enemies[randomLivingEnemyIndex()]].filter((target): target is BattleEnemy => Boolean(target));
    let changed = false;
    targets.forEach((target) => {
      if (changeBattleEnemyStatFromOriginalBase(battle, target, "enemyDefense", value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 7 ? gameText("source.main.b1cb5120", { value: value }) : gameText("source.main.c62f6b8e", { targets_0_name_enemy_name: targets[0]?.name ?? enemy.name, value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  if (action.type === 8 || action.type === 9) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 9 ? livingEnemies(battle) : [battle.enemies[randomLivingEnemyIndex()]].filter((target): target is BattleEnemy => Boolean(target));
    let changed = false;
    targets.forEach((target) => {
      if (changeBattleEnemyStatFromOriginalBase(battle, target, "enemySpeed", value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 9 ? gameText("source.main.2273fd46", { value: value }) : gameText("source.main.6cebb8f0", { targets_0_name_enemy_name: targets[0]?.name ?? enemy.name, value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  if (action.type === 10 || action.type === 11) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 11 ? livingParty() : [state.party[randomLivingActorIndex()]].filter((actor): actor is Actor => Boolean(actor));
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorOffense", -value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 11 ? gameText("source.main.f1869221", { value: value }) : gameText("source.main.55b20b59", { targets_0_name: targets[0]?.name ?? gameText("message.common.companion"), value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  if (action.type === 12 || action.type === 13) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 13 ? livingParty() : [state.party[randomLivingActorIndex()]].filter((actor): actor is Actor => Boolean(actor));
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorDefense", -value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 13 ? gameText("source.main.8b82734e", { value: value }) : gameText("source.main.a57d7a76", { targets_0_name: targets[0]?.name ?? gameText("message.common.companion"), value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  if (action.type === 14 || action.type === 15) {
    addReplacementSound();
    addActionTextWait();
    const targets = action.type === 15 ? livingParty() : [state.party[randomLivingActorIndex()]].filter((actor): actor is Actor => Boolean(actor));
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorSpeed", -value)) changed = true;
    });
    tokens.push(`Print ${changed ? (action.type === 15 ? gameText("source.main.a649940e", { value: value }) : gameText("source.main.893ee906", { targets_0_name: targets[0]?.name ?? gameText("message.common.companion"), value: value })) : gameText("source.main.5254005a")}`, "Wait 100");
    return finish();
  }
  return finish();
}

function damageActor(actor: Actor, amount: number, battle?: BattleState): void {
  void battle;
  if (applyActorDamageState(actor, amount)) queueOriginalActorDeathMessage(actor);
}

function queueOriginalActorDeathMessage(actor: Actor): void {
  appendOriginalMessageCacheEntry({
    token: printToken("source.main.0edfc8f5", { actor_name: actor.name }),
    context: { checkMode: false }
  });
  enterOriginalTimerMessageMode();
}

function triggerGameOver(): void {
  if (state.gameOverQueued) return;
  state.gameOverQueued = true;
  clearOriginalBattleTurnFlag();
  const wasBattle = Boolean(state.battle);
  hideOriginalBattleEnemyPictures();
  executeTokens(originalGameOverTokens(wasBattle), { checkMode: false });
}

function originalGameOverTokens(wasBattle: boolean): string[] {
  return [
    "StopBGM",
    printToken("source.main.64989700"),
    "Click",
    ...(wasBattle ? [] : ["WipeOut"]),
    "Menu First"
  ];
}

type OriginalMagicUseResult = { tokens: string[]; context?: EventScriptContext };

function applyBattleMagicResult(spell: Magic, battle: BattleState, targetActorForSpell = targetActor()): OriginalMagicUseResult {
  const targetName = targetNameForMagic(spell, targetActorForSpell);
  const targetArg = isWholeGroupState(spell.state) ? "A" : String(targetActorIndex());
  const tokens: string[] = [];
  let context: EventScriptContext | undefined;
  if (spell.type === 0) {
    tokens.push(printToken("source.main.51ae748d", { targetName: targetName, spell_value: spell.value }), "HP " + String(targetArg) + " " + String(spell.value));
  } else if (spell.type === 1) {
    tokens.push(printToken("source.main.50fb43c0", { targetName: targetName, spell_value: spell.value }), "MP " + String(targetArg) + " " + String(spell.value));
  } else if (spell.type === 2) {
    tokens.push(
      printToken("source.main.ad581a9d", { targetName: targetName, spell_value: spell.value }),
      "HP " + String(targetArg) + " " + String(spell.value),
      "MP " + String(targetArg) + " " + String(spell.value)
    );
  } else if (spell.type === 8) {
    const targets = battleTargets(spell.state, battle);
    let changed = false;
    targets.forEach((enemy) => {
      if (changeBattleEnemyActionEffects(battle, enemy, -spell.value)) {
        tokens.push(printToken("source.main.b224bfde", { enemy_name: enemy.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 9) {
    const targets = battleTargets(spell.state, battle);
    const reference = battle.enemies[battle.targetEnemyIndex] ?? targets[0];
    const referenceBase = reference ? enemyBaseStat(reference, "enemyDefense") : 0;
    let changed = false;
    targets.forEach((enemy) => {
      if (changeBattleEnemyStatFromOriginalReferenceBase(battle, enemy, "enemyDefense", -spell.value, referenceBase)) {
        tokens.push(printToken("source.main.b7dab49b", { enemy_name: enemy.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 10) {
    const targets = battleTargets(spell.state, battle);
    const reference = battle.enemies[battle.targetEnemyIndex] ?? targets[0];
    const referenceBase = reference ? enemyBaseStat(reference, "enemySpeed") : 0;
    let changed = false;
    targets.forEach((enemy) => {
      if (changeBattleEnemyStatFromOriginalReferenceBase(battle, enemy, "enemySpeed", -spell.value, referenceBase)) {
        tokens.push(printToken("source.main.788bb1b5", { enemy_name: enemy.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 11) {
    const targets = battleActorTargets(spell.state, targetActorForSpell);
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorOffense", spell.value)) {
        tokens.push(printToken("source.main.d65fef22", { actor_name: actor.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 12) {
    const targets = battleActorTargets(spell.state, targetActorForSpell);
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorDefense", spell.value)) {
        tokens.push(printToken("source.main.40d25e43", { actor_name: actor.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 13) {
    const targets = battleActorTargets(spell.state, targetActorForSpell);
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, "actorSpeed", spell.value)) {
        tokens.push(printToken("source.main.99db3093", { actor_name: actor.name, spell_value: spell.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (spell.type === 14) {
    const targets = battleTargets(spell.state, battle);
    targets.forEach((enemy) => {
      const targetIndex = battle.enemies.indexOf(enemy);
      if (targetIndex >= 0) tokens.push(`EnemyDamage ${targetIndex} ${Math.max(1, spell.value - battleEnemyDefense(enemy, battle))} Attack`);
    });
  } else if (spell.type === 18) {
    if (spell.script) {
      context = originalFacingEventContext();
      tokens.push(...compileOriginalEventMessageQueue(tokenize(spell.script), context));
    } else {
      tokens.push(printToken("source.main.633561e5"));
    }
  }
  return { tokens, context };
}

type OriginalItemUseResult = { tokens: string[]; returnItemId: number; context?: EventScriptContext };

function originalItemEffectTargets(item: Item, fallback: Actor): Actor[] {
  if (isWholeGroupState(item.state)) return state.party.filter((actor) => actor.state > 0);
  return [fallback];
}

function applyOriginalItemEffectTokens(item: Item, target: Actor, battle?: BattleState): OriginalItemUseResult {
  const targetName = targetNameForItem(item, target);
  const targetArg = isWholeGroupState(item.state) ? "A" : String(targetActorIndex());
  const tokens: string[] = [];
  let returnItemId = 0;
  let context: EventScriptContext | undefined;
  if (item.type === 0) {
    tokens.push(printToken("source.main.18d2addc", { targetName: targetName, item_value: item.value }), "HP " + String(targetArg) + " " + String(item.value));
  } else if (item.type === 1) {
    tokens.push(printToken("source.main.b55f180b", { targetName: targetName, item_value: item.value }), "MP " + String(targetArg) + " " + String(item.value));
  } else if (item.type === 2) {
    tokens.push(
      printToken("source.main.24a6a42c", { targetName: targetName, item_value: item.value }),
      "HP " + String(targetArg) + " " + String(item.value),
      "MP " + String(targetArg) + " " + String(item.value)
    );
  } else if (item.type === 3) {
    originalItemEffectTargets(item, target).forEach((actor) => {
      actor.maxHp += item.value;
    });
    tokens.push(printToken("source.main.649bac18", { targetName: targetName, item_value: item.value }));
  } else if (item.type === 4) {
    originalItemEffectTargets(item, target).forEach((actor) => {
      actor.maxMp += item.value;
    });
    tokens.push(printToken("source.main.7b152fbf", { targetName: targetName, item_value: item.value }));
  } else if (item.type === 5) {
    originalItemEffectTargets(item, target).forEach((actor) => {
      actor.offense += item.value;
    });
    tokens.push(printToken("source.main.546e29fc", { targetName: targetName, item_value: item.value }));
  } else if (item.type === 6) {
    originalItemEffectTargets(item, target).forEach((actor) => {
      actor.defense += item.value;
    });
    tokens.push(printToken("source.main.7b5162d7", { targetName: targetName, item_value: item.value }));
  } else if (item.type === 7) {
    originalItemEffectTargets(item, target).forEach((actor) => {
      actor.speed += item.value;
    });
    tokens.push(printToken("source.main.3d775ff1", { targetName: targetName, item_value: item.value }));
  } else if (battle && item.type === 8) {
    const targets = battleTargets(item.state, battle);
    let changed = false;
    targets.forEach((enemy) => {
      if (changeBattleEnemyActionEffects(battle, enemy, -item.value)) {
        tokens.push(printToken("source.main.14d3703d", { enemy_name: enemy.name, item_value: item.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (battle && (item.type === 9 || item.type === 10)) {
    const targets = battleTargets(item.state, battle);
    const stat = item.type === 9 ? "enemyDefense" : "enemySpeed";
    const label = item.type === 9 ? gameText("source.main.4b365d05") : gameText("source.main.086541c3");
    const reference = battle.enemies[battle.targetEnemyIndex] ?? targets[0];
    const referenceBase = reference ? enemyBaseStat(reference, stat) : 0;
    let changed = false;
    targets.forEach((enemy) => {
      if (changeBattleEnemyStatFromOriginalReferenceBase(battle, enemy, stat, -item.value, referenceBase)) {
        tokens.push(printToken("source.main.8db4d0c0", { enemy_name: enemy.name, label: label, item_value: item.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (battle && (item.type === 11 || item.type === 12 || item.type === 13)) {
    const stat = item.type === 11 ? "actorOffense" : item.type === 12 ? "actorDefense" : "actorSpeed";
    const label = item.type === 11 ? gameText("source.main.933fae80") : item.type === 12 ? gameText("source.main.4b365d05") : gameText("source.main.086541c3");
    const targets = battleActorTargets(item.state, target);
    let changed = false;
    targets.forEach((actor) => {
      if (changeBattleActorStatFromOriginalBase(battle, actor, stat, item.value)) {
        tokens.push(printToken("source.main.be06e8ca", { actor_name: actor.name, label: label, item_value: item.value }));
        changed = true;
      }
    });
    if (!changed) tokens.push(printToken("source.main.5254005a"));
  } else if (battle && item.type === 14) {
    const selectedIndex = battle.targetEnemyIndex;
    const selected = battle.enemies[selectedIndex];
    if (selected?.currentHp > 0) {
      const hitCount = isWholeGroupState(item.state) ? battleTargets(item.state, battle).length : 1;
      const damage = Math.max(1, item.value - battleEnemyDefense(selected, battle));
      for (let count = 0; count < hitCount; count += 1) tokens.push(`EnemyDamage ${selectedIndex} ${damage} Attack`);
    }
  } else if (item.type === 15) {
    returnItemId = target.weapon;
    target.weapon = item.id;
  } else if (item.type === 16) {
    returnItemId = target.protector;
    target.protector = item.id;
  } else if (item.type === 17) {
    const spellName = magic.get(item.value)?.name ?? gameText("source.main.35963a22");
    const result = learnMagicForActorFromItem(item.value, targetActorIndex());
    if (result === "learned") {
      tokens.push(printToken("source.main.73c34226", { target_name: target.name, spellName: spellName }));
    } else if (result === "known") {
      tokens.push(printToken("source.main.70474828", { target_name: target.name, spellName: spellName }));
      returnItemId = item.id;
    } else {
      tokens.push(printToken("source.main.e86c3607", { target_name: target.name }));
      returnItemId = item.id;
    }
  } else if (item.type === 18) {
    if (item.script) {
      context = originalFacingEventContext();
      tokens.push(...compileOriginalEventMessageQueue(tokenize(item.script), context));
    } else {
      tokens.push(printToken("source.main.633561e5"));
    }
  }
  return { tokens, returnItemId, context };
}

function settleOriginalItemUseInventory(entry: InventoryEntry, item: Item, returnItemId: number, mode: "field" | "battle"): void {
  if (!originalItemConsumed(item)) return;
  if (entry.group === 4) {
    if (returnItemId > 0) {
      addInventory(returnItemId, mode === "battle" ? state.battle?.commandActorIndex ?? originalSourceActorIndex() : originalSourceActorIndex());
      removeInventoryEntry(entry);
    }
    return;
  }
  if (returnItemId > 0) replaceInventoryEntry(entry, returnItemId);
  else removeInventoryEntry(entry);
}

function battleActorTargets(stateValue: number, fallback = targetActor()): Actor[] {
  if (isWholeGroupState(stateValue)) return livingParty();
  return [fallback].filter((actor) => actor.state === 1 && actor.hp > 0);
}

function targetNameForItem(item: Item, actor: Actor): string {
  return isWholeGroupState(item.state) ? gameText("source.main.54fa3e82") : actor.name;
}

function targetNameForMagic(spell: Magic, actor: Actor): string {
  return isWholeGroupState(spell.state) ? gameText("source.main.54fa3e82") : actor.name;
}

function targetScopeText(stateValue: number): string {
  return isWholeGroupState(stateValue) ? gameText("source.main.54fa3e82") : gameText("source.main.1ccec6bf");
}

function battleTargets(stateValue: number, battle: BattleState): BattleEnemy[] {
  const enemies = livingEnemies(battle);
  if (isWholeGroupState(stateValue)) return enemies;
  const selected = battle.enemies[battle.targetEnemyIndex];
  if (isLivingBattleEnemy(selected)) return [selected];
  return enemies.slice(0, 1);
}

function isOriginalBattleEnemySlot(enemy: BattleEnemy | undefined): enemy is BattleEnemy {
  return Boolean(enemy && enemy.id > 0);
}

function isLivingBattleEnemy(enemy: BattleEnemy | undefined): enemy is BattleEnemy {
  return Boolean(isOriginalBattleEnemySlot(enemy) && enemy.currentHp > 0);
}

function livingEnemies(battle: BattleState): BattleEnemy[] {
  return battle.enemies.filter(isLivingBattleEnemy);
}

function preferredBattleEnemyIndex(battle: BattleState): number {
  const selected = battle.enemies[battle.targetEnemyIndex];
  if (isLivingBattleEnemy(selected)) return battle.targetEnemyIndex;
  const fallback = battle.enemies.findIndex((enemy) => isLivingBattleEnemy(enemy));
  return fallback >= 0 ? fallback : 0;
}

function selectNextLivingEnemy(direction: number, startIndex?: number): void {
  const battle = state.battle;
  if (!battle) return;
  if (!livingEnemies(battle).length) return;
  let index = startIndex ?? battle.selected;
  for (let step = 0; step < battle.enemies.length; step += 1) {
    index = (index + direction + battle.enemies.length) % battle.enemies.length;
    if (isLivingBattleEnemy(battle.enemies[index])) {
      battle.targetEnemyIndex = index;
      return;
    }
  }
}

function isWholeGroupState(stateValue: number): boolean {
  return (stateValue & ORIGINAL_STATE_ALL_TARGETS) !== 0;
}

function availableMagic(actorIndex = originalSourceActorIndex()): number[] {
  const actorSpecific = state.actorMagic[actorIndex] ?? [];
  const source = state.actorMagic.some((ids) => ids.some(Boolean)) ? actorSpecific : state.learnedMagic;
  return source.filter((id) => magic.get(id)?.name);
}

function livingParty(): Actor[] {
  return state.party.filter((actor) => actor.state === 1 && actor.hp > 0);
}

function selectedActor(): Actor {
  const actor = state.party[state.sourceActorIndex];
  if (actor?.state === 1 && actor.hp > 0) return actor;
  return livingParty()[0] ?? state.party[0];
}

function selectedActorIndex(): number {
  const actor = state.party[state.sourceActorIndex];
  if (actor?.state === 1 && actor.hp > 0) return state.sourceActorIndex;
  const fallback = state.party.findIndex((candidate) => candidate.state === 1 && candidate.hp > 0);
  return fallback >= 0 ? fallback : 0;
}

function originalSourceActorIndex(): number {
  const actor = state.party[state.sourceActorIndex];
  if (actor?.state > 0) return state.sourceActorIndex;
  return firstOriginalExistingActorIndex();
}

function originalSourceActor(): Actor {
  return state.party[originalSourceActorIndex()] ?? state.party[0];
}

function targetActor(): Actor {
  return state.party[targetActorIndex()] ?? state.party[0];
}

function targetActorIndex(): number {
  const actor = state.party[state.targetActorIndex];
  if (actor?.state > 0) return state.targetActorIndex;
  const fallback = state.party.findIndex((candidate) => candidate.state > 0);
  return fallback >= 0 ? fallback : 0;
}

function firstOriginalExistingActorIndex(): number {
  const index = state.party.findIndex((actor) => actor.state > 0);
  return index >= 0 ? index : 0;
}

function applyOriginalLoadDataSideEffects(slot: number): void {
  state.direction = "down";
  state.saveDataSlot = slot;
  fChangeOriginalActors();
}

function learnMagicForActorFromItem(id: number, index: number): "learned" | "known" | "full" {
  if (!state.actorMagic[index]) state.actorMagic[index] = [];
  if (state.actorMagic[index].includes(id)) return "known";
  const slot = Array.from({ length: 16 }, (_, candidate) => candidate).find(
    (candidate) => (state.actorMagic[index][candidate] ?? 0) <= 0
  );
  if (slot === undefined) return "full";
  state.actorMagic[index][slot] = id;
  if (!state.learnedMagic.includes(id)) state.learnedMagic.push(id);
  return "learned";
}

function inventoryGroupForItem(id: number, ownerIndex = selectedActorIndex()): number {
  return (items.get(id)?.state ?? 0) & 256 ? 4 : clamp(ownerIndex, 0, 3);
}

function inventoryEntriesForMenu(actorIndex = selectedActorIndex()): InventoryEntry[] {
  return [
    ...(state.inventoryByActor[actorIndex] ?? []).map((id, slot) => ({ id, group: actorIndex, slot })),
    ...(state.inventoryByActor[4] ?? []).map((id, slot) => ({ id, group: 4, slot }))
  ].filter((entry) => entry.id > 0);
}

function personalInventoryEntriesForActor(actorIndex: number): InventoryEntry[] {
  const group = clamp(actorIndex, 0, 3);
  return (state.inventoryByActor[group] ?? [])
    .map((id, slot) => ({ id, group, slot }))
    .filter((entry) => entry.id > 0);
}

function sharedInventoryEntries(): InventoryEntry[] {
  return (state.inventoryByActor[4] ?? [])
    .map((id, slot) => ({ id, group: 4, slot }))
    .filter((entry) => entry.id > 0);
}

function allInventoryEntries(): InventoryEntry[] {
  return state.inventoryByActor.flatMap((row, group) =>
    row.map((id, slot) => ({ id, group, slot })).filter((entry) => entry.id > 0)
  );
}

function originalIfItemPredicate(rawValue: string | undefined): boolean {
  const itemId = asInt(rawValue, 0);
  for (let group = 0; group <= 4; group += 1) {
    if (group !== 4 && (state.party[group]?.state ?? 0) <= 0) continue;
    const row = state.inventoryByActor[group] ?? [];
    for (let slot = 0; slot < 10; slot += 1) {
      if ((row[slot] ?? 0) === itemId) return true;
    }
  }
  return false;
}

function addInventory(id: number, ownerIndex = selectedActorIndex()): boolean {
  const group = inventoryGroupForItem(id, ownerIndex);
  const row = state.inventoryByActor[group] ?? (state.inventoryByActor[group] = []);
  for (let slot = 0; slot < 10; slot += 1) {
    if ((row[slot] ?? 0) <= 0) {
      row[slot] = id;
      syncFlatInventory();
      return true;
    }
  }
  return false;
}

function removeInventory(id: number): void {
  const entry = allInventoryEntries().find((candidate) => candidate.id === id);
  if (entry) removeInventoryEntry(entry);
}

function removeInventoryEntry(entry: InventoryEntry): void {
  const row = state.inventoryByActor[entry.group];
  if (!row) return;
  row[entry.slot] = 0;
  syncFlatInventory();
}

function replaceInventoryEntry(entry: InventoryEntry, id: number): void {
  const row = state.inventoryByActor[entry.group];
  if (!row) return;
  row[entry.slot] = id;
  syncFlatInventory();
}

function inventoryFull(ownerIndex = selectedActorIndex()): boolean {
  const group = clamp(ownerIndex, 0, 3);
  const row = state.inventoryByActor[group] ?? [];
  return Array.from({ length: 10 }, (_, slot) => row[slot] ?? 0).every((id) => id > 0);
}

function firstEmptyInventorySlot(group: number): number {
  const row = state.inventoryByActor[group] ?? [];
  for (let slot = 0; slot < 10; slot += 1) {
    if ((row[slot] ?? 0) <= 0) return slot;
  }
  return -1;
}

function moveInventoryEntryToTargetFirstEmpty(entry: InventoryEntry): boolean {
  const targetIndex = targetActorIndex();
  const sourceRow = state.inventoryByActor[entry.group];
  const targetGroup = clamp(targetIndex, 0, 3);
  const targetSlot = firstEmptyInventorySlot(targetGroup);
  if (!sourceRow || targetSlot < 0) return false;
  const targetRow = state.inventoryByActor[targetGroup] ?? (state.inventoryByActor[targetGroup] = []);
  targetRow[targetSlot] = entry.id;
  sourceRow[entry.slot] = 0;
  syncFlatInventory();
  return true;
}

function transferInventoryEntry(entry: InventoryEntry, itemName: string): void {
  if (!originalMainStateIsWalk()) return;
  if (!moveInventoryEntryToTargetFirstEmpty(entry)) {
    executeTokens([printToken("source.main.c2258ed6"), "Click"], { checkMode: false });
    return;
  }
  executeTokens([printToken("source.main.b905691c", { itemName: itemName }), "Click"], { checkMode: false });
}

function actorBattleIndex(actor: Actor): number {
  return state.party.indexOf(actor);
}

function enemyBattleIndex(enemy: BattleEnemy, battle: BattleState): number {
  return battle.enemies.indexOf(enemy);
}

function totalOffense(actor: Actor): number {
  return actor.offense + (items.get(actor.weapon)?.value ?? 0);
}

function totalDefense(actor: Actor): number {
  return actor.defense + (items.get(actor.protector)?.value ?? 0);
}

function battleTotalOffense(actor: Actor, battle: BattleState): number {
  const index = actorBattleIndex(actor);
  const offense = index >= 0 ? battle.statBuffers.actorOffense[index] ?? actor.offense : actor.offense;
  return offense + (items.get(actor.weapon)?.value ?? 0);
}

function battleTotalDefense(actor: Actor, battle: BattleState): number {
  const index = actorBattleIndex(actor);
  const defense = index >= 0 ? battle.statBuffers.actorDefense[index] ?? actor.defense : actor.defense;
  return defense + (items.get(actor.protector)?.value ?? 0);
}

function battleActorSpeed(actor: Actor, battle: BattleState): number {
  const index = actorBattleIndex(actor);
  return index >= 0 ? battle.statBuffers.actorSpeed[index] ?? actor.speed : actor.speed;
}

function battleEnemyDefense(enemy: BattleEnemy, battle: BattleState): number {
  const index = enemyBattleIndex(enemy, battle);
  return index >= 0 ? battle.statBuffers.enemyDefense[index] ?? enemy.defense : enemy.defense;
}

function battleEnemySpeed(enemy: BattleEnemy, battle: BattleState): number {
  const index = enemyBattleIndex(enemy, battle);
  return index >= 0 ? battle.statBuffers.enemySpeed[index] ?? enemy.speed : enemy.speed;
}

function battleEnemyActionEffect(enemy: BattleEnemy, battle: BattleState, slot: number): number {
  const index = enemyBattleIndex(enemy, battle);
  const base = enemy.actions[slot]?.power ?? 0;
  return index >= 0 ? battle.statBuffers.enemyActionEffects[index]?.[slot] ?? base : base;
}

function actorBaseStat(actor: Actor, stat: "actorOffense" | "actorDefense" | "actorSpeed"): number {
  if (stat === "actorOffense") return actor.offense;
  if (stat === "actorDefense") return actor.defense;
  return actor.speed;
}

function changeBattleActorStatFromOriginalBase(
  battle: BattleState,
  actor: Actor,
  stat: "actorOffense" | "actorDefense" | "actorSpeed",
  delta: number
): boolean {
  const index = actorBattleIndex(actor);
  if (index < 0) return false;
  const current = battle.statBuffers[stat][index] ?? actorBaseStat(actor, stat);
  if (delta > 0 && current > actorBaseStat(actor, stat)) return false;
  if (delta < 0 && current < actorBaseStat(actor, stat)) return false;
  battle.statBuffers[stat][index] = Math.max(0, current + delta);
  return true;
}

function enemyBaseStat(enemy: BattleEnemy, stat: "enemyDefense" | "enemySpeed"): number {
  return stat === "enemyDefense" ? enemy.defense : enemy.speed;
}

function changeBattleEnemyStatFromOriginalBase(
  battle: BattleState,
  enemy: BattleEnemy,
  stat: "enemyDefense" | "enemySpeed",
  delta: number
): boolean {
  const index = enemyBattleIndex(enemy, battle);
  if (index < 0) return false;
  const current = battle.statBuffers[stat][index] ?? enemyBaseStat(enemy, stat);
  if (delta > 0 && current > enemyBaseStat(enemy, stat)) return false;
  if (delta < 0 && current < enemyBaseStat(enemy, stat)) return false;
  battle.statBuffers[stat][index] = Math.max(0, current + delta);
  return true;
}

function changeBattleEnemyStatFromOriginalReferenceBase(
  battle: BattleState,
  enemy: BattleEnemy,
  stat: "enemyDefense" | "enemySpeed",
  delta: number,
  referenceBase: number
): boolean {
  const index = enemyBattleIndex(enemy, battle);
  if (index < 0) return false;
  const current = battle.statBuffers[stat][index] ?? enemyBaseStat(enemy, stat);
  if (delta > 0 && current > referenceBase) return false;
  if (delta < 0 && current < referenceBase) return false;
  battle.statBuffers[stat][index] = Math.max(0, current + delta);
  return true;
}

function changeBattleEnemyActionEffects(battle: BattleState, enemy: BattleEnemy, delta: number): boolean {
  const index = enemyBattleIndex(enemy, battle);
  if (index < 0) return false;
  const row = battle.statBuffers.enemyActionEffects[index] ?? (battle.statBuffers.enemyActionEffects[index] = []);
  let changed = false;
  enemy.actions.forEach((action, slot) => {
    if (action.type < 0 || action.type >= 2) return;
    const base = action.power;
    const current = row[slot] ?? base;
    if (delta > 0 && current > base) return;
    if (delta < 0 && current < base) return;
    const next = Math.max(0, current + delta);
    if (next !== current) {
      row[slot] = next;
      changed = true;
    }
  });
  return changed;
}

function saveGame(slot: number): void {
  const slots = readSaveSlots();
  state.saveDataSlot = slot;
  const serialized = serializeState();
  slots[String(slot)] = {
    version: 2,
    state: serialized,
    savedAt: Date.now(),
    ...(ORIGINAL_INI_EXPORT_ENABLED ? { ini: exportOriginalIniSection(`Data ${slot}`, serialized) } : {})
  };
  localStorage.setItem(SAVE_KEY, JSON.stringify(slots));
}

function queueAutoSaveAfterIdle(reason: AutoSaveReason): void {
  deferOrRun(() => {
    autoSaveGame(reason);
  });
}

function autoSaveGame(reason: AutoSaveReason): boolean {
  if (!autoSaveCurrentStateCanResume()) return false;
  const serialized = serializeState();
  const autosave: StoredAutoSave = {
    version: 2,
    state: serialized,
    savedAt: Date.now(),
    reason,
    ...(ORIGINAL_INI_EXPORT_ENABLED ? { ini: exportOriginalIniSection("AutoSave", serialized) } : {})
  };
  try {
    localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(autosave));
    return true;
  } catch {
    return false;
  }
}

function autoSaveCurrentStateCanResume(): boolean {
  return (
    state.started &&
    state.mode !== "title" &&
    !state.battle &&
    !state.walking &&
    !state.forcedWalk &&
    !state.wipe &&
    !state.waitClick &&
    !state.timerMessageWait &&
    originalMessageQueueCount() === 0
  );
}

function loadGame(slot: number): boolean {
  const saved = loadSlot(slot);
  if (!saved) return false;
  applySerialized(serializedFromStoredSlot(saved), slot);
  state.mode = "map";
  state.started = true;
  setOriginalMenuMode("walk");
  return true;
}

function loadAutoSave(): boolean {
  const saved = latestAutoSave();
  if (!saved) return false;
  applySerialized(saved.state);
  state.mode = "map";
  state.started = true;
  setOriginalMenuMode("walk");
  return true;
}

function loadSlot(slot: number): StoredSaveSlot | undefined {
  return readSaveSlots()[String(slot)];
}

function readSaveSlots(): Record<string, StoredSaveSlot> {
  try {
    return JSON.parse(localStorage.getItem(SAVE_KEY) || "{}");
  } catch {
    return {};
  }
}

function latestSaveSlot(): number | undefined {
  const slots = readSaveSlots();
  let latest: { slot: number; savedAt: number; fallbackOrder: number } | undefined;
  for (const [key, value] of Object.entries(slots)) {
    const slot = Number(key);
    if (!Number.isInteger(slot) || slot < 1 || slot > 5 || !value || typeof value !== "object") continue;
    const savedAt = "savedAt" in value && Number.isFinite(value.savedAt) ? Number(value.savedAt) : 0;
    const fallbackOrder = savedAt > 0 ? 0 : slot;
    if (
      latest === undefined ||
      savedAt > latest.savedAt ||
      (savedAt === latest.savedAt && fallbackOrder > latest.fallbackOrder)
    ) {
      latest = { slot, savedAt, fallbackOrder };
    }
  }
  return latest?.slot;
}

function latestAutoSave(): StoredAutoSave | undefined {
  const saved = readAutoSave();
  if (!saved || saved.version !== 2 || !saved.state || typeof saved.savedAt !== "number" || !Number.isFinite(saved.savedAt)) {
    return undefined;
  }
  return saved;
}

function readAutoSave(): StoredAutoSave | undefined {
  try {
    const raw = JSON.parse(localStorage.getItem(AUTO_SAVE_KEY) || "null");
    if (!raw || typeof raw !== "object" || raw.version !== 2 || !raw.state || typeof raw.reason !== "string") {
      return undefined;
    }
    return raw as StoredAutoSave;
  } catch {
    return undefined;
  }
}

function isVersionedStoredSaveSlot(slot: StoredSaveSlot): slot is VersionedStoredSaveSlot {
  return "version" in slot && slot.version === 2 && "state" in slot;
}

function serializedFromStoredSlot(slot: StoredSaveSlot): SerializedSaveState {
  if (isVersionedStoredSaveSlot(slot)) return slot.state;
  return slot;
}

function serializeState() {
  return {
    mapId: state.mapId,
    x: state.x,
    y: state.y,
    bgm: state.bgm,
    enemyLevel: state.enemyLevel,
    charType: state.charType,
    party: state.party,
    money: state.money,
    inventory: flattenInventoryGroups(),
    inventoryByActor: state.inventoryByActor.map((row) => [...row]),
    learnedMagic: state.learnedMagic,
    actorMagic: state.actorMagic,
    flags: [...state.flags],
    tileOverrides: {},
    rngSeed: state.rngSeed,
    encounterCount: state.encounterCount,
    saveDataSlot: state.saveDataSlot,
    endingReached: state.endingReached
  };
}

function exportOriginalIniSection(sectionName: string, saved: ReturnType<typeof serializeState>): string {
  const lines: string[] = [originalSaveSectionHeader(sectionName)];
  saved.party.forEach((actor, index) => {
    lines.push(
      `${originalSavePartyKey("Name", index)}=${actor.name}`,
      `${originalSavePartyKey("HP", index)}=${actor.hp}`,
      `${originalSavePartyKey("MaxHP", index)}=${actor.maxHp}`,
      `${originalSavePartyKey("MP", index)}=${actor.mp}`,
      `${originalSavePartyKey("MaxMP", index)}=${actor.maxMp}`,
      `${originalSavePartyKey("Offense", index)}=${actor.offense}`,
      `${originalSavePartyKey("Weapon", index)}=${actor.weapon}`,
      `${originalSavePartyKey("Defense", index)}=${actor.defense}`,
      `${originalSavePartyKey("Protector", index)}=${actor.protector}`,
      `${originalSavePartyKey("Speed", index)}=${actor.speed}`,
      `${originalSavePartyKey("Level", index)}=${actor.level}`,
      `${originalSavePartyKey("Experience", index)}=${actor.experience}`,
      `${originalSavePartyKey("NextExp", index)}=${actor.nextExp}`,
      `${originalSavePartyKey("State", index)}=${actor.state}`
    );
  });
  lines.push(`${originalSaveGlobalKey("Money")}=${saved.money}`);
  const itemGroups = saved.inventoryByActor ?? cloneInventoryGroups(undefined, saved.inventory, saved.charType);
  itemGroups.forEach((row, index) =>
    lines.push(`${originalSaveGroupedKey("Item", index)}=${originalHexGroups(row, 1, 10)[0]}`)
  );
  [0, 1, 2, 3].forEach((index) => {
    originalHexGroups(saved.actorMagic[index] ?? [], 1, 16).forEach((value) =>
      lines.push(`${originalSaveGroupedKey("Magic", index)}=${value}`)
    );
  });
  lines.push(
    `${originalSaveGlobalKey("Flag")}=${originalFlagString(saved.flags)}`,
    `${originalSaveGlobalKey("X")}=${saved.x}`,
    `${originalSaveGlobalKey("Y")}=${saved.y}`,
    `${originalSaveGlobalKey("MPDFile")}=${originalStem(saved.mapId)}`,
    `${originalSaveGlobalKey("BGMFile")}=${originalStem(saved.bgm)}`,
    `${originalSaveGlobalKey("EnemyLevel")}=${saved.enemyLevel}`,
    `${originalSaveGlobalKey("ENMCount")}=${saved.encounterCount ?? 200}`,
    `${originalSaveGlobalKey("CharType")}=${saved.charType}`,
    originalSaveDataSentinelLine()
  );
  return lines.join("\n");
}

function importOriginalIniSection(text: string): void {
  const values = parseOriginalIniValues(text);
  const importedParty = state.party.map((actor, index) => ({
    ...actor,
    name: originalIniString(values, originalSavePartyKey("Name", index), actor.name),
    hp: originalIniInt(values, originalSavePartyKey("HP", index), actor.hp),
    maxHp: originalIniInt(values, originalSavePartyKey("MaxHP", index), actor.maxHp),
    mp: originalIniInt(values, originalSavePartyKey("MP", index), actor.mp),
    maxMp: originalIniInt(values, originalSavePartyKey("MaxMP", index), actor.maxMp),
    offense: originalIniInt(
      values,
      originalSavePartyKey("Offense", index),
      originalIniInt(values, `Offence ${index}`, actor.offense)
    ),
    weapon: originalIniInt(values, originalSavePartyKey("Weapon", index), actor.weapon),
    defense: originalIniInt(
      values,
      originalSavePartyKey("Defense", index),
      originalIniInt(values, `Defence ${index}`, actor.defense)
    ),
    protector: originalIniInt(values, originalSavePartyKey("Protector", index), actor.protector),
    speed: originalIniInt(values, originalSavePartyKey("Speed", index), actor.speed),
    level: originalIniInt(values, originalSavePartyKey("Level", index), actor.level),
    experience: originalIniInt(values, originalSavePartyKey("Experience", index), actor.experience),
    nextExp: originalIniInt(values, originalSavePartyKey("NextExp", index), actor.nextExp),
    state: originalIniInt(values, originalSavePartyKey("State", index), actor.state)
  }));
  state.party = importedParty;
  relocalizePartyNames();
  state.money = originalIniInt(values, originalSaveGlobalKey("Money"), state.money);
  state.inventoryByActor = Array.from({ length: 5 }, (_, index) =>
    originalHexSlotValues(values[originalSaveGroupedKey("Item", index)] ?? "", 10)
  );
  syncFlatInventory();
  state.actorMagic = Array.from({ length: 4 }, (_, index) =>
    originalHexSlotValues(values[originalSaveGroupedKey("Magic", index)] ?? "", 16)
  );
  state.learnedMagic = aggregateActorMagic(state.actorMagic);
  state.flags = new Set(
    (values[originalSaveGlobalKey("Flag")] ?? "").split("").flatMap((flag, index) => (flag === "1" ? [index + 1] : []))
  );
  state.mapId = normalizeMapArg(values[originalSaveGlobalKey("MPDFile")], state.mapId);
  const importedMap = currentMap();
  state.x = wrapMapCoordinate(originalIniInt(values, originalSaveGlobalKey("X"), state.x), importedMap.width);
  state.y = wrapMapCoordinate(originalIniInt(values, originalSaveGlobalKey("Y"), state.y), importedMap.height);
  state.bgm = normalizeBgm(values[originalSaveGlobalKey("BGMFile")] ?? state.bgm);
  state.enemyLevel = originalIniInt(values, originalSaveGlobalKey("EnemyLevel"), state.enemyLevel);
  state.encounterCount = originalIniInt(values, originalSaveGlobalKey("ENMCount"), 200);
  state.charType = clamp(
    originalIniInt(values, originalSaveGlobalKey("CharType"), state.charType),
    0,
    state.party.length - 1
  );
  applyOriginalLoadDataSideEffects(0);
  state.tileOverrides = {};
  state.rngSeed = makeRuntimeRngSeed();
  state.messages = [];
  state.messageHistory = [];
  state.originalMessageQueue = makeOriginalMessageQueueState();
  state.deferred = [];
  state.waitClick = false;
  clickAnimationStartedAt = 0;
  state.timerMessageWait = undefined;
  state.activeInspection = undefined;
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  state.mode = "map";
  state.started = true;
  resetOriginalMapDrawTrace();
  invalidateOriginalMapCache();
  setOriginalMenuMode("walk");
  state.endingReached = false;
  playBgm(state.bgm);
}

function parseOriginalIniValues(text: string): Record<string, string> {
  return originalSaveParseValues(text);
}

function originalIniString(values: Record<string, string>, key: string, fallback: string): string {
  return values[key] ?? fallback;
}

function originalIniInt(values: Record<string, string>, key: string, fallback: number): number {
  return asInt(values[key] ?? "", fallback);
}

function originalHexSlotValues(raw: string, limit: number): number[] {
  const compact = raw.replace(/\s+/g, "").toUpperCase();
  const result: number[] = [];
  for (let index = 0; index < compact.length - 1 && result.length < limit; index += 2) {
    const pair = compact.slice(index, index + 2);
    if (!/^[0-9A-F]{2}$/.test(pair)) continue;
    result.push(parseInt(pair, 16));
  }
  return result;
}

function originalHexGroups(values: number[], groups: number, groupSize: number): string[] {
  const padded = values.slice(0, groups * groupSize);
  while (padded.length < groups * groupSize) padded.push(0);
  return Array.from({ length: groups }, (_, group) =>
    padded
      .slice(group * groupSize, group * groupSize + groupSize)
      .map((value) => clamp(value, 0, 255).toString(16).toUpperCase().padStart(2, "0"))
      .join("")
  );
}

function originalFlagString(flags: number[]): string {
  const active = new Set(flags);
  return Array.from({ length: 255 }, (_, index) => (active.has(index + 1) ? "1" : "0")).join("");
}

function originalStem(value: string): string {
  const stem = value.replace(/\.(MPD|MID)$/i, "").toLowerCase();
  return stem ? stem[0].toUpperCase() + stem.slice(1) : "";
}

function applySerialized(saved: ReturnType<typeof serializeState>, slot = saved.saveDataSlot ?? 0): void {
  state.mapId = normalizeMapArg(saved.mapId, state.mapId);
  const savedMap = currentMap();
  state.x = wrapMapCoordinate(saved.x, savedMap.width);
  state.y = wrapMapCoordinate(saved.y, savedMap.height);
  state.bgm = saved.bgm;
  state.enemyLevel = saved.enemyLevel;
  state.charType = saved.charType;
  state.party = cloneParty(saved.party, { localizeNames: true });
  applyOriginalLoadDataSideEffects(slot);
  state.money = saved.money;
  state.inventoryByActor = cloneInventoryGroups(saved.inventoryByActor, saved.inventory, saved.charType);
  syncFlatInventory();
  state.actorMagic = cloneActorMagic(saved.actorMagic, saved.learnedMagic);
  state.learnedMagic = aggregateActorMagic(state.actorMagic);
  if (!state.learnedMagic.length) state.learnedMagic = [...saved.learnedMagic];
  state.flags = new Set(saved.flags);
  state.tileOverrides = {};
  state.rngSeed = saved.rngSeed ?? state.rngSeed;
  state.encounterCount = saved.encounterCount ?? 200;
  state.messages = [];
  state.messageHistory = [];
  state.originalMessageQueue = makeOriginalMessageQueueState();
  state.deferred = [];
  state.waitClick = false;
  clickAnimationStartedAt = 0;
  state.timerMessageWait = undefined;
  state.activeInspection = undefined;
  state.walking = undefined;
  state.forcedWalk = undefined;
  clearPointerIntent();
  state.endingReached = saved.endingReached;
  state.gameOverQueued = false;
  resetOriginalMapDrawTrace();
  invalidateOriginalMapCache();
}

function maxLevel(): number {
  return Math.max(...state.party.map((actor) => actor.level));
}

function asInt(value: string | number | undefined, fallback: number): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function originalVbInteger(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const absolute = Math.abs(value);
  const floor = Math.floor(absolute);
  const fraction = absolute - floor;
  if (fraction < 0.5) return sign * floor;
  if (fraction > 0.5) return sign * (floor + 1);
  return sign * (floor % 2 === 0 ? floor : floor + 1);
}

function originalVbLong(value: number): number {
  return originalVbInteger(value);
}

function originalRandomIndex(scale: number, accepts: (index: number) => boolean, fallback: number): number {
  for (let guard = 0; guard < 64; guard += 1) {
    const index = originalVbInteger(rng() * scale);
    if (accepts(index)) return index;
  }
  return fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function rng(): number {
  state.rngSeed = (Math.imul(state.rngSeed, 1664525) + 1013904223) >>> 0;
  return state.rngSeed / 0x100000000;
}

function audioAllowedByPage(): boolean {
  return pageAudioActive && document.visibilityState === "visible";
}

function isCurrentBgmRuntimeOwner(): boolean {
  return window.__FALEAL_BGM_RUNTIME_OWNER__ === bgmRuntimeOwner;
}

function shiftRuntimeDeadlines(durationMs: number): void {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return;
  if (state.walking) {
    state.walking.startedAt += durationMs;
    state.walking.nextFrameAt += durationMs;
  }
  if (state.wipe) {
    state.wipe.startedAt += durationMs;
    state.wipe.nextFrameAt += durationMs;
  }
  if (state.timerMessageWait) state.timerMessageWait.until += durationMs;
  if (state.originalMessageQueue.nextTimerAt > 0) state.originalMessageQueue.nextTimerAt += durationMs;
  if (state.originalMessageQueue.lastTimerStepAt !== undefined) {
    state.originalMessageQueue.lastTimerStepAt += durationMs;
  }
  if (clickAnimationStartedAt > 0) clickAnimationStartedAt += durationMs;
  if (gamepadCooldownUntil > 0) gamepadCooldownUntil += durationMs;
  if (pointerStepCooldownUntil > 0) pointerStepCooldownUntil += durationMs;
  if (nextKeyCheckAt > 0) nextKeyCheckAt += durationMs;
}

function setPageAudioActive(active: boolean): void {
  if (pageAudioActive === active) return;
  pageAudioActive = active;
  if (!pageAudioActive) {
    inactiveStartedAt = performance.now();
    stopFrameLoop();
    pauseBgmForInactive();
    stopSe();
    if (audioContext?.state === "running") void audioContext.suspend().catch(() => undefined);
    return;
  }
  if (inactiveStartedAt !== undefined) {
    shiftRuntimeDeadlines(performance.now() - inactiveStartedAt);
    inactiveStartedAt = undefined;
  }
  startFrameLoop();
  if (!isCurrentBgmRuntimeOwner()) return;
  if (!audioContext || !requestedBgm || !state.settings.bgmOn || bgmStoppedBySource) return;
  const resume = audioContext.state === "suspended" ? audioContext.resume().catch(() => undefined) : Promise.resolve();
  void resume.then(() => {
    if (!isCurrentBgmRuntimeOwner()) return;
    if (!requestedBgm || !state.settings.bgmOn || bgmStoppedBySource) return;
    if (!resumePausedBgm()) playBgm(requestedBgm);
  });
}

function resumeAudio(): void {
  if (document.visibilityState !== "visible") return;
  const wasInactive = !pageAudioActive;
  pageAudioActive = true;
  if (inactiveStartedAt !== undefined) {
    shiftRuntimeDeadlines(performance.now() - inactiveStartedAt);
    inactiveStartedAt = undefined;
  }
  startFrameLoop();
  if (!isCurrentBgmRuntimeOwner()) return;
  if (!audioContext) audioContext = new AudioContext();
  const shouldResumeBgm = Boolean(
    (wasInactive || !currentBgm?.source) && requestedBgm && state.settings.bgmOn && !bgmStoppedBySource
  );
  const resume = audioContext.state === "suspended" ? audioContext.resume().catch(() => undefined) : Promise.resolve();
  if (!shouldResumeBgm) return;
  void resume.then(() => {
    if (!isCurrentBgmRuntimeOwner()) return;
    if (!requestedBgm || !state.settings.bgmOn || bgmStoppedBySource) return;
    if (!resumePausedBgm()) playBgm(requestedBgm);
  });
}

function playBgm(name: string): void {
  if (!isCurrentBgmRuntimeOwner()) return;
  requestedBgm = normalizeBgm(name);
  bgmStoppedBySource = false;
  stopBgm();
  if (!BGM_PLAYBACK_ENABLED) return;
  if (!state.settings.bgmOn || !audioAllowedByPage() || !audioContext) return;
  const playbackId = ++bgmPlaybackId;
  const playback: BgmPlayback = { id: playbackId, name: requestedBgm };
  currentBgm = playback;
  if (audioContext.state !== "running") {
    const context = audioContext;
    void context.resume().catch(() => undefined).then(() => {
      if (currentBgm?.id !== playback.id || requestedBgm !== playback.name || context.state !== "running") return;
      void startBgmPlayback(playback);
    });
    return;
  }
  void startBgmPlayback(playback);
}

function bgmOpusFile(name: string): string | undefined {
  return originalBgmRuntimeAsset(normalizeBgm(name), BGM_OPUS_FILES);
}

async function startBgmPlayback(playback: BgmPlayback): Promise<void> {
  const context = audioContext;
  if (!context || context.state !== "running") return;
  const buffer = await loadBgmBuffer(playback.name, context);
  if (
    !buffer ||
    context.state !== "running" ||
    currentBgm?.id !== playback.id ||
    requestedBgm !== playback.name ||
    !state.settings.bgmOn ||
    bgmStoppedBySource
  ) {
    return;
  }
  const source = context.createBufferSource();
  const gain = context.createGain();
  source.buffer = buffer;
  source.loop = true;
  gain.gain.value = state.settings.bgmVolume / 100;
  source.connect(gain);
  gain.connect(context.destination);
  playback.source = source;
  playback.gain = gain;
  try {
    source.start();
  } catch {
    if (currentBgm?.id === playback.id) currentBgm = undefined;
  }
}

function loadBgmBuffer(name: string, context: AudioContext): Promise<AudioBuffer | undefined> {
  const normalized = normalizeBgm(name);
  const cached = bgmBufferCache.get(normalized);
  if (cached) return Promise.resolve(cached);
  const pending = bgmBufferPromises.get(normalized);
  if (pending) return pending;
  const file = bgmOpusFile(normalized);
  if (!file) return Promise.resolve(undefined);
  const promise = fetch(`/assets/audio/bgm/${encodeURIComponent(file)}`)
    .then((response) => (response.ok ? response.arrayBuffer() : undefined))
    .then((data) => (data ? context.decodeAudioData(data) : undefined))
    .then((buffer) => {
      if (buffer) bgmBufferCache.set(normalized, buffer);
      bgmBufferPromises.delete(normalized);
      return buffer;
    })
    .catch(() => {
      bgmBufferPromises.delete(normalized);
      return undefined;
    });
  bgmBufferPromises.set(normalized, promise);
  return promise;
}

function pauseBgmForInactive(): void {
  if (!isCurrentBgmRuntimeOwner()) return;
  if (currentBgm?.timer !== undefined) {
    window.clearTimeout(currentBgm.timer);
    currentBgm.timer = undefined;
  }
}

function resumePausedBgm(): boolean {
  if (!isCurrentBgmRuntimeOwner()) return false;
  if (!currentBgm || !requestedBgm || currentBgm.name !== normalizeBgm(requestedBgm) || !audioAllowedByPage()) return false;
  if (currentBgm.gain) currentBgm.gain.gain.value = state.settings.bgmVolume / 100;
  return Boolean(currentBgm.source);
}

function stopBgm(clearRequest = false, sourceStop = false): void {
  if (!isCurrentBgmRuntimeOwner()) return;
  if (currentBgm?.timer !== undefined) {
    window.clearTimeout(currentBgm.timer);
  }
  const source = currentBgm?.source;
  const gain = currentBgm?.gain;
  if (source) {
    try {
      source.stop();
    } catch {
      // Already stopped.
    }
    source.disconnect();
  }
  gain?.disconnect();
  currentBgm = undefined;
  if (clearRequest) requestedBgm = undefined;
  if (sourceStop) bgmStoppedBySource = true;
}

function playSe(sample: string): void {
  const asset = originalSoundEffectRuntimeAsset(sample);
  lastRequestedSe = asset || undefined;
  if (!state.settings.seOn || !audioContext || !audioAllowedByPage()) return;
  const audio = cloneSe(asset);
  audio.volume = state.settings.seVolume / 100;
  activeSeAudio.add(audio);
  const cleanup = () => activeSeAudio.delete(audio);
  audio.addEventListener("ended", cleanup, { once: true });
  audio.addEventListener("error", cleanup, { once: true });
  void audio.play().catch(cleanup);
}

function stopSe(): void {
  activeSeAudio.forEach((audio) => {
    audio.pause();
    try {
      audio.currentTime = 0;
    } catch {
      // Some browsers reject currentTime changes before metadata is loaded.
    }
  });
  activeSeAudio.clear();
}

function cloneSe(name: string): HTMLAudioElement {
  let source = seCache.get(name);
  if (!source) {
    source = new Audio(`/assets/audio/wav/${encodeURIComponent(name)}`);
    source.preload = "auto";
    seCache.set(name, source);
  }
  return source.cloneNode(true) as HTMLAudioElement;
}

function initAnalytics(): void {
  const measurementId = readAnalyticsMeasurementId();
  const enabled = Boolean(measurementId) && location.protocol === "https:" && location.hostname === PRODUCTION_HOST;
  const debugMode = shouldEnableAnalyticsDebug();
  const status: Record<string, unknown> = {
    enabled,
    reason: enabled ? "enabled" : analyticsDisabledReason(measurementId),
    measurementId: enabled ? measurementId : "",
    debugMode,
    hostname: location.hostname,
    protocol: location.protocol,
    scriptAppended: false,
    scriptLoaded: false,
    scriptError: false,
    commandCount: 0,
    lastCommand: null,
    lastEvent: null,
    usesOfficialArgumentsWrapper: true
  };
  window.__FALEAL_ANALYTICS_STATUS__ = status;
  if (!enabled || (window.__falealGoogleTagConfigured && window.__falealGoogleTagConfiguredId === measurementId)) return;
  if (!measurementId) return;
  window.__falealGoogleTagConfigured = true;
  window.__falealGoogleTagConfiguredId = measurementId;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagWrapper() {
    window.dataLayer?.push(arguments);
  };
  ensureGoogleTagScript(measurementId, status);
  sendGtag("js", new Date());
  sendGtag(
    "config",
    measurementId,
    scrubAnalyticsParams({
      page_location: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_title: document.title,
      send_page_view: true,
      debug_mode: debugMode ? true : undefined
    })
  );
}

function trackEvent(name: TrackedEventName, params: Record<string, unknown>): void {
  if (!TRACKED_EVENT_NAMES.includes(name)) return;
  const status = window.__FALEAL_ANALYTICS_STATUS__;
  const eventParams = scrubAnalyticsParams({
    app_name: APP_NAME,
    debug_mode: status?.debugMode === true ? true : undefined,
    ...params
  });
  if (status) status.lastEvent = { name, params: eventParams };
  if (!status?.enabled || !window.gtag) return;
  sendGtag("event", name, eventParams);
}

function readAnalyticsMeasurementId(): string {
  const queryId = new URLSearchParams(location.search).get("ga_id")?.trim();
  const metaId = document.querySelector<HTMLMetaElement>('meta[name="ga4-measurement-id"]')?.content?.trim() ?? "";
  return queryId || metaId || GA_MEASUREMENT_ID;
}

function shouldEnableAnalyticsDebug(): boolean {
  return new URLSearchParams(location.search).get("ga_debug") === "1";
}

function analyticsDisabledReason(measurementId: string): string {
  if (!measurementId) return "missing-measurement-id";
  if (location.protocol !== "https:") return "non-https";
  if (location.hostname !== PRODUCTION_HOST) return "non-production-host";
  return "disabled";
}

function ensureGoogleTagScript(measurementId: string, status: Record<string, unknown>): void {
  const scriptUrl = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  const existingScript = Array.from(document.scripts).some((script) => script.src === scriptUrl);
  if (existingScript) {
    status.scriptAppended = true;
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = scriptUrl;
  script.onload = () => (status.scriptLoaded = true);
  script.onerror = () => (status.scriptError = true);
  document.head.append(script);
  status.scriptAppended = true;
}

function sendGtag(...args: unknown[]): void {
  window.gtag?.(...args);
  const status = window.__FALEAL_ANALYTICS_STATUS__;
  if (!status) return;
  status.commandCount = Number(status.commandCount ?? 0) + 1;
  status.lastCommand = args[0] ?? null;
  if (args[0] === "event") {
    status.lastEvent = { name: args[1], params: args[2] ?? {} };
  }
}

function scrubAnalyticsParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || location.protocol === "file:") {
    return;
  }
  window.addEventListener("load", () => {
    if (debugAllowed()) {
      void unregisterServiceWorkers();
      return;
    }
    void navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
  });
}

async function unregisterServiceWorkers(): Promise<void> {
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.filter((key) => key.startsWith("faleal-story-classic-")).map((key) => caches.delete(key)));
  }
}

function debugAllowed(): boolean {
  return ["127.0.0.1", "localhost", ""].includes(location.hostname);
}

function debugOverlayEnabled(): boolean {
  return new URLSearchParams(location.search).get("debugOverlay") === "1";
}

wireOriginalMenuResponsiveScale();
focusOriginalTitleMenuSelection();
